import React, { useState, useEffect, useCallback, createContext, useContext, useRef } from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter, Routes, Route, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { QRCodeSVG } from 'qrcode.react';

// Centralized HTML sanitization to prevent XSS in any rich/HTML rendering.
// Allows safe iframes (YouTube/Vimeo embeds) used by the post editor while
// stripping inline scripts, event handlers, and other XSS vectors.
const sanitizeHtml = (html) => DOMPurify.sanitize(html || '', {
  USE_PROFILES: { html: true },
  ADD_TAGS: ['iframe'],
  ADD_ATTR: ['target', 'rel', 'allow', 'allowfullscreen', 'frameborder', 'scrolling'],
});
import { 
  Radio, Newspaper, Tag, Calendar, Heart, Music, Mic, ShoppingBag, ShoppingCart,
  User, LogOut, Menu, X, ChevronRight, ChevronLeft, Clock,
  MapPin, ExternalLink, Play, Pause, Volume2, VolumeX,
  Mail, Phone, Facebook, Instagram, Twitter, Search,
  Plus, Edit, Edit2, Trash2, Check, AlertCircle, Loader2, Star,
  ChevronUp, ArrowUp, Settings, Send, CheckCircle, XCircle,
  Image, Image as ImageIcon, DollarSign, Link, FileText, Repeat, Upload, Bell, Save
} from 'lucide-react';
import '@/App.css';

// Business Portal
import BusinessPortal from './business/BusinessPortal';

// Configure axios to send auth token with all requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Use relative URL when on the same domain to avoid CORS issues
const getApiBaseUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  // If we're on the preview domain, use relative URLs to avoid CORS
  if (window.location.origin === backendUrl) {
    return '';  // Relative to current origin
  }
  return backendUrl;
};

const API = `${getApiBaseUrl()}/api`;

// ============== CONSTANTS ==============
const LOCATIONS = [
  'Ennis',
  'Sheridan',
  'Twin Bridges',
  'Virginia City',
  'Harrison',
  'Pony',
  'Dillon',
  'Bozeman',
  'Butte',
  'Whitehall',
  'Three Forks',
  'Manhattan',
  'West Yellowstone',
  'Big Sky',
  'Livingston',
  'Gardiner',
  'Cooke City'
];

const CATEGORIES = [
  'Food & Drink',
  'Adventures',
  'Lodging',
  'Shopping',
  'Beauty',
  'Wellness'
];

const EVENT_CATEGORIES = [
  'Music',
  'Art',
  'Food',
  'Shopping',
  'Education',
  'History',
  'Other'
];

// ============== MODAL PORTAL ==============
const ModalPortal = ({ children }) => {
  return ReactDOM.createPortal(children, document.body);
};

// ============== AUTH CONTEXT ==============
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch { setUser(null); }
    finally { setLoading(false); }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    if (res.data.token) {
      localStorage.setItem('auth_token', res.data.token);
    }
    setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    localStorage.removeItem('auth_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// ============== TOAST NOTIFICATION ==============
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-3 border-2 border-[#2C2416] animate-slide-up ${
      type === 'success' ? 'bg-[#3A5A40] text-[#F5F0E1]' : 
      type === 'error' ? 'bg-[#8B3A3A] text-[#F5F0E1]' : 'bg-[#F5F0E1] text-[#2C2416]'
    }`}>
      {type === 'success' ? <CheckCircle size={18} /> : type === 'error' ? <XCircle size={18} /> : <AlertCircle size={18} />}
      <span className="font-utility text-sm">{message}</span>
      <button onClick={onClose} className="ml-2 hover:opacity-70"><X size={16} /></button>
    </div>
  );
};

// ============== RICH TEXT EDITOR ==============
const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [showEmbedInput, setShowEmbedInput] = useState(false);
  const [embedCode, setEmbedCode] = useState('');
  const savedSelectionRef = useRef(null);

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      // Sanitize incoming HTML to prevent XSS attacks via persisted content
      editorRef.current.innerHTML = sanitizeHtml(value);
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const saveSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      savedSelectionRef.current = selection.getRangeAt(0);
    }
  };

  const restoreSelection = () => {
    const selection = window.getSelection();
    if (savedSelectionRef.current) {
      selection.removeAllRanges();
      selection.addRange(savedSelectionRef.current);
    }
  };

  const handleLink = () => {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      saveSelection();
      setShowLinkInput(true);
    } else {
      alert('Please select text first to create a link');
    }
  };

  const insertLink = () => {
    if (linkUrl) {
      restoreSelection();
      // Ensure URL has protocol
      const url = linkUrl.startsWith('http://') || linkUrl.startsWith('https://') 
        ? linkUrl 
        : 'https://' + linkUrl;
      execCommand('createLink', url);
      setShowLinkInput(false);
      setLinkUrl('');
      savedSelectionRef.current = null;
      handleInput(); // Trigger onChange to save the link
    }
  };

  const handleEmbed = () => {
    setShowEmbedInput(true);
  };

  const insertEmbed = () => {
    // Get embed code from state or directly from DOM element as fallback
    let code = embedCode;
    if (!code || !code.trim()) {
      const textarea = document.getElementById('embed-code-textarea');
      if (textarea) {
        code = textarea.value;
      }
    }
    
    if (code && code.trim()) {
      // Focus the editor first and ensure cursor is at the end
      if (editorRef.current) {
        editorRef.current.focus();
        // Move cursor to end of content
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(editorRef.current);
        range.collapse(false); // Collapse to end
        selection.removeAllRanges();
        selection.addRange(range);
      }
      // Insert embed code as raw HTML
      const wrapper = `<div class="embed-wrapper" style="margin: 1rem 0;">${code}</div>`;
      document.execCommand('insertHTML', false, wrapper);
      setShowEmbedInput(false);
      setEmbedCode('');
      handleInput(); // Trigger onChange
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.url) {
        const img = `<img src="${res.data.url}" alt="Uploaded image" style="max-width: 100%; height: auto; margin: 1rem 0;" />`;
        document.execCommand('insertHTML', false, img);
        editorRef.current?.focus();
        handleInput(); // Trigger onChange
      }
    } catch (err) {
      console.error('Image upload failed:', err);
      alert('Failed to upload image. Please try again.');
    }
  };

  return (
    <div className="border-2 border-[#2C2416] bg-white">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 border-b-2 border-[#2C2416] bg-[#E8E2D2] flex-wrap">
        <button
          type="button"
          onClick={() => execCommand('bold')}
          className="p-2 hover:bg-[#2C2416] hover:text-white transition-colors border border-[#2C2416]"
          title="Bold (Ctrl+B)"
        >
          <strong>B</strong>
        </button>
        <button
          type="button"
          onClick={() => execCommand('italic')}
          className="p-2 hover:bg-[#2C2416] hover:text-white transition-colors border border-[#2C2416]"
          title="Italic (Ctrl+I)"
        >
          <em>I</em>
        </button>
        <button
          type="button"
          onClick={() => execCommand('underline')}
          className="p-2 hover:bg-[#2C2416] hover:text-white transition-colors border border-[#2C2416]"
          title="Underline (Ctrl+U)"
        >
          <u>U</u>
        </button>
        <div className="w-px h-6 bg-[#2C2416] mx-1" />
        <button
          type="button"
          onClick={() => execCommand('formatBlock', '<h2>')}
          className="px-2 py-1 hover:bg-[#2C2416] hover:text-white transition-colors border border-[#2C2416] font-utility text-xs"
          title="Heading 2"
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => execCommand('formatBlock', '<h3>')}
          className="px-2 py-1 hover:bg-[#2C2416] hover:text-white transition-colors border border-[#2C2416] font-utility text-xs"
          title="Heading 3"
        >
          H3
        </button>
        <button
          type="button"
          onClick={() => execCommand('formatBlock', '<p>')}
          className="px-2 py-1 hover:bg-[#2C2416] hover:text-white transition-colors border border-[#2C2416] font-utility text-xs"
          title="Paragraph"
        >
          P
        </button>
        <div className="w-px h-6 bg-[#2C2416] mx-1" />
        <button
          type="button"
          onClick={() => execCommand('insertUnorderedList')}
          className="p-2 hover:bg-[#2C2416] hover:text-white transition-colors border border-[#2C2416]"
          title="Bullet List"
        >
          <span className="text-sm">• List</span>
        </button>
        <button
          type="button"
          onClick={() => execCommand('insertOrderedList')}
          className="p-2 hover:bg-[#2C2416] hover:text-white transition-colors border border-[#2C2416]"
          title="Numbered List"
        >
          <span className="text-sm">1. List</span>
        </button>
        <div className="w-px h-6 bg-[#2C2416] mx-1" />
        <button
          type="button"
          onClick={handleLink}
          className="p-2 hover:bg-[#2C2416] hover:text-white transition-colors border border-[#2C2416]"
          title="Insert Link"
        >
          <Link size={16} />
        </button>
        <label className="p-2 hover:bg-[#2C2416] hover:text-white transition-colors border border-[#2C2416] cursor-pointer" title="Insert Image">
          <Image size={16} />
          <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
        </label>
        <button
          type="button"
          onClick={handleEmbed}
          className="p-2 hover:bg-[#2C2416] hover:text-white transition-colors border border-[#2C2416]"
          title="Insert Embed Code (YouTube, iframes, etc.)"
        >
          <FileText size={16} />
        </button>
      </div>

      {/* Link Input Modal */}
      {showLinkInput && (
        <div className="p-3 bg-[#F5F0E1] border-b-2 border-[#2C2416] flex items-center gap-2">
          <input
            type="url"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="Enter URL (e.g., https://example.com)"
            className="flex-1 px-3 py-2 border-2 border-[#2C2416] font-utility text-sm"
            autoFocus
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                insertLink();
              }
            }}
          />
          <button
            type="button"
            onClick={insertLink}
            className="px-4 py-2 bg-[#3A5A40] text-white border-2 border-[#2C2416] font-utility text-sm hover:bg-[#2C2416]"
          >
            Insert
          </button>
          <button
            type="button"
            onClick={() => { setShowLinkInput(false); setLinkUrl(''); }}
            className="px-4 py-2 bg-[#8B3A3A] text-white border-2 border-[#2C2416] font-utility text-sm hover:bg-[#2C2416]"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Embed Code Input Modal */}
      {showEmbedInput && (
        <div className="p-3 bg-[#F5F0E1] border-b-2 border-[#2C2416]">
          <label className="block font-utility text-xs uppercase tracking-wide mb-2 font-bold">
            Embed Code (YouTube, Twitter, iframes, etc.)
          </label>
          <textarea
            id="embed-code-textarea"
            data-testid="embed-code-textarea"
            value={embedCode}
            onChange={(e) => setEmbedCode(e.target.value)}
            placeholder="Paste your embed code here (e.g., <iframe src='...'></iframe>)"
            className="w-full px-3 py-2 border-2 border-[#2C2416] font-mono text-xs min-h-[100px]"
            autoFocus
          />
          <div className="flex items-center gap-2 mt-2">
            <button
              type="button"
              onClick={insertEmbed}
              data-testid="insert-embed-btn"
              className="px-4 py-2 bg-[#3A5A40] text-white border-2 border-[#2C2416] font-utility text-sm hover:bg-[#2C2416]"
            >
              Insert Embed
            </button>
            <button
              type="button"
              onClick={() => { setShowEmbedInput(false); setEmbedCode(''); }}
              className="px-4 py-2 bg-[#8B3A3A] text-white border-2 border-[#2C2416] font-utility text-sm hover:bg-[#2C2416]"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Editor Area */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="p-4 min-h-[400px] focus:outline-none prose prose-sm max-w-none"
        style={{
          fontFamily: 'Spectral, serif',
          fontSize: '16px',
          lineHeight: '1.6',
          color: '#2C2416'
        }}
        data-placeholder={placeholder || 'Start writing...'}
      />

      <style>{`
        [contentEditable="true"]:empty:before {
          content: attr(data-placeholder);
          color: #999;
          font-style: italic;
        }
        .prose h2 { font-size: 1.5em; font-weight: bold; margin: 1em 0 0.5em; }
        .prose h3 { font-size: 1.25em; font-weight: bold; margin: 1em 0 0.5em; }
        .prose p { margin: 0.5em 0; }
        .prose ul, .prose ol { margin: 0.5em 0; padding-left: 2em; }
        .prose a { color: #3A5A40; text-decoration: underline; }
        .prose img { max-width: 100%; height: auto; margin: 1rem 0; }
        .prose .embed-wrapper { margin: 1rem 0; }
        .prose iframe { max-width: 100%; border: none; }
      `}</style>
    </div>
  );
};

// ============== NOTIFICATION SUBSCRIPTION BANNER ==============
const NotificationBanner = () => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isUnsupportedBrowser, setIsUnsupportedBrowser] = useState(false);

  useEffect(() => {
    // Check if user already subscribed
    const checkSubscription = async () => {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      }
    };
    checkSubscription();

    // Check for unsupported browsers (iOS Safari specifically)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const hasPushManager = 'PushManager' in window;
    if (isIOS && !hasPushManager) {
      setIsUnsupportedBrowser(true);
    }

    // Check if banner was dismissed and if 24 hours have passed
    const dismissedTimestamp = localStorage.getItem('notification-banner-dismissed');
    if (dismissedTimestamp) {
      const dismissedTime = parseInt(dismissedTimestamp);
      const oneDayInMs = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
      const currentTime = Date.now();
      
      // If less than 24 hours have passed, keep it dismissed
      if (currentTime - dismissedTime < oneDayInMs) {
        setIsDismissed(true);
      } else {
        // More than 24 hours have passed, show banner again
        localStorage.removeItem('notification-banner-dismissed');
        setIsDismissed(false);
      }
    }
  }, []);

  const subscribeToPush = async () => {
    setIsLoading(true);
    try {
      // Detect iOS devices
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      
      // Check if browser supports notifications
      if (!('Notification' in window)) {
        alert('❌ Your browser does not support notifications.');
        setIsLoading(false);
        return false;
      }

      // Check if service workers are supported
      if (!('serviceWorker' in navigator)) {
        alert('❌ Your browser does not support service workers, which are required for notifications.');
        setIsLoading(false);
        return false;
      }

      // Check if push notifications are supported
      if (!('PushManager' in window)) {
        if (isIOS && isSafari) {
          alert('📱 iPhone/iPad Safari: Web Push notifications require iOS 16.4 or later and may have limited support. Please ensure:\n\n1. You have iOS 16.4+\n2. You\'ve added this site to your Home Screen\n3. You\'re opening from the Home Screen icon\n\nAlternatively, try using Chrome or Firefox on your iPhone.');
        } else {
          alert('❌ Your browser does not support push notifications. Please try Chrome, Firefox, or Edge.');
        }
        setIsLoading(false);
        return false;
      }

      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        alert('❌ Please enable notifications in your browser settings to receive updates.');
        setIsLoading(false);
        return false;
      }

      // Wait for service worker to be ready
      const registration = await navigator.serviceWorker.ready;
      
      // Use the correct VAPID public key (base64url format)
      const vapidPublicKey = 'BMZFdSvlwyelPp4E4BLytJbU0OCmC8FhT3uL8wI1SdxRdn2OhmHGLfbAzH8ZEaU9FLIGPpJLCfrR1e2qRMEHqzU';
      
      // Subscribe to push notifications
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidPublicKey
      });

      // Send subscription to backend
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
          auth: arrayBufferToBase64(subscription.getKey('auth'))
        }
      });

      setIsSubscribed(true);
      alert('✅ Subscribed! You\'ll receive notifications for contests, special offers, breaking news and more.');
      setIsLoading(false);
      return true;
    } catch (err) {
      console.error('Push subscription error:', err);
      let errorMessage = 'Failed to subscribe to notifications.';
      
      if (err.name === 'NotAllowedError') {
        errorMessage = '❌ Notification permission was denied. Please enable notifications in your browser settings.';
      } else if (err.name === 'AbortError') {
        errorMessage = '❌ Push subscription failed. Please make sure you are using HTTPS and notifications are enabled.';
      } else if (err.message && err.message.includes('permission')) {
        errorMessage = '❌ Notification permission denied. Please check your browser settings.';
      }
      
      alert(errorMessage);
      setIsLoading(false);
      return false;
    }
  };

  // Helper function to convert ArrayBuffer to Base64
  const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    // Store current timestamp instead of boolean
    localStorage.setItem('notification-banner-dismissed', Date.now().toString());
  };

  return (
    <div className="bg-gradient-to-r from-[#8B3A3A] to-[#6B2A2A] border-y-2 border-[#2C2416] mb-8 relative" data-testid="push-subscription-banner">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center gap-6 text-center">
          <div className="flex flex-col items-center gap-4 text-[#F5F0E1]">
            <div className="p-3 bg-white/10 rounded-full">
              <Bell size={40} />
            </div>
            <p style={{fontFamily: "'Germania One', serif", textShadow: '2px 2px 4px rgba(0,0,0,0.75)'}} className="text-xl md:text-3xl font-bold tracking-wider leading-tight max-w-4xl">
              Subscribe to notifications for contests, special offers, breaking news and more
            </p>
            {isUnsupportedBrowser && (
              <div className="text-sm md:text-base text-[#F5F0E1] bg-white/10 px-4 py-3 rounded max-w-2xl">
                <p className="font-bold mb-2">📱 iPhone/iPad Users:</p>
                <p className="mb-2">To receive notifications, you'll need to:</p>
                <ol className="text-left text-sm space-y-1 ml-4 list-decimal">
                  <li>Tap the <Share size={14} className="inline" /> Share button at the bottom of Safari</li>
                  <li>Scroll and select "Add to Home Screen"</li>
                  <li>Open Vigilante Radio from your Home Screen</li>
                  <li>Subscribe to notifications</li>
                </ol>
                <p className="text-xs mt-2 opacity-80">Requires iOS 16.4+</p>
              </div>
            )}
          </div>
          
          <button
            onClick={subscribeToPush}
            disabled={isLoading || isSubscribed}
            style={{fontFamily: "'Germania One', serif", textShadow: '1px 1px 2px rgba(0,0,0,0.35)'}}
            className="bg-[#F5F0E1] text-[#8B3A3A] px-8 py-4 border-2 border-[#2C2416] text-lg tracking-widest font-bold hover:bg-white transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-3 shadow-lg"
            data-testid="push-subscribe-button"
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin" size={22} />
                Subscribing...
              </>
            ) : isSubscribed ? (
              <>
                <Bell size={22} />
                Subscribed
              </>
            ) : (
              <>
                <Bell size={22} />
                Subscribe Now
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============== ROTATING AD BANNER ==============
const AdBanner = () => {
  const [ads, setAds] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/ads`);
        if (!cancelled) setAds(r.data || []);
      } catch (err) {
        console.debug('Failed to load ads', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Rotate every 3 seconds with a short fade for polish.
  useEffect(() => {
    if (ads.length < 2) return; // No point rotating a single ad
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setCurrentIdx((i) => (i + 1) % ads.length);
        setVisible(true);
      }, 250); // Match the fade-out transition
    }, 3000);
    return () => clearInterval(interval);
  }, [ads.length]);

  if (ads.length === 0) return null;
  const ad = ads[currentIdx];

  const inner = (
    <div
      className="relative w-full flex items-center justify-center border-y-2 border-[#8B3A3A] overflow-hidden py-3"
      data-testid="ad-banner-slot"
    >
      <div
        className="w-full flex items-center justify-center"
        style={{ transition: 'opacity 250ms ease-in-out', opacity: visible ? 1 : 0 }}
      >
      {ad.image_url ? (
        <div className="relative w-full max-w-md md:max-w-lg aspect-[5/3] mx-auto">
          <img
            src={ad.image_url}
            alt={ad.title || 'Advertisement'}
            className="absolute inset-0 w-full h-full object-contain"
            data-testid="ad-banner-image"
          />
        </div>
      ) : (
        <span className="font-heading text-lg text-[#2C2416] px-4 py-3">{ad.title}</span>
      )}
      {ad.caption && (
        <span className="hidden md:block ml-4 font-utility text-xs uppercase tracking-wider text-[#5C503D]">
          {ad.caption}
        </span>
      )}
      </div>
      {ads.length > 1 && (
        <span className="absolute bottom-1 right-2 font-mono text-[10px] text-[#5C503D]/60" data-testid="ad-banner-counter">
          {currentIdx + 1}/{ads.length}
        </span>
      )}
    </div>
  );

  return (
    <div className="w-full" data-testid="ad-banner">
      {ad.link_url ? (
        <a
          href={ad.link_url}
          target="_blank"
          rel="noopener noreferrer sponsored"
          aria-label={`${ad.title || 'Advertisement'} — external link`}
          data-testid="ad-banner-link"
        >
          {inner}
        </a>
      ) : inner}
    </div>
  );
};

// ============== PWA INSTALL PROMPT ==============
const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);

  // Subscribe to push notifications
  const subscribeToPush = async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        console.log('Push notifications not supported');
        return false;
      }

      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.log('Notification permission denied');
        return false;
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Get VAPID public key from server
      const vapidResponse = await axios.get(`${API}/push/vapid-public-key`);
      const vapidPublicKey = vapidResponse.data.publicKey;

      // Convert VAPID key from base64 to Uint8Array
      const convertedVapidKey = urlBase64ToUint8Array(vapidPublicKey);

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey
      });

      // Send subscription to server
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
          auth: arrayBufferToBase64(subscription.getKey('auth'))
        },
        user_agent: navigator.userAgent
      });

      setNotificationsEnabled(true);
      localStorage.setItem('notifications-enabled', 'true');
      console.log('Push subscription successful');
      return true;
    } catch (error) {
      console.error('Error subscribing to push:', error);
      return false;
    }
  };

  // Helper functions for encoding
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const arrayBufferToBase64 = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  };

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                      window.navigator.standalone || 
                      document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check if iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    // Check if notifications are already enabled
    const notifEnabled = localStorage.getItem('notifications-enabled') === 'true';
    setNotificationsEnabled(notifEnabled);

    // Listen for beforeinstallprompt event (Android/Desktop)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after 3 seconds if not dismissed before
      setTimeout(() => {
        const dismissed = localStorage.getItem('pwa-prompt-dismissed');
        if (!dismissed && !standalone) {
          setShowPrompt(true);
        }
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS, show prompt if not standalone and not dismissed
    if (ios && !standalone) {
      setTimeout(() => {
        const dismissed = localStorage.getItem('pwa-prompt-dismissed');
        if (!dismissed) {
          setShowPrompt(true);
        }
      }, 3000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt && !isIOS) return;

    if (deferredPrompt) {
      // Android/Desktop
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('PWA installed');
        // Subscribe to push notifications after install
        await subscribeToPush();
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
    // For iOS, the prompt stays open with instructions
  };

  const handleEnableNotifications = async () => {
    const success = await subscribeToPush();
    if (success) {
      alert('✅ Notifications enabled! You\'ll receive updates about new deals, events, and news.');
    } else {
      alert('❌ Could not enable notifications. Please check your browser settings.');
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  if (isStandalone || !showPrompt) return null;

  return (
    <ModalPortal>
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-slide-up">
        <div className="bg-[#F5F0E1] border-2 border-[#2C2416] shadow-lg">
          {/* Header */}
          <div className="p-4 border-b-2 border-[#2C2416] bg-[#2C2416] text-[#F5F0E1]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShoppingBag size={20} />
                <h3 className="font-utility text-sm uppercase tracking-wide font-bold">Install App</h3>
              </div>
              <button onClick={handleDismiss} className="hover:opacity-70">
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            <p className="font-utility text-sm mb-4">
              Install Vigilante Radio Montana on your device for quick access to news, deals, events, and more!
            </p>

            {isIOS ? (
              // iOS Instructions
              <div className="space-y-3 mb-4">
                <p className="font-utility text-xs font-bold uppercase tracking-wide">How to Install:</p>
                <ol className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <span className="font-bold">1.</span>
                    <span>Tap the <strong>Share</strong> button <Share size={14} className="inline" /> in Safari</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold">2.</span>
                    <span>Scroll and tap <strong>"Add to Home Screen"</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-bold">3.</span>
                    <span>Tap <strong>"Add"</strong> to confirm</span>
                  </li>
                </ol>
              </div>
            ) : (
              // Android/Desktop button
              <button
                onClick={handleInstallClick}
                className="w-full bg-[#8B3A3A] text-[#F5F0E1] py-3 px-4 border-2 border-[#2C2416] font-utility text-sm uppercase tracking-wide font-bold hover:bg-[#2C2416] transition-colors mb-3"
              >
                <Plus size={16} className="inline mr-2" />
                Add to Home Screen
              </button>
            )}

            {/* Notification Permission Button */}
            {!notificationsEnabled && !isIOS && (
              <button
                onClick={handleEnableNotifications}
                className="w-full bg-[#3A5A40] text-[#F5F0E1] py-2 px-4 border-2 border-[#2C2416] font-utility text-xs uppercase tracking-wide font-bold hover:bg-[#2C2416] transition-colors mb-3 flex items-center justify-center gap-2"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                </svg>
                Enable Notifications
              </button>
            )}

            {notificationsEnabled && (
              <div className="mb-3 p-2 bg-[#3A5A40]/10 border border-[#3A5A40] text-xs font-utility text-[#2C2416] flex items-center gap-2">
                <CheckCircle size={14} className="text-[#3A5A40]" />
                Notifications enabled
              </div>
            )}

            <button
              onClick={handleDismiss}
              className="w-full text-center text-xs font-utility text-[#5C503D] hover:text-[#2C2416] underline"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

// Share icon component for iOS instructions
const Share = ({ size = 16, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
    <polyline points="16 6 12 2 8 6"></polyline>
    <line x1="12" y1="2" x2="12" y2="15"></line>
  </svg>
);

// ============== HEADER ==============
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const navItems = [
    { id: 'radio', label: 'Radio', icon: Radio },
    { id: 'news', label: 'News', icon: Newspaper },
    { id: 'events', label: 'Events', icon: Calendar },
    { id: 'podcast', label: 'Podcast/Archive', icon: Mic },
    { id: 'bands', label: 'Bands', icon: Music },
  ];

  return (
    <header className="bg-[#F5F0E1] border-b-2 border-[#2C2416] sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Logo and Title */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <button className="lg:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <img src="/logo.png" alt="Vigilante Radio Montana" className="h-16 md:h-20 w-auto" data-testid="site-logo" />
              <div className="hidden sm:block">
                <h1 className="font-heading text-xl md:text-2xl font-black tracking-tight" data-testid="site-title">
                  VIGILANTE RADIO MONTANA
                </h1>
                <p className="font-mono text-xs tracking-widest text-[#3A5A40]">YOUR VOICE FOR RESISTING CHANGE</p>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:block border-t-2 border-[#2C2416] pt-4">
          <ul className="flex items-center justify-center gap-1">
            {navItems.map(item => (
              <li key={item.id}>
                <button onClick={() => scrollToSection(item.id)}
                  className="flex items-center gap-2 px-4 py-2 font-utility text-sm uppercase tracking-wide hover:bg-[#2C2416] hover:text-[#F5F0E1] transition-colors"
                  data-testid={`nav-${item.id}`}>
                  <item.icon size={16} />
                  {item.label}
                </button>
              </li>
            ))}
            <li>
              <a href="/business"
                className="flex items-center gap-2 px-4 py-2 font-utility text-sm uppercase tracking-wide bg-[#8B3A3A] text-[#F5F0E1] hover:bg-[#6B2A2A] transition-colors border-l-2 border-[#2C2416]"
                data-testid="nav-business">
                <User size={16} />
                Business Portal
              </a>
            </li>
          </ul>
        </nav>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="lg:hidden border-t-2 border-[#2C2416] pt-4 mt-4">
            <ul className="flex flex-col gap-1">
              {navItems.map(item => (
                <li key={item.id}>
                  <button onClick={() => scrollToSection(item.id)}
                    className="w-full text-left px-4 py-3 font-utility text-sm uppercase tracking-wide hover:bg-[#E8E2D2] flex items-center gap-3">
                    <item.icon size={18} />
                    {item.label}
                  </button>
                </li>
              ))}
              <li className="border-t-2 border-[#2C2416]/20 pt-2 mt-2">
                <a href="/business"
                  className="w-full text-left px-4 py-3 font-utility text-sm uppercase tracking-wide bg-[#8B3A3A] text-[#F5F0E1] hover:bg-[#6B2A2A] flex items-center gap-3">
                  <User size={18} />
                  Business Portal
                </a>
              </li>
            </ul>
          </nav>
        )}
      </div>
    </header>
  );
};

// ============== AUTH MODAL ==============
const AuthModal = ({ onClose }) => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Invalid credentials');
    } finally { setLoading(false); }
  };

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 9999 }} data-testid="auth-modal">
        <div className="bg-[#F5F0E1] border-2 border-[#2C2416] p-8 max-w-md w-full animate-slide-up">
          <div className="flex justify-between items-center mb-6">
            <h2 className="headline-md">Admin Login</h2>
            <button onClick={onClose} className="p-2 hover:bg-[#E8E2D2]"><X size={24} /></button>
          </div>
          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 text-red-700">
              <AlertCircle size={16} /><span className="font-utility text-sm">{error}</span>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block font-utility text-sm uppercase tracking-wide mb-2">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="input-newspaper w-full" placeholder="admin@vigilanteradio.com" required data-testid="auth-email" />
            </div>
            <div>
              <label className="block font-utility text-sm uppercase tracking-wide mb-2">Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="input-newspaper w-full" placeholder="Enter password" required data-testid="auth-password" />
            </div>
            <button type="submit" className="btn-primary w-full flex items-center justify-center gap-2" disabled={loading} data-testid="auth-submit">
              {loading && <Loader2 className="animate-spin" size={18} />} Sign In
            </button>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
};

// ============== PUSH NOTIFICATION SENDER ==============
const PushNotificationSender = ({ showToast }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [url, setUrl] = useState('/');
  const [loading, setLoading] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(0);

  useEffect(() => {
    fetchSubscriberCount();
  }, []);

  const fetchSubscriberCount = async () => {
    try {
      const res = await axios.get(`${API}/push/subscriptions/count`);
      setSubscriberCount(res.data.count);
    } catch (err) {
      console.error('Error fetching subscriber count:', err);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!title || !body) {
      showToast('Please fill in title and message', 'error');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/push/send`, {
        title,
        body,
        url,
        icon: '/logo-192.png',
        tag: 'vigilante-radio-admin'
      });

      showToast(`✅ Notification sent to ${res.data.sent} users!`, 'success');
      setTitle('');
      setBody('');
      setUrl('/');
    } catch (err) {
      showToast('Failed to send notification', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border-2 border-[#2C2416] p-6">
        <div className="flex items-center gap-3 mb-4">
          <Send size={24} className="text-[#8B3A3A]" />
          <h4 className="font-utility text-lg uppercase tracking-wide font-bold">Send Push Notification</h4>
        </div>
        
        <div className="mb-4 p-4 bg-[#E8E2D2] border border-[#2C2416]">
          <p className="font-utility text-sm">
            <strong>{subscriberCount}</strong> {subscriberCount === 1 ? 'user has' : 'users have'} enabled notifications
          </p>
        </div>

        <form onSubmit={handleSend} className="space-y-4">
          <div>
            <label className="block font-utility text-sm uppercase tracking-wide mb-2">
              Title <span className="text-[#8B3A3A]">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., New Deal Alert!"
              className="w-full px-3 py-2 border-2 border-[#2C2416] font-utility"
              maxLength={50}
            />
            <p className="text-xs text-[#5C503D] mt-1">{title.length}/50 characters</p>
          </div>

          <div>
            <label className="block font-utility text-sm uppercase tracking-wide mb-2">
              Message <span className="text-[#8B3A3A]">*</span>
            </label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="e.g., 50% off at Wild West Pizza this week!"
              rows={4}
              className="w-full px-3 py-2 border-2 border-[#2C2416] font-utility"
              maxLength={120}
            />
            <p className="text-xs text-[#5C503D] mt-1">{body.length}/120 characters</p>
          </div>

          <div>
            <label className="block font-utility text-sm uppercase tracking-wide mb-2">
              Link URL (optional)
            </label>
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="/"
              className="w-full px-3 py-2 border-2 border-[#2C2416] font-utility"
            />
            <p className="text-xs text-[#5C503D] mt-1">Where users go when they click the notification</p>
          </div>

          <button
            type="submit"
            disabled={loading || !title || !body}
            className="w-full bg-[#8B3A3A] text-[#F5F0E1] py-3 px-4 border-2 border-[#2C2416] font-utility text-sm uppercase tracking-wide font-bold hover:bg-[#2C2416] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Sending...
              </>
            ) : (
              <>
                <Send size={18} />
                Send to {subscriberCount} {subscriberCount === 1 ? 'User' : 'Users'}
              </>
            )}
          </button>
        </form>
      </div>

      <div className="bg-white border-2 border-[#2C2416] p-6">
        <h5 className="font-utility text-sm uppercase tracking-wide font-bold mb-3">💡 Tips</h5>
        <ul className="space-y-2 text-sm text-[#5C503D]">
          <li>• Keep titles short and attention-grabbing</li>
          <li>• Use clear, actionable messages</li>
          <li>• Include specific deals or event details</li>
          <li>• Test notifications on your own device first</li>
          <li>• Don't send too frequently (respect your users!)</li>
        </ul>
      </div>
    </div>
  );
};

// ============== NEWSLETTER COMPOSER ==============
const NewsletterComposer = ({ showToast }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmingSend, setConfirmingSend] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(null);
  const [broadcasts, setBroadcasts] = useState([]);

  const loadSubscribers = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/newsletter/subscribers`);
      setSubscriberCount(r.data.count);
    } catch (err) {
      console.error('Failed to load subscriber count', err);
    }
  }, []);

  const loadBroadcasts = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/newsletter/broadcasts`);
      setBroadcasts(r.data.broadcasts || []);
    } catch (err) {
      console.error('Failed to load broadcast history', err);
    }
  }, []);

  useEffect(() => {
    loadSubscribers();
    loadBroadcasts();
  }, [loadSubscribers, loadBroadcasts]);

  const sendTest = async () => {
    if (!subject.trim() || !body.trim()) {
      showToast?.('Please enter both a subject and body before sending a test.', 'error');
      return;
    }
    if (!testEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail)) {
      showToast?.('Enter a valid test email address.', 'error');
      return;
    }
    setSendingTest(true);
    try {
      await axios.post(`${API}/newsletter/send`, {
        subject,
        html: body,
        test_email: testEmail,
      });
      showToast?.(`Test email sent to ${testEmail}`, 'success');
    } catch (err) {
      showToast?.(err.response?.data?.detail || 'Failed to send test email.', 'error');
    } finally {
      setSendingTest(false);
    }
  };

  const sendBroadcast = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/newsletter/send`, { subject, html: body });
      showToast?.(
        `Newsletter sent: ${r.data.success_count} succeeded${r.data.fail_count ? `, ${r.data.fail_count} failed` : ''}.`,
        r.data.fail_count ? 'warning' : 'success'
      );
      // Reset composer on success
      setSubject('');
      setBody('');
      setConfirmingSend(false);
      await loadBroadcasts();
    } catch (err) {
      showToast?.(err.response?.data?.detail || 'Newsletter send failed.', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="newsletter-composer">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="headline-sm">Newsletter Composer</h3>
          <p className="font-utility text-sm text-[#5C503D] mt-1">
            {subscriberCount !== null
              ? `${subscriberCount} active subscriber${subscriberCount === 1 ? '' : 's'}`
              : 'Loading subscriber count…'}
          </p>
        </div>
      </div>

      {/* Subject + Body */}
      <div>
        <label className="block font-utility text-xs uppercase tracking-wider mb-1">Subject *</label>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="This week on Vigilante Radio…"
          className="w-full border-2 border-[#2C2416] bg-transparent p-2 font-body"
          data-testid="newsletter-subject"
        />
      </div>

      <div>
        <div className="flex justify-between items-center mb-1">
          <label className="font-utility text-xs uppercase tracking-wider">Body *</label>
          <button
            type="button"
            onClick={() => setPreviewMode((p) => !p)}
            className="font-utility text-xs uppercase tracking-wider text-[#8B3A3A] hover:underline"
            data-testid="newsletter-preview-toggle"
          >
            {previewMode ? 'Edit' : 'Preview'}
          </button>
        </div>
        {previewMode ? (
          <div
            className="border-2 border-[#2C2416] p-4 bg-white min-h-[200px] font-body leading-relaxed prose max-w-none"
            data-testid="newsletter-preview"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(body) }}
          />
        ) : (
          <RichTextEditor
            value={body}
            onChange={setBody}
            placeholder="Write your newsletter — supports headings, links, images, and embeds."
            data-testid="newsletter-body"
          />
        )}
      </div>

      {/* Test send */}
      <div className="border-2 border-[#5C503D]/30 bg-[#E8E2D2] p-4 rounded">
        <h4 className="font-heading font-bold mb-2 flex items-center gap-2">
          <Send size={16} /> Send Test Email
        </h4>
        <p className="font-utility text-xs text-[#5C503D] mb-3">
          Send the newsletter only to one address so you can preview it before publishing.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="your-email@example.com"
            className="flex-1 border-2 border-[#2C2416] bg-transparent p-2 font-body"
            data-testid="newsletter-test-email"
          />
          <button
            type="button"
            onClick={sendTest}
            disabled={sendingTest}
            className="px-4 py-2 border-2 border-[#2C2416] font-utility text-sm uppercase tracking-wide hover:bg-[#2C2416] hover:text-[#F5F0E1] transition-colors disabled:opacity-60"
            data-testid="newsletter-send-test-btn"
          >
            {sendingTest ? 'Sending…' : 'Send Test'}
          </button>
        </div>
      </div>

      {/* Send to all */}
      <div className="border-t-2 border-[#2C2416] pt-4 flex flex-wrap items-center gap-3 justify-between">
        <p className="font-utility text-sm text-[#5C503D]">
          Ready to send to <strong>{subscriberCount ?? '—'}</strong> subscribers?
        </p>
        <button
          type="button"
          disabled={sending || !subject.trim() || !body.trim() || !subscriberCount}
          onClick={() => setConfirmingSend(true)}
          className="btn-primary disabled:opacity-50"
          data-testid="newsletter-send-all-btn"
        >
          <Send size={16} className="inline-block mr-1" /> Send to All Subscribers
        </button>
      </div>

      {/* Confirm send modal */}
      {confirmingSend && (
        <ModalPortal>
          <div className="fixed inset-0 flex items-center justify-center p-4"
               style={{ background: 'rgba(0,0,0,0.6)', zIndex: 10001 }}
               onClick={() => !sending && setConfirmingSend(false)}
               data-testid="newsletter-confirm-modal">
            <div className="bg-[#F5F0E1] border-4 border-[#2C2416] max-w-md w-full p-6"
                 onClick={(e) => e.stopPropagation()}>
              <h3 className="font-heading text-xl mb-2">Send Newsletter?</h3>
              <p className="font-body mb-4">
                This will email <strong>{subscriberCount}</strong> subscriber
                {subscriberCount === 1 ? '' : 's'}. There's no undo.
              </p>
              <div className="bg-[#E8E2D2] p-3 mb-4 rounded">
                <p className="font-utility text-xs uppercase tracking-wider text-[#5C503D]">Subject</p>
                <p className="font-heading text-base truncate">{subject}</p>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setConfirmingSend(false)} disabled={sending}
                        className="px-4 py-2 border-2 border-[#2C2416] font-utility text-sm uppercase tracking-wide hover:bg-[#E8E2D2]"
                        data-testid="newsletter-confirm-cancel">Cancel</button>
                <button onClick={sendBroadcast} disabled={sending}
                        className="btn-primary disabled:opacity-60"
                        data-testid="newsletter-confirm-send">
                  {sending ? 'Sending…' : 'Yes, Send Now'}
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}

      {/* History */}
      <div className="border-t-2 border-[#2C2416] pt-4">
        <h4 className="font-heading text-lg mb-3">Past Broadcasts</h4>
        {broadcasts.length === 0 ? (
          <p className="font-utility text-sm text-[#5C503D]">No newsletters sent yet.</p>
        ) : (
          <div className="space-y-2" data-testid="newsletter-history">
            {broadcasts.map((b) => (
              <div key={b.id} className="border border-[#2C2416]/30 bg-white p-3 flex flex-wrap justify-between items-center gap-2">
                <div className="min-w-0">
                  <p className="font-heading text-sm truncate" title={b.subject}>{b.subject}</p>
                  <p className="font-utility text-xs text-[#5C503D]">
                    {new Date(b.started_at).toLocaleString()}
                  </p>
                </div>
                <div className="flex gap-3 font-mono text-xs">
                  <span className="text-[#3A5A40]">{b.success_count} sent</span>
                  {b.fail_count > 0 && (
                    <span className="text-[#8B3A3A]">{b.fail_count} failed</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


// ============== AD MANAGER (Admin) ==============
const AdManager = ({ showToast }) => {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, title, image_url, link_url, caption, active, order}
  const [saving, setSaving] = useState(false);

  const emptyForm = { title: '', image_url: '', link_url: '', caption: '', active: true, order: 0 };

  const loadAds = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ads?active_only=false`);
      setAds(r.data || []);
    } catch (err) {
      showToast?.('Failed to load ads.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { loadAds(); }, [loadAds]);

  const startCreate = () => setEditing({ ...emptyForm });
  const startEdit = (ad) => setEditing({ ...emptyForm, ...ad });

  const save = async () => {
    if (!editing.title.trim()) {
      showToast?.('Ad title is required.', 'error');
      return;
    }
    if (!editing.image_url) {
      showToast?.('Please upload an ad image.', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: editing.title.trim(),
        image_url: editing.image_url,
        link_url: (editing.link_url || '').trim(),
        caption: (editing.caption || '').trim(),
        active: !!editing.active,
        order: Number(editing.order) || 0,
      };
      if (editing.id) {
        await axios.put(`${API}/ads/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/ads`, payload);
      }
      showToast?.(editing.id ? 'Ad updated.' : 'Ad created.', 'success');
      setEditing(null);
      await loadAds();
    } catch (err) {
      showToast?.(err.response?.data?.detail || 'Failed to save ad.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ad) => {
    if (!window.confirm(`Delete ad "${ad.title}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/ads/${ad.id}`);
      showToast?.('Ad deleted.', 'success');
      await loadAds();
    } catch (err) {
      showToast?.('Failed to delete ad.', 'error');
    }
  };

  return (
    <div data-testid="ad-manager" className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="headline-sm">Manage Ads</h3>
          <p className="font-utility text-xs text-[#5C503D] mt-1">
            Ads rotate every 3 seconds below the notification banner. Set <strong>active</strong> off to hide temporarily.
          </p>
        </div>
        <button onClick={startCreate} className="btn-primary" data-testid="ad-new-btn">
          <Plus size={16} className="inline mr-1" /> New Ad
        </button>
      </div>

      {loading ? (
        <p className="font-utility text-sm text-[#5C503D]">Loading…</p>
      ) : ads.length === 0 ? (
        <p className="font-utility text-sm text-[#5C503D]">No ads yet. Click "New Ad" to create one.</p>
      ) : (
        <ul className="space-y-3" data-testid="ad-list">
          {ads.map((ad) => (
            <li key={ad.id} className="border-2 border-[#2C2416]/30 bg-white p-3 flex items-center gap-3">
              {ad.image_url && (
                <img src={ad.image_url} alt={ad.title} className="w-20 h-14 object-contain bg-[#2C2416] rounded" />
              )}
              <div className="min-w-0 flex-1">
                <p className="font-heading text-sm truncate">{ad.title}</p>
                <p className="font-utility text-xs text-[#5C503D]">
                  Order: {ad.order} • {ad.active ? 'Active' : 'Hidden'} {ad.link_url && ' • links out'}
                </p>
              </div>
              <button
                onClick={() => startEdit(ad)}
                className="p-2 hover:bg-[#E8E2D2] border border-[#2C2416]"
                title="Edit"
                data-testid={`ad-edit-${ad.id}`}
              ><Edit2 size={16} /></button>
              <button
                onClick={() => remove(ad)}
                className="p-2 hover:bg-red-100 border border-red-600 text-red-600"
                title="Delete"
                data-testid={`ad-delete-${ad.id}`}
              ><Trash2 size={16} /></button>
            </li>
          ))}
        </ul>
      )}

      {/* Edit / Create modal */}
      {editing && (
        <ModalPortal>
          <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.6)', zIndex: 10002 }}
            onClick={() => !saving && setEditing(null)}
          >
            <div
              className="bg-[#F5F0E1] border-4 border-[#2C2416] max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
              data-testid="ad-editor-modal"
            >
              <h3 className="font-heading text-xl mb-4">{editing.id ? 'Edit Ad' : 'New Ad'}</h3>
              <div className="space-y-3">
                <div>
                  <label className="block font-utility text-xs uppercase tracking-wider mb-1">Title *</label>
                  <input
                    type="text"
                    value={editing.title}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    className="w-full border-2 border-[#2C2416] bg-transparent p-2 font-body"
                    data-testid="ad-title-input"
                  />
                </div>
                <FormImageUpload
                  label="Ad Image *"
                  currentImage={editing.image_url}
                  onChange={(v) => setEditing({ ...editing, image_url: v })}
                  helpText="Wide banner works best — image auto-compressed."
                />
                <div>
                  <label className="block font-utility text-xs uppercase tracking-wider mb-1">Link URL (optional)</label>
                  <input
                    type="url"
                    placeholder="https://…"
                    value={editing.link_url}
                    onChange={(e) => setEditing({ ...editing, link_url: e.target.value })}
                    className="w-full border-2 border-[#2C2416] bg-transparent p-2 font-body"
                    data-testid="ad-link-input"
                  />
                </div>
                <div>
                  <label className="block font-utility text-xs uppercase tracking-wider mb-1">Caption (optional, desktop only)</label>
                  <input
                    type="text"
                    value={editing.caption}
                    onChange={(e) => setEditing({ ...editing, caption: e.target.value })}
                    className="w-full border-2 border-[#2C2416] bg-transparent p-2 font-body"
                    data-testid="ad-caption-input"
                  />
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block font-utility text-xs uppercase tracking-wider mb-1">Order</label>
                    <input
                      type="number"
                      value={editing.order}
                      onChange={(e) => setEditing({ ...editing, order: e.target.value })}
                      className="w-full border-2 border-[#2C2416] bg-transparent p-2 font-body"
                      data-testid="ad-order-input"
                    />
                  </div>
                  <label className="flex items-end gap-2 pb-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editing.active}
                      onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                      className="w-4 h-4 accent-[#8B3A3A]"
                      data-testid="ad-active-toggle"
                    />
                    <span className="font-utility text-sm">Active</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-5">
                <button
                  onClick={() => setEditing(null)}
                  disabled={saving}
                  className="px-4 py-2 border-2 border-[#2C2416] font-utility text-sm uppercase tracking-wide hover:bg-[#E8E2D2]"
                  data-testid="ad-cancel-btn"
                >Cancel</button>
                <button
                  onClick={save}
                  disabled={saving}
                  className="btn-primary disabled:opacity-60"
                  data-testid="ad-save-btn"
                >{saving ? 'Saving…' : 'Save Ad'}</button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
};


// ============== TRANSACTION REPORTS ==============
const TransactionReports = ({ showToast }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/reports/transactions/preview`);
      setTransactions(res.data.recent_transactions || []);
      setTotalCount(res.data.total_transactions || 0);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      showToast('Failed to load transactions', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleSendReport = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/reports/transactions/send`);
      showToast('✅ Transaction report sent successfully to vigilanteradiomt@gmail.com!', 'success');
    } catch (err) {
      showToast('Failed to send report email', 'error');
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header & Send Button */}
      <div className="bg-white border-2 border-[#2C2416] p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <FileText size={24} className="text-[#8B3A3A]" />
            <h4 className="font-utility text-lg uppercase tracking-wide font-bold">Transaction Reports</h4>
          </div>
          <button
            onClick={handleSendReport}
            disabled={sending || totalCount === 0}
            className="bg-[#8B3A3A] text-[#F5F0E1] py-2 px-4 border-2 border-[#2C2416] font-utility text-sm uppercase tracking-wide font-bold hover:bg-[#2C2416] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {sending ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Sending...
              </>
            ) : (
              <>
                <Send size={18} />
                Send Report Email
              </>
            )}
          </button>
        </div>

        <div className="p-4 bg-[#E8E2D2] border border-[#2C2416]">
          <p className="font-utility text-sm">
            <strong>{totalCount}</strong> total transaction{totalCount !== 1 ? 's' : ''} • Automated reports sent every <strong>Wednesday at 8:00 AM MT</strong>
          </p>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white border-2 border-[#2C2416] p-6">
        <h5 className="font-utility text-sm uppercase tracking-wide font-bold mb-4">Recent Transactions</h5>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="animate-spin" size={32} />
          </div>
        ) : transactions.length === 0 ? (
          <p className="text-center text-[#5C503D] py-8 font-body">No transactions yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-[#2C2416]">
                  <th className="text-left py-2 px-2 font-utility uppercase tracking-wide">Date</th>
                  <th className="text-left py-2 px-2 font-utility uppercase tracking-wide">Customer</th>
                  <th className="text-left py-2 px-2 font-utility uppercase tracking-wide">Deal/Item</th>
                  <th className="text-left py-2 px-2 font-utility uppercase tracking-wide">Business</th>
                  <th className="text-right py-2 px-2 font-utility uppercase tracking-wide">Amount</th>
                  <th className="text-left py-2 px-2 font-utility uppercase tracking-wide">Voucher</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx, idx) => (
                  <tr key={tx.id || idx} className="border-b border-[#E8E2D2] hover:bg-[#F5F0E1] transition-colors">
                    <td className="py-2 px-2 font-body text-xs">
                      {tx.created_at ? new Date(tx.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="py-2 px-2 font-body">
                      <div className="text-xs">{tx.customer_name || 'N/A'}</div>
                      <div className="text-xs text-[#5C503D]">{tx.customer_email || ''}</div>
                    </td>
                    <td className="py-2 px-2 font-body text-xs">{tx.deal_title || tx.item_title || 'N/A'}</td>
                    <td className="py-2 px-2 font-body text-xs">{tx.business || 'N/A'}</td>
                    <td className="py-2 px-2 font-utility font-bold text-right">${tx.amount?.toFixed(2) || '0.00'}</td>
                    <td className="py-2 px-2 font-mono text-xs">{tx.voucher_code || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-white border-2 border-[#2C2416] p-6">
        <h5 className="font-utility text-sm uppercase tracking-wide font-bold mb-3">ℹ️ About Reports</h5>
        <ul className="space-y-2 text-sm text-[#5C503D]">
          <li>• Reports are automatically emailed every Wednesday at 8:00 AM Mountain Time</li>
          <li>• The CSV attachment includes all transaction details</li>
          <li>• Use "Send Report Email" to manually send the current report anytime</li>
          <li>• Report emails are sent to: <strong>vigilanteradiomt@gmail.com</strong></li>
        </ul>
      </div>
    </div>
  );
};

// ============== ADMIN PANEL ==============

// Business Users Manager Component
const BusinessUsersManager = ({ businessUsers, deals, onRefresh, showToast }) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null); // For delete confirmation
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    business_name: '',
    contact_name: '',
    phone: '',
    deal_ids: []
  });

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      // Business auth routes are now under /api prefix for proper K8s routing
      const registerUrl = `${API}/business/auth/register`;
      
      console.log('📤 Creating business user at:', registerUrl);
      console.log('📦 Form data:', { ...formData, password: '[REDACTED]' });
      
      const response = await axios.post(registerUrl, formData);
      console.log('✅ Business user created:', response.data);
      
      showToast(`✅ ${formData.business_name} created successfully!`, 'success');
      setShowCreateForm(false);
      setFormData({
        email: '',
        password: '',
        business_name: '',
        contact_name: '',
        phone: '',
        deal_ids: []
      });
      
      // Wait a moment for database to sync, then refresh
      setTimeout(() => {
        console.log('🔄 Refreshing business users list...');
        onRefresh();
      }, 500);
    } catch (err) {
      console.error('❌ Business user creation failed:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      const errorMsg = err.response?.data?.detail || err.response?.data?.message || err.message || 'Unknown error';
      showToast(`❌ Failed to create business user: ${errorMsg}`, 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: '', // Don't pre-fill password
      business_name: user.business_name,
      contact_name: user.contact_name,
      phone: user.phone || '',
      deal_ids: user.deal_ids || []
    });
    setShowCreateForm(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      // Only include fields that have values
      const updateData = {
        business_name: formData.business_name,
        contact_name: formData.contact_name,
        phone: formData.phone,
        deal_ids: formData.deal_ids
      };
      
      // Only update email if changed
      if (formData.email !== editingUser.email) {
        updateData.email = formData.email;
      }
      
      // Only update password if provided
      if (formData.password) {
        updateData.password = formData.password;
      }
      
      console.log('📝 Updating business user:', editingUser.id);
      
      await axios.put(`${API}/business/auth/${editingUser.id}`, updateData);
      console.log('✅ Business user updated');
      
      showToast(`✅ ${formData.business_name} updated successfully!`, 'success');
      setShowCreateForm(false);
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        business_name: '',
        contact_name: '',
        phone: '',
        deal_ids: []
      });
      
      setTimeout(() => onRefresh(), 500);
    } catch (err) {
      console.error('❌ Business user update failed:', err);
      const errorMsg = err.response?.data?.detail || err.message;
      showToast(`❌ Failed to update: ${errorMsg}`, 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingUser(null);
    setFormData({
      email: '',
      password: '',
      business_name: '',
      contact_name: '',
      phone: '',
      deal_ids: []
    });
  };

  const handleDelete = async () => {
    if (!deletingUser) return;
    
    try {
      console.log('🗑️ Deleting business user:', deletingUser.id);
      
      await axios.delete(`${API}/business/auth/${deletingUser.id}`);
      console.log('✅ Business user deleted');
      
      showToast(`✅ ${deletingUser.business_name} deleted successfully`, 'success');
      setDeletingUser(null);
      
      setTimeout(() => onRefresh(), 500);
    } catch (err) {
      console.error('❌ Business user deletion failed:', err);
      const errorMsg = err.response?.data?.detail || err.message;
      showToast(`❌ Failed to delete: ${errorMsg}`, 'error');
    }
  };

  const toggleDealSelection = (dealId) => {
    setFormData(prev => ({
      ...prev,
      deal_ids: prev.deal_ids.includes(dealId)
        ? prev.deal_ids.filter(id => id !== dealId)
        : [...prev.deal_ids, dealId]
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="headline-sm">Business Portal Users</h3>
        <button 
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={18} /> Create Business User
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-white border-2 border-[#2C2416] p-6">
          <h4 className="font-utility text-lg uppercase tracking-wide font-bold mb-4">
            {editingUser ? 'Edit Business User' : 'New Business User'}
          </h4>
          <form onSubmit={editingUser ? handleUpdate : handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="font-utility text-sm uppercase tracking-wide block mb-2">Business Name *</label>
                <input
                  type="text"
                  required
                  value={formData.business_name}
                  onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                  className="w-full border-2 border-[#2C2416] p-2 font-body"
                  placeholder="The Bale of Hay Saloon"
                />
              </div>
              <div>
                <label className="font-utility text-sm uppercase tracking-wide block mb-2">Contact Name *</label>
                <input
                  type="text"
                  required
                  value={formData.contact_name}
                  onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                  className="w-full border-2 border-[#2C2416] p-2 font-body"
                  placeholder="John Smith"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="font-utility text-sm uppercase tracking-wide block mb-2">Email *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full border-2 border-[#2C2416] p-2 font-body"
                  placeholder="business@example.com"
                />
              </div>
              <div>
                <label className="font-utility text-sm uppercase tracking-wide block mb-2">
                  Password {editingUser ? '(leave blank to keep current)' : '*'}
                </label>
                <input
                  type="password"
                  required={!editingUser}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full border-2 border-[#2C2416] p-2 font-body"
                  placeholder={editingUser ? "Leave blank to keep current password" : "Min 8 characters"}
                  minLength={formData.password ? "8" : undefined}
                />
              </div>
            </div>

            <div>
              <label className="font-utility text-sm uppercase tracking-wide block mb-2">Phone</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full border-2 border-[#2C2416] p-2 font-body"
                placeholder="406-555-1234"
              />
            </div>

            <div>
              <label className="font-utility text-sm uppercase tracking-wide block mb-2">Assign Deals</label>
              <div className="border-2 border-[#2C2416] p-4 max-h-48 overflow-y-auto space-y-2">
                {deals.length === 0 ? (
                  <p className="font-body text-sm text-[#5C503D]">No deals available. Create deals first.</p>
                ) : (
                  deals.map(deal => (
                    <label key={deal.id} className="flex items-center gap-2 cursor-pointer hover:bg-[#E8E2D2] p-2">
                      <input
                        type="checkbox"
                        checked={formData.deal_ids.includes(deal.id)}
                        onChange={() => toggleDealSelection(deal.id)}
                        className="w-4 h-4"
                      />
                      <span className="font-body text-sm">{deal.title} - {deal.business}</span>
                    </label>
                  ))
                )}
              </div>
            </div>

            <div className="flex gap-4 pt-4 border-t-2 border-[#2C2416]">
              <button
                type="submit"
                disabled={creating}
                className="btn-primary flex items-center gap-2"
              >
                {creating ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                {creating ? (editingUser ? 'Updating...' : 'Creating...') : (editingUser ? 'Update Business User' : 'Create Business User')}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white border-2 border-[#2C2416]">
        <div className="p-4 border-b-2 border-[#2C2416] bg-[#E8E2D2]">
          <p className="font-utility text-sm uppercase tracking-wide font-bold">
            {businessUsers.length} Business User{businessUsers.length !== 1 ? 's' : ''}
          </p>
        </div>
        
        {businessUsers.length === 0 ? (
          <div className="p-8 text-center">
            <User size={48} className="mx-auto text-[#5C503D] mb-4" />
            <p className="font-body text-[#5C503D]">No business users yet. Create one to get started.</p>
          </div>
        ) : (
          <div className="divide-y-2 divide-[#2C2416]">
            {businessUsers.map(user => {
              const userDeals = deals.filter(d => user.deal_ids?.includes(d.id));
              return (
                <div key={user.id} className="p-4 hover:bg-[#E8E2D2] transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h5 className="font-heading text-lg font-bold">{user.business_name}</h5>
                        {user.active ? (
                          <span className="badge-newspaper text-xs">Active</span>
                        ) : (
                          <span className="badge-newspaper text-xs bg-[#8B3A3A]">Inactive</span>
                        )}
                      </div>
                      <div className="space-y-1">
                        <p className="font-body text-sm flex items-center gap-2">
                          <User size={14} /> {user.contact_name}
                        </p>
                        <p className="font-body text-sm flex items-center gap-2">
                          <Mail size={14} /> {user.email}
                        </p>
                        {user.phone && (
                          <p className="font-body text-sm flex items-center gap-2">
                            <Phone size={14} /> {user.phone}
                          </p>
                        )}
                        <p className="font-utility text-xs text-[#5C503D] mt-2">
                          Created: {new Date(user.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      {userDeals.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-[#2C2416]/20">
                          <p className="font-utility text-xs uppercase tracking-wide text-[#5C503D] mb-2">
                            Assigned Deals ({userDeals.length})
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {userDeals.map(deal => (
                              <span key={deal.id} className="inline-block bg-[#E8E2D2] border border-[#2C2416] px-2 py-1 text-xs font-body">
                                {deal.title}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(user)}
                        className="p-2 hover:bg-[#E8E2D2] border border-[#2C2416] transition-colors"
                        title="Edit Business User"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => setDeletingUser(user)}
                        className="p-2 hover:bg-red-100 border border-red-600 text-red-600 transition-colors"
                        title="Delete Business User"
                      >
                        <Trash2 size={18} />
                      </button>
                      <a
                        href="/business"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 hover:bg-white border border-[#2C2416] transition-colors"
                        title="Open Business Portal"
                      >
                        <ExternalLink size={18} />
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deletingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white border-2 border-[#2C2416] p-6 max-w-md w-full">
            <h4 className="font-utility text-lg uppercase tracking-wide font-bold mb-4 text-red-600">
              Delete Business User
            </h4>
            <p className="font-body mb-4">
              Are you sure you want to delete <strong>{deletingUser.business_name}</strong> ({deletingUser.email})?
            </p>
            <p className="font-body text-sm text-[#5C503D] mb-6">
              This action cannot be undone. The business user will lose access to the portal immediately.
            </p>
            <div className="flex gap-4">
              <button
                onClick={handleDelete}
                className="flex-1 bg-red-600 text-white border-2 border-red-600 font-utility text-sm uppercase tracking-wide py-2 px-4 flex items-center justify-center gap-2 hover:bg-red-700 hover:border-red-700 transition-colors"
              >
                <Trash2 size={16} /> Delete
              </button>
              <button
                onClick={() => setDeletingUser(null)}
                className="flex-1 border-2 border-[#2C2416] text-[#2C2416] font-utility text-sm uppercase tracking-wide py-2 px-4 hover:bg-[#E8E2D2] transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const AdminPanel = ({ onClose, showToast }) => {
  const [activeTab, setActiveTab] = useState('deals');
  const [data, setData] = useState({ deals: [], events: [], nonprofits: [], bands: [], posts: [], products: [], businessUsers: [] });
  const [editItem, setEditItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // { type, id, title }

  const tabs = [
    { id: 'deals', label: 'Deals', icon: Tag },
    { id: 'events', label: 'Events', icon: Calendar },
    { id: 'nonprofits', label: 'Nonprofits', icon: Heart },
    { id: 'bands', label: 'Bands', icon: Music },
    { id: 'posts', label: 'Posts', icon: Newspaper },
    { id: 'products', label: 'Products', icon: ShoppingBag },
    { id: 'ads', label: 'Ads', icon: ImageIcon },
    { id: 'businessUsers', label: 'Business Users', icon: User },
    { id: 'notifications', label: 'Push Notifications', icon: Send },
    { id: 'newsletter', label: 'Newsletter', icon: Mail },
    { id: 'reports', label: 'Transaction Reports', icon: FileText },
  ];

  useEffect(() => { fetchAllData(); }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [deals, events, nonprofits, bands, posts, products, businessUsers] = await Promise.all([
        axios.get(`${API}/deals`),
        axios.get(`${API}/events?approved_only=false`),
        axios.get(`${API}/nonprofits`),
        axios.get(`${API}/bands`),
        axios.get(`${API}/posts?published_only=false`),
        axios.get(`${API}/products`),
        axios.get(`${API}/business-users`).catch((err) => {
          console.error('⚠️ Failed to fetch business users:', {
            status: err.response?.status,
            statusText: err.response?.statusText,
            detail: err.response?.data?.detail,
            message: err.message,
            url: `${API}/business-users`,
            hasAuthToken: !!localStorage.getItem('auth_token')
          });
          return { data: [] };
        }),
      ]);
      console.log('✅ Fetched data:', {
        deals: deals.data.length,
        events: events.data.length,
        nonprofits: nonprofits.data.length,
        bands: bands.data.length,
        posts: posts.data.length,
        products: products.data.length,
        businessUsers: businessUsers.data.length
      });
      setData({
        deals: deals.data,
        events: events.data,
        nonprofits: nonprofits.data,
        bands: bands.data,
        posts: posts.data,
        products: products.data,
        businessUsers: businessUsers.data,
      });
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally { setLoading(false); }
  };

  const handleDelete = async (type, id) => {
    const item = data[type]?.find(item => item.id === id);
    setShowDeleteConfirm({ 
      type, 
      id, 
      title: item?.title || item?.name || 'this item' 
    });
  };

  const confirmDelete = async () => {
    if (!showDeleteConfirm) return;
    
    const { type, id } = showDeleteConfirm;
    try {
      await axios.delete(`${API}/${type}/${id}`);
      showToast('Item deleted successfully', 'success');
      fetchAllData();
    } catch (err) {
      showToast(`Error deleting item: ${err.response?.data?.detail || err.message}`, 'error');
    } finally {
      setShowDeleteConfirm(null);
    }
  };

  const handleSave = async (type, item) => {
    try {
      if (item.id) {
        await axios.put(`${API}/${type}/${item.id}`, item);
        showToast('Item updated successfully', 'success');
      } else {
        await axios.post(`${API}/${type}`, item);
        showToast('Item created successfully', 'success');
      }
      setEditItem(null);
      fetchAllData();
    } catch (err) {
      showToast('Error saving item', 'error');
    }
  };


  const handleApproveDeal = async (dealId) => {
    try {
      await axios.post(`${API}/deals/${dealId}/approve`);
      showToast('Deal approved! Confirmation emails sent.', 'success');
      fetchAllData();
    } catch (err) {
      showToast(`Error approving deal: ${err.response?.data?.detail || err.message}`, 'error');
    }
  };

  const handleRejectDeal = async (dealId) => {
    try {
      await axios.delete(`${API}/deals/${dealId}`);
      showToast('Deal rejected and deleted', 'success');
      fetchAllData();
    } catch (err) {
      showToast('Error rejecting deal', 'error');
    }
  };

  const handleApproveNonprofit = async (id) => {
    try {
      await axios.post(`${API}/nonprofits/${id}/approve`);
      showToast('Nonprofit approved! Confirmation emails sent.', 'success');
      fetchAllData();
    } catch (err) {
      showToast(`Error: ${err.response?.data?.detail || err.message}`, 'error');
    }
  };

  const handleRejectNonprofit = async (id) => {
    try {
      await axios.delete(`${API}/nonprofits/${id}`);
      showToast('Nonprofit rejected and deleted', 'success');
      fetchAllData();
    } catch (err) {
      showToast('Error rejecting nonprofit', 'error');
    }
  };

  const handleApproveBand = async (id) => {
    try {
      await axios.post(`${API}/bands/${id}/approve`);
      showToast('Band approved! Confirmation emails sent.', 'success');
      fetchAllData();
    } catch (err) {
      showToast(`Error: ${err.response?.data?.detail || err.message}`, 'error');
    }
  };

  const handleRejectBand = async (id) => {
    try {
      await axios.delete(`${API}/bands/${id}`);
      showToast('Band rejected and deleted', 'success');
      fetchAllData();
    } catch (err) {
      showToast('Error rejecting band', 'error');
    }
  };

  const handleApproveEvent = async (id) => {
    try {
      await axios.post(`${API}/events/${id}/approve`);
      showToast('Event approved! Confirmation emails sent.', 'success');
      fetchAllData();
    } catch (err) {
      showToast(`Error: ${err.response?.data?.detail || err.message}`, 'error');
    }
  };

  const handleRejectEvent = async (id) => {
    try {
      await axios.delete(`${API}/events/${id}`);
      showToast('Event rejected and deleted', 'success');
      fetchAllData();
    } catch (err) {
      showToast('Error rejecting event', 'error');
    }
  };

  const handlePauseDeal = async (id, currentlyPaused) => {
    try {
      const action = currentlyPaused ? 'unpause' : 'pause';
      await axios.post(`${API}/deals/${id}/${action}`);
      fetchAllData(); // Refresh all data
      showToast(`Deal ${currentlyPaused ? 'unpaused' : 'paused'} successfully`, 'success');
    } catch (err) {
      showToast(`Failed to ${currentlyPaused ? 'unpause' : 'pause'} deal`, 'error');
      console.error(err);
    }
  };


  const renderList = () => {
    const items = data[activeTab] || [];
    
    return (
      <div className="space-y-3">
        {items.length === 0 ? (
          <p className="text-center text-[#5C503D] py-8 font-body">No {activeTab} found. Create one!</p>
        ) : (
          items.map(item => (
            <div key={item.id} className="border-2 border-[#2C2416] p-4 hover:bg-[#E8E2D2] transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h4 className="font-heading font-bold">{item.title || item.name}</h4>
                  <p className="font-utility text-sm text-[#5C503D] truncate">
                    {item.business || item.category || item.genre || item.author || ''}
                  </p>
                  {(item.status === 'pending' || (!item.approved && activeTab === 'events') || (!item.published && activeTab === 'posts') || item.paused || (activeTab === 'deals' && item.quantity_available !== null && item.quantity_available !== undefined)) && (
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {item.paused && activeTab === 'deals' && (
                        <span className="inline-block px-2 py-1 text-xs font-utility uppercase bg-[#5C503D] text-white">
                          ⏸ Paused
                        </span>
                      )}
                      {activeTab === 'deals' && item.quantity_available !== null && item.quantity_available !== undefined && (
                        <span className={`inline-block px-2 py-1 text-xs font-utility uppercase ${item.quantity_available === 0 ? 'bg-[#8B3A3A]' : item.quantity_available <= 5 ? 'bg-[#FFA500]' : 'bg-[#3A5A40]'} text-white`}>
                          {item.quantity_available} {item.quantity_available === 1 ? 'left' : 'remaining'}
                        </span>
                      )}
                      {(item.status === 'pending' || (!item.approved && activeTab === 'events') || (!item.published && activeTab === 'posts')) && (
                        <span className="inline-block px-2 py-1 text-xs font-utility uppercase bg-[#FFA500] text-white">
                          {item.status === 'pending' ? 'Pending Approval' : !item.published ? 'Draft' : 'Pending'}
                        </span>
                      )}
                      {item.contact_name && (
                        <span className="text-xs text-[#5C503D]">{item.contact_name}</span>
                      )}
                      {item.contact_email && (
                        <span className="text-xs text-[#5C503D]">({item.contact_email})</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                  {item.status === 'pending' && activeTab === 'deals' && (
                    <>
                      <button 
                        onClick={() => handleApproveDeal(item.id)}
                        className="p-2 bg-[#3A5A40] text-[#F5F0E1] hover:bg-[#2C4530] transition-colors rounded"
                        data-testid={`approve-${item.id}`}
                        title="Approve Deal"
                      >
                        <Check size={18} />
                      </button>
                      <button 
                        onClick={() => handleRejectDeal(item.id)}
                        className="p-2 bg-[#8B3A3A] text-[#F5F0E1] hover:bg-[#6B2A2A] transition-colors rounded"
                        data-testid={`reject-${item.id}`}
                        title="Reject Deal"
                      >
                        <X size={18} />
                      </button>
                    </>
                  )}
                  {item.status === 'pending' && activeTab === 'nonprofits' && (
                    <>
                      <button 
                        onClick={() => handleApproveNonprofit(item.id)}
                        className="p-2 bg-[#3A5A40] text-[#F5F0E1] hover:bg-[#2C4530] transition-colors rounded"
                        title="Approve Nonprofit"
                      >
                        <Check size={18} />
                      </button>
                      <button 
                        onClick={() => handleRejectNonprofit(item.id)}
                        className="p-2 bg-[#8B3A3A] text-[#F5F0E1] hover:bg-[#6B2A2A] transition-colors rounded"
                        title="Reject Nonprofit"
                      >
                        <X size={18} />
                      </button>
                    </>
                  )}
                  {item.status === 'pending' && activeTab === 'bands' && (
                    <>
                      <button 
                        onClick={() => handleApproveBand(item.id)}
                        className="p-2 bg-[#3A5A40] text-[#F5F0E1] hover:bg-[#2C4530] transition-colors rounded"
                        title="Approve Band"
                      >
                        <Check size={18} />
                      </button>
                      <button 
                        onClick={() => handleRejectBand(item.id)}
                        className="p-2 bg-[#8B3A3A] text-[#F5F0E1] hover:bg-[#6B2A2A] transition-colors rounded"
                        title="Reject Band"
                      >
                        <X size={18} />
                      </button>
                    </>
                  )}
                  {!item.approved && activeTab === 'bands' && !item.status && (
                    <button 
                      onClick={() => handleApproveBand(item.id)}
                      className="p-2 bg-[#3A5A40] text-[#F5F0E1] hover:bg-[#2C4530] transition-colors rounded"
                      title="Approve Band"
                    >
                      <Check size={18} />
                    </button>
                  )}
                  {item.status === 'pending' && activeTab === 'events' && (
                    <>
                      <button 
                        onClick={() => handleApproveEvent(item.id)}
                        className="p-2 bg-[#3A5A40] text-[#F5F0E1] hover:bg-[#2C4530] transition-colors rounded"
                        title="Approve Event"
                      >
                        <Check size={18} />
                      </button>
                      <button 
                        onClick={() => handleRejectEvent(item.id)}
                        className="p-2 bg-[#8B3A3A] text-[#F5F0E1] hover:bg-[#6B2A2A] transition-colors rounded"
                        title="Reject Event"
                      >
                        <X size={18} />
                      </button>
                    </>
                  )}
                  {/* Pause/Unpause button for deals */}
                  {activeTab === 'deals' && item.status !== 'pending' && (
                    <button 
                      onClick={() => handlePauseDeal(item.id, item.paused)}
                      className={`p-2 ${item.paused ? 'bg-[#3A5A40]' : 'bg-[#5C503D]'} text-[#F5F0E1] hover:opacity-80 transition-opacity rounded`}
                      title={item.paused ? 'Unpause Deal (Make Active)' : 'Pause Deal (Make Inactive)'}
                    >
                      {item.paused ? <Play size={18} /> : <Pause size={18} />}
                    </button>
                  )}
                  <button onClick={() => setEditItem({ type: activeTab, data: item })}
                    className="p-2 hover:bg-[#2C2416] hover:text-[#F5F0E1] transition-colors" data-testid={`edit-${item.id}`}>
                    <Edit size={18} />
                  </button>
                  <button onClick={() => handleDelete(activeTab, item.id)}
                    className="p-2 hover:bg-[#8B3A3A] hover:text-[#F5F0E1] transition-colors" data-testid={`delete-${item.id}`}>
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" style={{ zIndex: 9999 }} data-testid="admin-panel-modal">
        <div className="bg-[#F5F0E1] border-2 border-[#2C2416] max-w-4xl w-full my-8 animate-slide-up max-h-[90vh] flex flex-col">
          <div className="p-6 border-b-2 border-[#2C2416] flex justify-between items-center shrink-0">
            <h2 className="headline-lg flex items-center gap-2"><Settings size={28} /> Admin Panel</h2>
            <button onClick={onClose} className="p-2 hover:bg-[#E8E2D2]" data-testid="close-admin"><X size={24} /></button>
          </div>

          <div className="flex border-b-2 border-[#2C2416] overflow-x-auto shrink-0">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 font-utility text-sm uppercase tracking-wide whitespace-nowrap transition-colors ${
                  activeTab === tab.id ? 'bg-[#2C2416] text-[#F5F0E1]' : 'hover:bg-[#E8E2D2]'
                }`} data-testid={`admin-tab-${tab.id}`}>
                <tab.icon size={16} /> {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6 overflow-y-auto flex-1">
            {activeTab === 'notifications' ? (
              <PushNotificationSender showToast={showToast} />
            ) : activeTab === 'newsletter' ? (
              <NewsletterComposer showToast={showToast} />
            ) : activeTab === 'ads' ? (
              <AdManager showToast={showToast} />
            ) : activeTab === 'reports' ? (
              <TransactionReports showToast={showToast} />
            ) : activeTab === 'businessUsers' ? (
              <BusinessUsersManager 
                businessUsers={data.businessUsers}
                deals={data.deals}
                onRefresh={fetchAllData}
                showToast={showToast}
              />
            ) : (
              <>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="headline-sm">Manage {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</h3>
                  <button onClick={() => setEditItem({ type: activeTab, data: {} })}
                    className="btn-primary flex items-center gap-2" data-testid="create-new-btn">
                    <Plus size={18} /> Create New
                  </button>
                </div>
                {loading ? (
                  <div className="flex justify-center py-8"><Loader2 className="animate-spin" size={32} /></div>
                ) : renderList()}
              </>
            )}
          </div>

          {editItem && (
            <EditModal 
              type={editItem.type} 
              item={editItem.data} 
              onClose={() => setEditItem(null)}
              onSave={(item) => handleSave(editItem.type, item)}
            />
          )}
          
          {showDeleteConfirm && (
            <ModalPortal>
              <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 10001 }}>
                <div className="bg-[#F5F0E1] border-4 border-[#8B3A3A] max-w-md w-full animate-slide-up">
                  <div className="p-6 border-b-2 border-[#8B3A3A]">
                    <h3 className="headline-md text-[#8B3A3A] flex items-center gap-2">
                      <AlertCircle size={24} /> Confirm Delete
                    </h3>
                  </div>
                  <div className="p-6">
                    <p className="font-body text-lg">
                      Are you sure you want to delete <strong>"{showDeleteConfirm.title}"</strong>?
                    </p>
                    <p className="font-utility text-sm text-[#5C503D] mt-2">
                      This action cannot be undone.
                    </p>
                  </div>
                  <div className="p-6 border-t-2 border-[#2C2416] flex justify-end gap-4">
                    <button 
                      onClick={() => setShowDeleteConfirm(null)} 
                      className="btn-secondary"
                      data-testid="cancel-delete-btn"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={confirmDelete} 
                      className="bg-[#8B3A3A] text-[#F5F0E1] px-6 py-3 font-heading font-bold hover:bg-[#6B2A2A] transition-colors flex items-center gap-2"
                      data-testid="confirm-delete-btn"
                    >
                      <Trash2 size={18} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            </ModalPortal>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};

// ============== EDIT MODAL (Admin) ==============
const EditModal = ({ type, item, onClose, onSave }) => {
  const [formData, setFormData] = useState(item);
  const [loading, setLoading] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await onSave(formData);
    setLoading(false);
  };

  const renderFields = () => {
    switch (type) {
      case 'deals':
        return (
          <>
            <FormField label="Title" value={formData.title || ''} onChange={(v) => handleChange('title', v)} required />
            <FormField label="Business Name" value={formData.business || ''} onChange={(v) => handleChange('business', v)} required />
            <FormField label="Description" value={formData.description || ''} onChange={(v) => handleChange('description', v)} multiline />
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Original Price" type="number" value={formData.original_price || ''} onChange={(v) => handleChange('original_price', parseFloat(v))} required />
              <FormField label="Deal Price" type="number" value={formData.deal_price || ''} onChange={(v) => handleChange('deal_price', parseFloat(v))} required />
            </div>
            <FormMultiSelect 
              label="Categories" 
              values={Array.isArray(formData.category) ? formData.category : (formData.category ? [formData.category] : [])} 
              options={CATEGORIES} 
              onChange={(v) => handleChange('category', v)} 
              required 
            />
            <FormMultiSelect 
              label="Locations" 
              values={Array.isArray(formData.location) ? formData.location : (formData.location ? [formData.location] : [])} 
              options={LOCATIONS} 
              onChange={(v) => handleChange('location', v)} 
              required 
            />
            <FormImageUpload label="Deal Image" currentImage={formData.image_url} onChange={(v) => handleChange('image_url', v)} />
            <FormField label="Expires At" type="date" value={formData.expires_at?.split('T')[0] || ''} onChange={(v) => handleChange('expires_at', v)} />
            <FormField 
              label="Quantity Available (leave blank for unlimited)" 
              type="number" 
              value={formData.quantity_available || ''} 
              onChange={(v) => handleChange('quantity_available', v ? parseInt(v) : null)} 
              placeholder="e.g., 50"
            />
            <p className="text-xs text-[#5C503D] -mt-3 mb-3">
              💡 Set a quantity to limit inventory. Leave blank for unlimited deals.
            </p>
            <FormCheckbox label="Featured Deal" checked={formData.featured || false} onChange={(v) => handleChange('featured', v)} />
            <FormCheckbox label="✅ Approved (visible to public)" checked={formData.approved || false} onChange={(v) => handleChange('approved', v)} />
          </>
        );
      case 'events':
        return (
          <>
            <FormField label="Title" value={formData.title || ''} onChange={(v) => handleChange('title', v)} required />
            <FormField label="Description" value={formData.description || ''} onChange={(v) => handleChange('description', v)} multiline />
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Date" type="date" value={formData.date || ''} onChange={(v) => handleChange('date', v)} required />
              <FormField label="Time" value={formData.time || ''} onChange={(v) => handleChange('time', v)} placeholder="e.g., 7:00 PM" required />
            </div>
            <FormField label="Location" type="select" value={formData.location || ''} onChange={(v) => handleChange('location', v)} options={LOCATIONS} required />
            <FormField label="Category" type="select" value={formData.category || ''} onChange={(v) => handleChange('category', v)} options={EVENT_CATEGORIES} required />
            <FormField label="Organizer" value={formData.organizer || ''} onChange={(v) => handleChange('organizer', v)} />
            <FormImageUpload label="Event Image" currentImage={formData.image_url} onChange={(v) => handleChange('image_url', v)} />
            <div className="grid grid-cols-2 gap-4">
              <FormCheckbox label="Featured Event" checked={formData.featured || false} onChange={(v) => handleChange('featured', v)} />
              <FormCheckbox label="Approved" checked={formData.approved || false} onChange={(v) => handleChange('approved', v)} />
            </div>
          </>
        );
      case 'nonprofits':
        return (
          <>
            <FormField label="Name" value={formData.name || ''} onChange={(v) => handleChange('name', v)} required />
            <FormField label="Mission" value={formData.mission || ''} onChange={(v) => handleChange('mission', v)} placeholder="Short mission statement" />
            <FormField label="Description" value={formData.description || ''} onChange={(v) => handleChange('description', v)} multiline />
            <FormField label="Category" value={formData.category || ''} onChange={(v) => handleChange('category', v)} placeholder="e.g., Healthcare, Environment" />
            <FormMultiSelect 
              label="Locations" 
              values={Array.isArray(formData.location) ? formData.location : (formData.location ? [formData.location] : [])} 
              options={LOCATIONS} 
              onChange={(v) => handleChange('location', v)} 
            />
            <FormImageUpload label="Organization Image" currentImage={formData.image_url} onChange={(v) => handleChange('image_url', v)} />
            <FormField label="Website" value={formData.website || ''} onChange={(v) => handleChange('website', v)} />
            <FormField label="Donation URL" value={formData.donation_url || ''} onChange={(v) => handleChange('donation_url', v)} />
            <FormCheckbox label="Featured Nonprofit" checked={formData.featured || false} onChange={(v) => handleChange('featured', v)} />
            <FormCheckbox label="✅ Approved (visible to public)" checked={formData.approved || false} onChange={(v) => handleChange('approved', v)} />
          </>
        );
      case 'bands':
        return (
          <>
            <FormField label="Name" value={formData.name || ''} onChange={(v) => handleChange('name', v)} required />
            <FormField label="Genre" value={formData.genre || ''} onChange={(v) => handleChange('genre', v)} placeholder="e.g., Bluegrass, Folk" />
            <FormField label="Bio" value={formData.bio || ''} onChange={(v) => handleChange('bio', v)} multiline />
            <FormField label="Location" type="select" value={formData.location || ''} onChange={(v) => handleChange('location', v)} options={LOCATIONS} />
            <FormImageUpload label="Band Photo" currentImage={formData.image_url} onChange={(v) => handleChange('image_url', v)} />
            <FormField 
              label="Amazon Music Page URL" 
              value={formData.amazon_music_url || ''} 
              onChange={(v) => handleChange('amazon_music_url', v)} 
              placeholder="https://www.amazon.com/music/player/artists/..."
              helpText="Full Amazon Music artist or album page URL"
            />
            <FormField label="Website" value={formData.website || ''} onChange={(v) => handleChange('website', v)} />
            <FormCheckbox label="Featured Band" checked={formData.featured || false} onChange={(v) => handleChange('featured', v)} />
            <FormCheckbox label="✅ Approved (visible to public)" checked={formData.approved || false} onChange={(v) => handleChange('approved', v)} />
          </>
        );
      case 'posts':
        return (
          <div className="space-y-6">
            {/* WordPress-style Post Editor */}
            <div className="bg-white border-2 border-[#2C2416] p-6">
              <FormField 
                label="Post Title" 
                value={formData.title || ''} 
                onChange={(v) => handleChange('title', v)} 
                required 
                placeholder="Enter your post title here"
              />
            </div>

            <div className="bg-white border-2 border-[#2C2416] p-6">
              <label className="block font-utility text-sm uppercase tracking-wide mb-3">Featured Image</label>
              <FormImageUpload 
                currentImage={formData.image_url} 
                onChange={(v) => handleChange('image_url', v)}
                helpText="Upload a featured image for your post (recommended: 1200x630px)"
              />
            </div>

            <div className="bg-white border-2 border-[#2C2416] p-6">
              <FormField 
                label="Excerpt" 
                value={formData.excerpt || ''} 
                onChange={(v) => handleChange('excerpt', v)} 
                multiline 
                rows={3}
                placeholder="Write a short summary that will appear in post previews and cards..."
              />
            </div>

            <div className="bg-white border-2 border-[#2C2416] p-6">
              <label className="block font-utility text-sm uppercase tracking-wide mb-3">Post Content</label>
              <RichTextEditor
                value={formData.content || ''}
                onChange={(content) => handleChange('content', content)}
                placeholder="Write your post content here... 

Use the toolbar above to:
• Format text (bold, italic, underline)
• Add hyperlinks
• Insert images
• Create lists and headings
• And more!"
              />
              <p className="text-xs text-[#5C503D] mt-4 font-utility">
                💡 <strong>Tip:</strong> Select text to format it. Click the link icon to add hyperlinks. Use the image icon to embed images.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white border-2 border-[#2C2416] p-6">
                <FormField 
                  label="Category" 
                  value={formData.category || ''} 
                  onChange={(v) => handleChange('category', v)} 
                  placeholder="e.g., Local News, Tourism, Music"
                />
              </div>
              
              <div className="bg-white border-2 border-[#2C2416] p-6">
                <FormField 
                  label="Author" 
                  value={formData.author || ''} 
                  onChange={(v) => handleChange('author', v)}
                  placeholder="e.g., VRM News Desk"
                />
              </div>
            </div>

            <div className="bg-[#E8E2D2] border-2 border-[#2C2416] p-6">
              <h4 className="font-utility text-sm uppercase tracking-wide mb-4 font-bold">Publishing Options</h4>
              <div className="space-y-3">
                <FormCheckbox 
                  label="Published (visible to public)" 
                  checked={formData.published || false} 
                  onChange={(v) => handleChange('published', v)} 
                />
                <FormCheckbox 
                  label="⭐ Featured Post (stick to top)" 
                  checked={formData.featured || false} 
                  onChange={(v) => handleChange('featured', v)} 
                />
                <p className="text-xs text-[#5C503D] font-utility mt-2">Featured posts will always appear at the top of the news section, regardless of publish date.</p>
              </div>
            </div>
          </div>
        );
      case 'products':
        return (
          <>
            <FormField label="Name" value={formData.name || ''} onChange={(v) => handleChange('name', v)} required />
            <FormField label="Description" value={formData.description || ''} onChange={(v) => handleChange('description', v)} multiline />
            <FormField label="Price" type="number" value={formData.price || ''} onChange={(v) => handleChange('price', parseFloat(v))} required />
            <FormField label="Category" value={formData.category || ''} onChange={(v) => handleChange('category', v)} placeholder="e.g., Apparel, Drinkware" />
            <FormField label="Image URL" value={formData.image_url || ''} onChange={(v) => handleChange('image_url', v)} />
            <div>
              <label className="block font-utility text-sm uppercase tracking-wide mb-2">Variants (JSON array)</label>
              <textarea
                value={JSON.stringify(formData.variants || [], null, 2)}
                onChange={(e) => {
                  try { handleChange('variants', JSON.parse(e.target.value)); } catch {}
                }}
                className="w-full border-2 border-[#2C2416] bg-transparent p-3 font-mono text-sm"
                rows={4}
                placeholder='[{"size": "M", "color": "Black", "stock": 10}]'
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" style={{ zIndex: 10000 }}>
      <div className="bg-[#F5F0E1] border-2 border-[#2C2416] max-w-4xl w-full my-8 animate-slide-up max-h-[90vh] flex flex-col">
        <div className="p-6 border-b-2 border-[#2C2416] flex justify-between items-center bg-[#F5F0E1] flex-shrink-0">
          <h3 className="headline-md">{item.id ? 'Edit' : 'Create'} {type.slice(0, -1)}</h3>
          <button onClick={onClose} className="p-2 hover:bg-[#E8E2D2]"><X size={24} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {renderFields()}
        </form>
        <div className="p-6 border-t-2 border-[#2C2416] flex justify-end gap-4 bg-[#F5F0E1] flex-shrink-0">
          <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handleSubmit} disabled={loading} className="btn-primary flex items-center gap-2" data-testid="save-item-btn">
            {loading && <Loader2 className="animate-spin" size={18} />} {item.id ? 'Update' : 'Publish'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============== FORM COMPONENTS ==============
const FormField = ({ label, value, onChange, type = 'text', multiline = false, rows = 4, required = false, placeholder = '', options = [] }) => (
  <div>
    <label className="block font-utility text-sm uppercase tracking-wide mb-2">
      {label} {required && <span className="text-[#8B3A3A]">*</span>}
    </label>
    {type === 'select' ? (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border-2 border-[#2C2416] bg-[#F5F0E1] p-3 font-body"
        required={required}
      >
        <option value="">Select {label}</option>
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    ) : multiline ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border-2 border-[#2C2416] bg-transparent p-3 font-body"
        rows={rows}
        required={required}
        placeholder={placeholder}
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input-newspaper w-full"
        required={required}
        placeholder={placeholder}
        step={type === 'number' ? '0.01' : undefined}
      />
    )}
  </div>
);

const FormMultiSelect = ({ label, values = [], options = [], onChange, required = false }) => {
  const handleToggle = (option) => {
    if (values.includes(option)) {
      onChange(values.filter(v => v !== option));
    } else {
      onChange([...values, option]);
    }
  };

  return (
    <div>
      <label className="block font-utility text-sm uppercase tracking-wide mb-2">
        {label} {required && <span className="text-[#8B3A3A]">*</span>}
      </label>
      <div className="grid grid-cols-2 gap-3 p-3 border-2 border-[#2C2416] bg-[#F5F0E1]">
        {options.map(option => (
          <label key={option} className="flex items-center gap-2 cursor-pointer hover:bg-[#E8E2D2] p-2 transition-colors">
            <div className={`w-5 h-5 border-2 border-[#2C2416] flex items-center justify-center flex-shrink-0 ${values.includes(option) ? 'bg-[#3A5A40]' : 'bg-[#F5F0E1]'}`}>
              {values.includes(option) && <Check size={14} className="text-[#F5F0E1]" />}
            </div>
            <input 
              type="checkbox" 
              checked={values.includes(option)} 
              onChange={() => handleToggle(option)} 
              className="sr-only" 
            />
            <span className="font-body text-sm">{option}</span>
          </label>
        ))}
      </div>
      {required && values.length === 0 && (
        <p className="text-xs text-[#8B3A3A] mt-1">Please select at least one option</p>
      )}
    </div>
  );
};

const FormCheckbox = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-3 cursor-pointer">
    <div className={`w-5 h-5 border-2 border-[#2C2416] flex items-center justify-center ${checked ? 'bg-[#3A5A40]' : ''}`}>
      {checked && <Check size={14} className="text-[#F5F0E1]" />}
    </div>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only" />
    <span className="font-utility text-sm">{label}</span>
  </label>
);

const FormImageUpload = ({ label, currentImage, onChange, helpText }) => {
  const [preview, setPreview] = useState(currentImage || null);
  const [uploading, setUploading] = useState(false);

  // Sync preview when parent loads/updates currentImage asynchronously
  // (e.g., when opening an existing post for editing).
  useEffect(() => {
    setPreview(currentImage || null);
  }, [currentImage]);

  /**
   * Compress an image client-side before upload.
   * - Limits long edge to 1600px (preserves quality, drastically cuts file size)
   * - Re-encodes as JPEG at 0.85 quality
   * - Guarantees the resulting base64 stays well under typical proxy body limits.
   * iPhone photos are commonly 4-6 MB raw; this brings them to ~300-600KB.
   */
  const compressImage = (file) => new Promise((resolve, reject) => {
    const img = new window.Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = () => reject(new Error('Could not read image file'));
    img.onload = () => {
      const MAX_DIM = 1600;
      let { width, height } = img;
      if (width > MAX_DIM || height > MAX_DIM) {
        if (width >= height) {
          height = Math.round((height / width) * MAX_DIM);
          width = MAX_DIM;
        } else {
          width = Math.round((width / height) * MAX_DIM);
          height = MAX_DIM;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      // Always re-encode as JPEG for predictable size (PNG of photos balloons in base64)
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      resolve(dataUrl);
    };
    img.onerror = () => reject(new Error('Image could not be decoded'));
    reader.readAsDataURL(file);
  });

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Allow up to 10MB raw (typical iPhone HEIC/JPEG). We compress before upload.
    if (file.size > 10 * 1024 * 1024) {
      alert('Image size must be less than 10MB');
      return;
    }
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    setUploading(true);
    try {
      const dataUrl = await compressImage(file);
      setPreview(dataUrl);
      onChange(dataUrl);
    } catch (err) {
      console.error('Image compression failed:', err);
      alert('Could not process this image. Please try a different file.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onChange('');
  };

  return (
    <div>
      <label className="block font-utility text-sm uppercase tracking-wide mb-2">
        {label}
      </label>
      <div className="space-y-3">
        {preview ? (
          <div className="relative border-2 border-[#2C2416] p-2">
            <img src={preview} alt="Preview" className="w-full h-48 object-cover" />
            <button
              type="button"
              onClick={handleRemove}
              className="absolute top-4 right-4 bg-[#8B3A3A] text-[#F5F0E1] p-2 hover:bg-[#6B2A2A] transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <label className="flex flex-col items-center justify-center border-2 border-dashed border-[#2C2416] p-8 cursor-pointer hover:bg-[#E8E2D2] transition-colors">
            <Image size={32} className="text-[#5C503D] mb-2" />
            <span className="font-utility text-sm text-[#5C503D]">Click to upload image</span>
            <span className="font-utility text-xs text-[#5C503D] mt-1">(Max 10MB — auto-compressed)</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
              disabled={uploading}
            />
          </label>
        )}
        {uploading && (
          <div className="flex items-center justify-center gap-2 text-[#5C503D]">
            <Loader2 className="animate-spin" size={16} />
            <span className="font-utility text-sm">Uploading...</span>
          </div>
        )}
      </div>
      
      {helpText && <p className="text-xs text-[#5C503D] mt-2 font-utility">{helpText}</p>}
    </div>
  );
};

// ============== SUBMIT EVENT MODAL (Public) ==============
const SubmitEventModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    category: '',
    organizer: '',
    image_url: '',
    featured: false,
    all_day: false,
    end_date: '',
    end_time: '',
    repeat_type: 'none',
    repeat_interval: 1,
    repeat_days: '',
    repeat_end_type: 'never',
    repeat_end_date: '',
    repeat_end_count: 10
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleDay = (day) => {
    const days = formData.repeat_days ? formData.repeat_days.split(',') : [];
    if (days.includes(day)) {
      setFormData(prev => ({ ...prev, repeat_days: days.filter(d => d !== day).join(',') }));
    } else {
      setFormData(prev => ({ ...prev, repeat_days: [...days, day].join(',') }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/events/submit-request`, formData);
      setSubmitted(true);
    } catch (err) {
      alert('Failed to submit request. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <ModalPortal>
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
          <div className="bg-[#F5F0E1] border-4 border-[#3A5A40] max-w-md w-full animate-slide-up">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-[#3A5A40] rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-[#F5F0E1]" />
              </div>
              <h2 className="headline-lg text-[#3A5A40] mb-3">Request Submitted!</h2>
              <p className="font-body text-lg mb-4">
                Thank you for your interest in listing your event on Vigilante Radio Montana!
              </p>
              <p className="font-body text-[#5C503D] mb-6">
                We've received your request and will contact you shortly.
              </p>
              <button onClick={onClose} className="btn-primary">Close</button>
            </div>
          </div>
        </div>
      </ModalPortal>
    );
  }

  const weekDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const selectedDays = formData.repeat_days ? formData.repeat_days.split(',') : [];

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
        <div className="bg-[#F5F0E1] border-2 border-[#2C2416] max-w-3xl w-full max-h-[90vh] flex flex-col animate-slide-up">
          <div className="p-6 border-b-2 border-[#2C2416] flex justify-between items-center bg-[#F5F0E1]">
            <h2 className="headline-lg">Submit Your Event</h2>
            <button onClick={onClose} className="p-2 hover:bg-[#E8E2D2]" type="button">
              <X size={24} />
            </button>
          </div>
          
          <div className="overflow-y-auto flex-1">
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <p className="font-body text-[#5C503D] mb-4">
                Submit your event to be featured on Vigilante Radio Montana.
              </p>

              <div className="border-b-2 border-[#2C2416] pb-4 mb-4">
                <h3 className="font-heading text-lg font-bold mb-3">Contact Information</h3>
                <FormField label="Your Name" value={formData.contact_name} onChange={(v) => handleChange('contact_name', v)} required placeholder="John Doe" />
                <FormField label="Email Address" type="email" value={formData.contact_email} onChange={(v) => handleChange('contact_email', v)} required placeholder="you@example.com" />
                <FormField label="Phone Number" type="tel" value={formData.contact_phone} onChange={(v) => handleChange('contact_phone', v)} required placeholder="(406) 555-1234" />
              </div>

              <div className="border-b-2 border-[#2C2416] pb-4 mb-4">
                <h3 className="font-heading text-lg font-bold mb-3">Event Information</h3>
                <FormField label="Event Title" value={formData.title} onChange={(v) => handleChange('title', v)} required placeholder="Summer Music Festival" />
                <FormField label="Description" value={formData.description} onChange={(v) => handleChange('description', v)} multiline rows={3} placeholder="Tell us about your event..." />
                
                {/* All-day event toggle */}
                <div className="mb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.all_day}
                      onChange={(e) => handleChange('all_day', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="font-utility text-sm">All-day event</span>
                  </label>
                </div>

                {/* Date and time fields */}
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Start Date" type="date" value={formData.date} onChange={(v) => handleChange('date', v)} required />
                  {!formData.all_day && (
                    <FormField label="Start Time" value={formData.time} onChange={(v) => handleChange('time', v)} required placeholder="7:00 PM" />
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <FormField label="End Date (Optional)" type="date" value={formData.end_date} onChange={(v) => handleChange('end_date', v)} />
                  {!formData.all_day && formData.end_date && (
                    <FormField label="End Time" value={formData.end_time} onChange={(v) => handleChange('end_time', v)} placeholder="9:00 PM" />
                  )}
                </div>

                <FormField label="Location" type="select" value={formData.location} onChange={(v) => handleChange('location', v)} options={LOCATIONS} required />
                <FormField label="Category" type="select" value={formData.category} onChange={(v) => handleChange('category', v)} options={EVENT_CATEGORIES} required />
                <FormField label="Organizer Name" value={formData.organizer} onChange={(v) => handleChange('organizer', v)} placeholder="Organization name" />
                <FormImageUpload label="Event Image (Optional)" currentImage={formData.image_url} onChange={(v) => handleChange('image_url', v)} />
              </div>

              {/* Repeating event section */}
              <div className="border-b-2 border-[#2C2416] pb-4 mb-4">
                <h3 className="font-heading text-lg font-bold mb-3">Repeat Event</h3>
                
                <div className="mb-4">
                  <label className="font-utility text-sm font-bold block mb-2">Repeat</label>
                  <select
                    value={formData.repeat_type}
                    onChange={(e) => handleChange('repeat_type', e.target.value)}
                    className="w-full px-3 py-2 border-2 border-[#2C2416] bg-[#F5F0E1] font-body"
                  >
                    <option value="none">Does not repeat</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>

                {formData.repeat_type !== 'none' && (
                  <>
                    {/* Repeat interval */}
                    <div className="mb-4">
                      <label className="font-utility text-sm font-bold block mb-2">Repeat every</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          max="30"
                          value={formData.repeat_interval}
                          onChange={(e) => handleChange('repeat_interval', parseInt(e.target.value))}
                          className="w-20 px-3 py-2 border-2 border-[#2C2416] bg-[#F5F0E1] font-body"
                        />
                        <span className="font-body">
                          {formData.repeat_type === 'daily' && 'day(s)'}
                          {formData.repeat_type === 'weekly' && 'week(s)'}
                          {formData.repeat_type === 'monthly' && 'month(s)'}
                          {formData.repeat_type === 'yearly' && 'year(s)'}
                        </span>
                      </div>
                    </div>

                    {/* Weekly repeat - select days */}
                    {formData.repeat_type === 'weekly' && (
                      <div className="mb-4">
                        <label className="font-utility text-sm font-bold block mb-2">Repeat on</label>
                        <div className="flex flex-wrap gap-2">
                          {weekDays.map(day => (
                            <button
                              key={day}
                              type="button"
                              onClick={() => toggleDay(day)}
                              className={`px-3 py-1 text-sm font-utility uppercase transition-colors ${
                                selectedDays.includes(day)
                                  ? 'bg-[#2C2416] text-[#F5F0E1]'
                                  : 'bg-[#E8E2D2] text-[#2C2416] hover:bg-[#D8D2C2]'
                              }`}
                            >
                              {day.substring(0, 3)}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Repeat end */}
                    <div className="mb-4">
                      <label className="font-utility text-sm font-bold block mb-2">Ends</label>
                      <select
                        value={formData.repeat_end_type}
                        onChange={(e) => handleChange('repeat_end_type', e.target.value)}
                        className="w-full px-3 py-2 border-2 border-[#2C2416] bg-[#F5F0E1] font-body mb-2"
                      >
                        <option value="never">Never</option>
                        <option value="on_date">On date</option>
                        <option value="after_count">After</option>
                      </select>

                      {formData.repeat_end_type === 'on_date' && (
                        <input
                          type="date"
                          value={formData.repeat_end_date}
                          onChange={(e) => handleChange('repeat_end_date', e.target.value)}
                          className="w-full px-3 py-2 border-2 border-[#2C2416] bg-[#F5F0E1] font-body"
                        />
                      )}

                      {formData.repeat_end_type === 'after_count' && (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="1"
                            max="365"
                            value={formData.repeat_end_count}
                            onChange={(e) => handleChange('repeat_end_count', parseInt(e.target.value))}
                            className="w-24 px-3 py-2 border-2 border-[#2C2416] bg-[#F5F0E1] font-body"
                          />
                          <span className="font-body">occurrence(s)</span>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="border-t-2 border-[#2C2416] pt-4 mt-6">
                <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
                  {loading ? (<><Loader2 className="animate-spin" size={18} />Submitting...</>) : (<><Send size={18} />Submit Event Request</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

// ============== SECTION HEADER ==============
const SectionHeader = ({ icon: Icon, overline, title, subtitle, id }) => (
  <div id={id} className="scroll-mt-24 pt-16 pb-8">
    <div className="divider-ornament mb-8">
      <span>{Icon && <Icon size={24} className="text-[#8B3A3A]" />}</span>
    </div>
    <div className="text-center">
      <p className="overline mb-2">{overline}</p>
      <h2 className="font-armstrong text-5xl md:text-6xl mb-4 tracking-wider">{title}</h2>
      {subtitle && <p className="font-body text-lg max-w-2xl mx-auto text-[#5C503D]">{subtitle}</p>}
    </div>
  </div>
);

// ============== RADIO SECTION ==============
// ============== CUSTOM RADIO PLAYER ==============
const CustomRadioPlayer = () => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [error, setError] = useState(null);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [listeners, setListeners] = useState(null);

  const STREAM_URL = 'https://streaming.live365.com/a63544';
  const METADATA_URL = 'https://api.live365.com/station/a63544';

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.muted = muted;
    }
  }, [volume, muted]);

  // Poll Live365 metadata API every 15s for now-playing info
  useEffect(() => {
    let cancelled = false;
    const fetchNowPlaying = async () => {
      try {
        const res = await fetch(METADATA_URL, { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const track = data['current-track'] || data.current_track;
        if (track) {
          // Live365 frequently returns placeholder URLs like "https://apple.store.url/song_id".
          // Detect those and fall back to search URLs built from title + artist.
          const isPlaceholder = (url) => !url || /apple\.store\.url|amazon\.store\.url|amazon\.music\.store\.url/i.test(url);
          const q = encodeURIComponent(`${track.title || ''} ${track.artist || ''}`.trim());
          const appleUrl = isPlaceholder(track.apple_music_url)
            ? `https://music.apple.com/us/search?term=${q}`
            : track.apple_music_url;
          const amazonUrl = isPlaceholder(track.amazon_music_url || track.amazon_store_url)
            ? `https://www.amazon.com/s?k=${q}&i=digital-music`
            : (track.amazon_music_url || track.amazon_store_url);
          setNowPlaying({
            title: track.title || 'Unknown',
            artist: track.artist || '',
            art: track.art || null,
            appleUrl,
            amazonUrl,
          });
        }
        if (typeof data.listeners === 'number') {
          setListeners(data.listeners);
        }
      } catch (err) {
        // Silent fail — metadata is enhancement, not critical
        console.debug('Now-playing fetch failed', err);
      }
    };
    fetchNowPlaying();
    const interval = setInterval(fetchNowPlaying, 15000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    setError(null);
    try {
      if (isPlaying) {
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
        setIsPlaying(false);
      } else {
        setIsLoading(true);
        audio.src = `${STREAM_URL}?t=${Date.now()}`;
        audio.load();
        await audio.play();
        setIsPlaying(true);
      }
    } catch (err) {
      console.error('Radio playback error:', err);
      setError('Unable to start stream. Please try again.');
      setIsPlaying(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAudioEvents = {
    onPlaying: () => { setIsLoading(false); setIsPlaying(true); },
    onWaiting: () => setIsLoading(true),
    onPause: () => setIsPlaying(false),
    onError: () => {
      setError('Stream temporarily unavailable.');
      setIsPlaying(false);
      setIsLoading(false);
    },
  };

  return (
    <div
      className="w-full max-w-md mx-auto bg-black/40 rounded-lg p-5 sm:p-6"
      data-testid="custom-radio-player"
    >
      <audio ref={audioRef} preload="none" crossOrigin="anonymous" {...handleAudioEvents} />

      {/* Now Playing strip */}
      {nowPlaying && (
        <div
          className="flex items-center gap-3 mb-5 p-3 bg-[#2C2416]/60 rounded border border-[#F5F0E1]/10"
          data-testid="now-playing-strip"
        >
          {nowPlaying.art ? (
            <img
              src={nowPlaying.art}
              alt={`${nowPlaying.title} cover`}
              className="w-14 h-14 rounded object-cover flex-shrink-0"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <div className="w-14 h-14 rounded bg-[#8B3A3A]/30 flex-shrink-0 flex items-center justify-center">
              <Music className="w-6 h-6 text-[#F5F0E1]/60" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[10px] uppercase tracking-wider text-[#F5F0E1]/50 mb-0.5">Now Playing</p>
            <p
              className="font-heading text-sm text-[#F5F0E1] truncate"
              title={nowPlaying.title}
              data-testid="now-playing-title"
            >
              {nowPlaying.title}
            </p>
            {nowPlaying.artist && (
              <p
                className="font-utility text-xs text-[#F5F0E1]/70 truncate"
                title={nowPlaying.artist}
                data-testid="now-playing-artist"
              >
                {nowPlaying.artist}
              </p>
            )}
            {(nowPlaying.appleUrl || nowPlaying.amazonUrl) && (
              <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="buy-links">
                {nowPlaying.appleUrl && (
                  <a
                    href={nowPlaying.appleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="buy-apple-music"
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider bg-[#F5F0E1]/10 hover:bg-[#F5F0E1]/20 text-[#F5F0E1] transition-colors"
                  >
                    <ExternalLink className="w-2.5 h-2.5" />
                    Apple Music
                  </a>
                )}
                {nowPlaying.amazonUrl && (
                  <a
                    href={nowPlaying.amazonUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="buy-amazon-music"
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider bg-[#F5F0E1]/10 hover:bg-[#F5F0E1]/20 text-[#F5F0E1] transition-colors"
                  >
                    <ExternalLink className="w-2.5 h-2.5" />
                    Amazon
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Play / Pause button */}
      <div className="flex flex-col items-center gap-4">
        <button
          onClick={togglePlay}
          disabled={isLoading}
          aria-label={isPlaying ? 'Pause radio' : 'Play radio'}
          data-testid="radio-play-pause-btn"
          className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#8B3A3A] hover:bg-[#A04444] active:scale-95 transition-all flex items-center justify-center shadow-lg disabled:opacity-60 disabled:cursor-not-allowed border-4 border-[#F5F0E1]/20"
        >
          {isLoading ? (
            <Loader2 className="w-9 h-9 text-[#F5F0E1] animate-spin" />
          ) : isPlaying ? (
            <Pause className="w-9 h-9 text-[#F5F0E1]" fill="currentColor" />
          ) : (
            <Play className="w-9 h-9 text-[#F5F0E1] ml-1" fill="currentColor" />
          )}
        </button>

        <div className="text-center">
          <p className="font-mono text-xs uppercase tracking-wider text-[#F5F0E1]/60">
            {isLoading ? 'Connecting…' : isPlaying ? 'Now Streaming' : 'Tap to Listen Live'}
          </p>
          {listeners !== null && isPlaying && (
            <p className="font-mono text-[10px] text-[#F5F0E1]/40 mt-1" data-testid="listener-count">
              {listeners} listening
            </p>
          )}
        </div>

        {/* Volume controls */}
        <div className="flex items-center gap-3 w-full max-w-xs">
          <button
            onClick={() => setMuted((m) => !m)}
            aria-label={muted ? 'Unmute' : 'Mute'}
            data-testid="radio-mute-btn"
            className="text-[#F5F0E1]/80 hover:text-[#F5F0E1] transition-colors"
          >
            {muted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={muted ? 0 : volume}
            onChange={(e) => { setVolume(parseFloat(e.target.value)); setMuted(false); }}
            aria-label="Volume"
            data-testid="radio-volume-slider"
            className="flex-1 h-1 accent-[#8B3A3A] cursor-pointer"
          />
        </div>

        {error && (
          <p className="font-mono text-xs text-red-300" data-testid="radio-error">{error}</p>
        )}
      </div>
    </div>
  );
};

const RadioSection = () => (
  <section data-testid="radio-section">
    <SectionHeader icon={Radio} overline="Listen Now" title="VIGILANTE RADIO"
      subtitle="Support Montana music, get Montana news and connect with your community. Broadcasting from the capital of the Montana Territory, Virginia City, MT" id="radio" />
    <div className="max-w-3xl mx-auto">
      <div className="radio-player-container p-6 md:p-8">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="relative">
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-[#F5F0E1] flex items-center justify-center border-4 border-[#F5F0E1]/20 overflow-hidden">
              <img src="/logo.png" alt="Vigilante Radio Montana" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -bottom-2 -right-2 bg-green-500 rounded-full px-3 py-1 border-2 border-[#2C2416]">
              <span className="font-mono text-xs text-white font-bold">LIVE</span>
            </div>
          </div>
          
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-heading text-2xl text-[#F5F0E1] mb-2">Vigilante Radio Montana</h3>
            <p className="font-utility text-[#F5F0E1]/70 mb-4">Live from Southwest Montana</p>
            <div className="flex items-center justify-center md:justify-start gap-2 mb-4">
              <span className="w-3 h-3 rounded-full bg-green-400 animate-pulse" />
              <span className="font-mono text-sm text-[#F5F0E1]/80">ON AIR 24/7</span>
            </div>
          </div>
        </div>
        
        {/* Custom Radio Player - replaces Live365 branded widget for mobile compatibility */}
        <div className="mt-6">
          <CustomRadioPlayer />
        </div>

        {/* Recently Played Widget */}
        <div className="mt-6 bg-black/30 rounded p-4">
          <h4 className="font-utility text-sm uppercase tracking-wide text-[#F5F0E1]/80 mb-2 text-center">RECENTLY PLAYED</h4>
          <iframe 
            src="https://live365.com/embeds/v1/played/a63544?s=md&m=dark" 
            width="100%" 
            height="450"
            frameBorder="0" 
            allow="autoplay" 
            title="Recently Played Songs"
            data-testid="recently-played-widget"
          ></iframe>
        </div>
      </div>
    </div>
  </section>
);

// ============== NEWS SECTION ==============
const NewsSection = () => {
  const [madisonian, setMadisonian] = useState(null);
  const [loadingMadisonian, setLoadingMadisonian] = useState(true);

  useEffect(() => { fetchMadisonian(); }, []);

  const fetchMadisonian = async () => {
    setLoadingMadisonian(true);
    try {
      const res = await axios.get(`${API}/madisonian/latest`);
      setMadisonian(res.data);
    } catch (err) {
      console.error('Error fetching Madisonian:', err);
      setMadisonian({
        issue_date: "Latest Issue",
        issue_url: "https://www.madisoniannews.com/eedition",
        cover_image: "https://www.madisoniannews.com/sites/default/files/styles/cover/public/IMG_3393.jpeg",
        eedition_url: "https://www.madisoniannews.com/eedition"
      });
    } finally { setLoadingMadisonian(false); }
  };

  return (
    <section data-testid="news-section">
      <SectionHeader icon={Newspaper} overline="Montana's Oldest Continually Publishing Newspaper" title="THE MADISONIAN" id="news" />
      
      {/* The Madisonian E-Edition Feature */}
      <div className="max-w-5xl mx-auto mb-12">
        <div className="border-2 border-[#2C2416] bg-[#E8E2D2] overflow-hidden">
          <div className="p-4 md:p-6 border-b-2 border-[#2C2416] bg-[#2C2416] text-[#F5F0E1]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Newspaper className="w-6 h-6" />
                <div>
                  <h3 className="font-heading text-xl md:text-2xl font-bold">THE MADISONIAN</h3>
                  <p className="font-mono text-xs text-[#F5F0E1]/70">Southwest Montana's Community Newspaper Since 1873</p>
                </div>
              </div>
              <a 
                href="https://www.madisoniannews.com/eedition" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hidden md:flex items-center gap-2 bg-[#F5F0E1] text-[#2C2416] px-4 py-2 font-utility text-sm uppercase tracking-wide hover:bg-[#8B3A3A] hover:text-[#F5F0E1] transition-colors"
              >
                <ExternalLink size={16} /> View All Issues
              </a>
            </div>
          </div>
          
          <div className="p-6 md:p-8">
            {loadingMadisonian ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="animate-spin w-8 h-8 text-[#8B3A3A]" />
              </div>
            ) : madisonian && (
              <div className="flex flex-col md:flex-row gap-8 items-center">
                {/* Cover Image */}
                <a 
                  href={madisonian.issue_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block w-full md:w-1/3 group"
                  data-testid="madisonian-cover"
                >
                  <div className="relative border-2 border-[#2C2416] shadow-lg group-hover:shadow-xl transition-shadow overflow-hidden">
                    <img 
                      src={madisonian.cover_image} 
                      alt={`The Madisonian - ${madisonian.issue_date}`}
                      className="w-full h-auto"
                      onError={(e) => {
                        e.target.src = "https://www.madisoniannews.com/sites/default/files/styles/cover/public/IMG_3393.jpeg";
                      }}
                    />
                    <div className="absolute inset-0 bg-[#2C2416]/0 group-hover:bg-[#2C2416]/10 transition-colors flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-[#8B3A3A] text-[#F5F0E1] px-4 py-2 font-utility text-sm uppercase">
                        Read Now
                      </div>
                    </div>
                  </div>
                </a>
                
                {/* Issue Info */}
                <div className="flex-1 text-center md:text-left">
                  <p className="overline mb-2">Current Issue</p>
                  <h4 className="font-heading text-2xl md:text-3xl font-bold mb-4">{madisonian.issue_date}</h4>
                  <p className="font-body text-lg text-[#5C503D] mb-6">
                    Your weekly source for news, sports, and community events across Madison County and Southwest Montana.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                    <a 
                      href={madisonian.issue_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn-primary flex items-center justify-center gap-2"
                    >
                      <FileText size={18} /> Read This Issue
                    </a>
                    <a 
                      href="https://www.madisoniannews.com/subscriptions" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn-secondary flex items-center justify-center gap-2"
                    >
                      <Mail size={18} /> Subscribe
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Mobile View All button */}
          <div className="md:hidden p-4 border-t-2 border-[#2C2416]">
            <a 
              href="https://www.madisoniannews.com/eedition" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-secondary w-full flex items-center justify-center gap-2"
            >
              <ExternalLink size={16} /> View All Issues
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============== VRM BLOG POSTS SECTION ==============
const VRMBlogSection = () => {
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/posts?published_only=true`);
      // Sort posts: featured posts first, then by date
      const sortedPosts = res.data.sort((a, b) => {
        if (a.featured && !b.featured) return -1;
        if (!a.featured && b.featured) return 1;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
      setPosts(sortedPosts);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <section className="section-container">
      <SectionHeader 
        icon={Newspaper} 
        overline="Vigilante Radio Montana" 
        title="MORE NEWS FROM STUDIO 3-7-77"
        subtitle="Updates, stories, and announcements from the heart of Montana"
        id="vrm-news" 
      />

      {posts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map(post => (
            <article key={post.id} className="card-newspaper cursor-pointer relative" onClick={() => setSelectedPost(post)} data-testid={`post-card-${post.id}`}>
              {post.featured && (
                <div className="absolute top-0 left-0 bg-[#8B3A3A] text-[#F5F0E1] px-3 py-1 font-utility text-xs uppercase tracking-wide z-10">
                  ⭐ Featured
                </div>
              )}
              {post.image_url && <div className="aspect-video overflow-hidden"><img src={post.image_url} alt={post.title} className="w-full h-full object-cover img-newsprint" /></div>}
              <div className="p-4">
                <span className="badge-newspaper mb-2">{post.category}</span>
                <h3 className="headline-sm mt-2 mb-2">{post.title}</h3>
                <p className="font-body text-sm text-[#5C503D] line-clamp-3">{post.excerpt}</p>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#2C2416]/20">
                  <span className="font-mono text-xs text-[#5C503D]">{post.author}</span>
                  <span className="font-mono text-xs text-[#5C503D]">{new Date(post.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <p className="text-center font-body text-[#5C503D]">No posts yet. Check back soon for updates!</p>
      )}

      {selectedPost && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" style={{ zIndex: 9999 }}>
          <div className="bg-[#F5F0E1] border-2 border-[#2C2416] max-w-3xl w-full my-8 animate-slide-up">
            <div className="p-6 border-b-2 border-[#2C2416] flex justify-between items-start">
              <div>
                <span className="badge-newspaper mb-2">{selectedPost.category}</span>
                <h2 className="headline-lg mt-2">{selectedPost.title}</h2>
                <p className="font-mono text-sm text-[#5C503D] mt-2">By {selectedPost.author} • {new Date(selectedPost.created_at).toLocaleDateString()}</p>
              </div>
              <button onClick={() => setSelectedPost(null)} className="p-2 hover:bg-[#E8E2D2]"><X size={24} /></button>
            </div>
            {selectedPost.image_url && <img src={selectedPost.image_url} alt={selectedPost.title} className="w-full img-newsprint" />}
            <div className="p-6">
              <div 
                className="font-body text-lg leading-relaxed prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedPost.content) }}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// ============== SUBMIT DEAL MODAL ==============
const SubmitDealModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    title: '',
    business: '',
    description: '',
    original_price: '',
    deal_price: '',
    category: '',
    location: '',
    image_url: '',
    expires_at: '',
    redemption_instructions: '',
    featured: false
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Calculate savings percentage
  const savingsPercent = formData.original_price && formData.deal_price
    ? Math.round(((parseFloat(formData.original_price) - parseFloat(formData.deal_price)) / parseFloat(formData.original_price)) * 100)
    : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/deals/submit-request`, {
        ...formData,
        original_price: parseFloat(formData.original_price),
        deal_price: parseFloat(formData.deal_price)
      });
      setSubmitted(true);
    } catch (err) {
      alert('Failed to submit request. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <ModalPortal>
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
          <div className="bg-[#F5F0E1] border-4 border-[#3A5A40] max-w-md w-full animate-slide-up">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-[#3A5A40] rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-[#F5F0E1]" />
              </div>
              <h2 className="headline-lg text-[#3A5A40] mb-3">Request Submitted!</h2>
              <p className="font-body text-lg mb-4">
                Thank you for your interest in featuring your company on Vigilante Radio Montana!
              </p>
              <p className="font-body text-[#5C503D] mb-6">
                We've received your request and will contact you shortly to discuss featuring your deal on our platform.
              </p>
              <button onClick={onClose} className="btn-primary">
                Close
              </button>
            </div>
          </div>
        </div>
      </ModalPortal>
    );
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
        <div className="bg-[#F5F0E1] border-2 border-[#2C2416] max-w-2xl w-full max-h-[90vh] flex flex-col animate-slide-up">
          <div className="p-6 border-b-2 border-[#2C2416] flex justify-between items-center bg-[#F5F0E1]">
            <h2 className="headline-lg">Feature Your Company</h2>
            <button onClick={onClose} className="p-2 hover:bg-[#E8E2D2]" type="button">
              <X size={24} />
            </button>
          </div>
          
          <div className="overflow-y-auto flex-1">
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <p className="font-body text-[#5C503D] mb-4">
                Submit your deal to be featured on Vigilante Radio Montana. We'll review your submission and contact you to finalize the details.
              </p>

              <div className="border-b-2 border-[#2C2416] pb-4 mb-4">
                <h3 className="font-heading text-lg font-bold mb-3">Contact Information</h3>
                <FormField label="Your Name" value={formData.contact_name} onChange={(v) => handleChange('contact_name', v)} required placeholder="John Doe" />
                <FormField label="Email Address" type="email" value={formData.contact_email} onChange={(v) => handleChange('contact_email', v)} required placeholder="you@business.com" />
                <FormField label="Phone Number" type="tel" value={formData.contact_phone} onChange={(v) => handleChange('contact_phone', v)} required placeholder="(406) 555-1234" />
              </div>

              <div className="border-b-2 border-[#2C2416] pb-4 mb-4">
                <h3 className="font-heading text-lg font-bold mb-3">Deal Information</h3>
                <FormField label="Company/Business Name" value={formData.business} onChange={(v) => handleChange('business', v)} required placeholder="Your business name" />
                <FormField label="Deal Title" value={formData.title} onChange={(v) => handleChange('title', v)} required placeholder="e.g., 20% Off All Services" />
                <FormField label="Description" value={formData.description} onChange={(v) => handleChange('description', v)} multiline rows={3} placeholder="Describe your deal and what makes it special" />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Original Price" type="number" step="0.01" value={formData.original_price} onChange={(v) => handleChange('original_price', v)} required placeholder="80.00" />
                <FormField label="Deal Price" type="number" step="0.01" value={formData.deal_price} onChange={(v) => handleChange('deal_price', v)} required placeholder="40.00" />
              </div>
              
              {savingsPercent > 0 && (
                <div className="bg-[#3A5A40] text-[#F5F0E1] p-3 mt-3 text-center">
                  <p className="font-heading text-2xl font-bold">{savingsPercent}% OFF</p>
                  <p className="font-utility text-sm">
                    Customers save ${(parseFloat(formData.original_price || 0) - parseFloat(formData.deal_price || 0)).toFixed(2)}
                  </p>
                </div>
              )}
              
              <FormMultiSelect 
                label="Categories" 
                values={Array.isArray(formData.category) ? formData.category : (formData.category ? [formData.category] : [])} 
                options={CATEGORIES} 
                onChange={(v) => handleChange('category', v)} 
                required 
              />
              <FormMultiSelect 
                label="Locations" 
                values={Array.isArray(formData.location) ? formData.location : (formData.location ? [formData.location] : [])} 
                options={LOCATIONS} 
                onChange={(v) => handleChange('location', v)} 
                required 
              />
              <FormField 
                label="Redemption Instructions" 
                value={formData.redemption_instructions} 
                onChange={(v) => handleChange('redemption_instructions', v)} 
                multiline 
                rows={2}
                placeholder="e.g., Show voucher code to server. Valid for dine-in only." 
              />
              <FormImageUpload label="Deal Image (Optional)" currentImage={formData.image_url} onChange={(v) => handleChange('image_url', v)} />
              <FormField label="Deal Expiration Date (Optional)" type="date" value={formData.expires_at?.split('T')[0] || ''} onChange={(v) => handleChange('expires_at', v)} />

              <div className="border-t-2 border-[#2C2416] pt-4 mt-6">
                <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
                  {loading ? (<><Loader2 className="animate-spin" size={18} />Submitting...</>) : (<><Send size={18} />Submit Deal Request</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

// ============== SUBMIT NONPROFIT MODAL ==============
const SubmitNonprofitModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    name: '',
    mission: '',
    description: '',
    category: '',
    location: '',
    image_url: '',
    website: '',
    donation_url: '',
    featured: false
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/nonprofits/submit-request`, formData);
      setSubmitted(true);
    } catch (err) {
      alert('Failed to submit request. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <ModalPortal>
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
          <div className="bg-[#F5F0E1] border-4 border-[#3A5A40] max-w-md w-full animate-slide-up">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-[#3A5A40] rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-[#F5F0E1]" />
              </div>
              <h2 className="headline-lg text-[#3A5A40] mb-3">Request Submitted!</h2>
              <p className="font-body text-lg mb-4">
                Thank you for your interest in listing your nonprofit on Vigilante Radio Montana!
              </p>
              <p className="font-body text-[#5C503D] mb-6">
                We've received your request and will contact you shortly.
              </p>
              <button onClick={onClose} className="btn-primary">Close</button>
            </div>
          </div>
        </div>
      </ModalPortal>
    );
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
        <div className="bg-[#F5F0E1] border-2 border-[#2C2416] max-w-2xl w-full max-h-[90vh] flex flex-col animate-slide-up">
          <div className="p-6 border-b-2 border-[#2C2416] flex justify-between items-center bg-[#F5F0E1]">
            <h2 className="headline-lg">List Your Nonprofit</h2>
            <button onClick={onClose} className="p-2 hover:bg-[#E8E2D2]" type="button">
              <X size={24} />
            </button>
          </div>
          
          <div className="overflow-y-auto flex-1">
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <p className="font-body text-[#5C503D] mb-4">
                Submit your nonprofit to be featured on Vigilante Radio Montana.
              </p>

              <div className="border-b-2 border-[#2C2416] pb-4 mb-4">
                <h3 className="font-heading text-lg font-bold mb-3">Contact Information</h3>
                <FormField label="Your Name" value={formData.contact_name} onChange={(v) => handleChange('contact_name', v)} required placeholder="John Doe" />
                <FormField label="Email Address" type="email" value={formData.contact_email} onChange={(v) => handleChange('contact_email', v)} required placeholder="you@nonprofit.org" />
                <FormField label="Phone Number" type="tel" value={formData.contact_phone} onChange={(v) => handleChange('contact_phone', v)} required placeholder="(406) 555-1234" />
              </div>

              <div className="border-b-2 border-[#2C2416] pb-4 mb-4">
                <h3 className="font-heading text-lg font-bold mb-3">Nonprofit Information</h3>
                <FormField label="Organization Name" value={formData.name} onChange={(v) => handleChange('name', v)} required placeholder="Your Nonprofit Name" />
                <FormField label="Mission Statement" value={formData.mission} onChange={(v) => handleChange('mission', v)} required placeholder="Brief mission statement" />
                <FormField label="Description" value={formData.description} onChange={(v) => handleChange('description', v)} multiline rows={3} placeholder="Tell us about your organization" />
                <FormField label="Category" value={formData.category} onChange={(v) => handleChange('category', v)} placeholder="e.g., Healthcare, Environment" />
                <FormMultiSelect 
                  label="Locations" 
                  values={Array.isArray(formData.location) ? formData.location : (formData.location ? [formData.location] : [])} 
                  options={LOCATIONS} 
                  onChange={(v) => handleChange('location', v)} 
                />
                <FormImageUpload label="Organization Logo (Optional)" currentImage={formData.image_url} onChange={(v) => handleChange('image_url', v)} />
                <FormField label="Website" value={formData.website} onChange={(v) => handleChange('website', v)} placeholder="https://yournonprofit.org" />
                <FormField label="Donation URL (Optional)" value={formData.donation_url} onChange={(v) => handleChange('donation_url', v)} placeholder="https://donate.yournonprofit.org" />
              </div>

              <div className="border-t-2 border-[#2C2416] pt-4 mt-6">
                <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
                  {loading ? (<><Loader2 className="animate-spin" size={18} />Submitting...</>) : (<><Send size={18} />Submit Nonprofit Request</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

// ============== SUBMIT BAND MODAL ==============
const SubmitBandModal = ({ onClose }) => {
  const [formData, setFormData] = useState({
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    name: '',
    genre: '',
    bio: '',
    location: '',
    image_url: '',
    amazon_music_url: '',
    website: '',
    featured: false
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/bands/submit-request`, formData);
      setSubmitted(true);
    } catch (err) {
      alert('Failed to submit request. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <ModalPortal>
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
          <div className="bg-[#F5F0E1] border-4 border-[#3A5A40] max-w-md w-full animate-slide-up">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-[#3A5A40] rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-[#F5F0E1]" />
              </div>
              <h2 className="headline-lg text-[#3A5A40] mb-3">Request Submitted!</h2>
              <p className="font-body text-lg mb-4">
                Thank you for your interest in being featured on Vigilante Radio Montana!
              </p>
              <p className="font-body text-[#5C503D] mb-6">
                We've received your band submission and will contact you shortly.
              </p>
              <button onClick={onClose} className="btn-primary">Close</button>
            </div>
          </div>
        </div>
      </ModalPortal>
    );
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
        <div className="bg-[#F5F0E1] border-2 border-[#2C2416] max-w-2xl w-full max-h-[90vh] flex flex-col animate-slide-up">
          <div className="p-6 border-b-2 border-[#2C2416] flex justify-between items-center bg-[#F5F0E1]">
            <h2 className="headline-lg">Submit Your Band</h2>
            <button onClick={onClose} className="p-2 hover:bg-[#E8E2D2]" type="button">
              <X size={24} />
            </button>
          </div>
          
          <div className="overflow-y-auto flex-1">
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <p className="font-body text-[#5C503D] mb-4">
                Submit your band to be featured on Vigilante Radio Montana.
              </p>

              <div className="border-b-2 border-[#2C2416] pb-4 mb-4">
                <h3 className="font-heading text-lg font-bold mb-3">Contact Information</h3>
                <FormField label="Your Name" value={formData.contact_name} onChange={(v) => handleChange('contact_name', v)} required placeholder="John Doe" />
                <FormField label="Email Address" type="email" value={formData.contact_email} onChange={(v) => handleChange('contact_email', v)} required placeholder="you@band.com" />
                <FormField label="Phone Number" type="tel" value={formData.contact_phone} onChange={(v) => handleChange('contact_phone', v)} required placeholder="(406) 555-1234" />
              </div>

              <div className="border-b-2 border-[#2C2416] pb-4 mb-4">
                <h3 className="font-heading text-lg font-bold mb-3">Band Information</h3>
                <FormField label="Band Name" value={formData.name} onChange={(v) => handleChange('name', v)} required placeholder="Your Band Name" />
                <FormField label="Genre" value={formData.genre} onChange={(v) => handleChange('genre', v)} required placeholder="e.g., Bluegrass, Folk, Rock" />
                <FormField label="Bio" value={formData.bio} onChange={(v) => handleChange('bio', v)} multiline rows={3} required placeholder="Tell us about your band" />
                <FormField label="Location" type="select" value={formData.location} onChange={(v) => handleChange('location', v)} options={LOCATIONS} required />
                <FormImageUpload label="Band Photo (Optional)" currentImage={formData.image_url} onChange={(v) => handleChange('image_url', v)} />
                <FormField 
                  label="Amazon Music Page URL (Optional)" 
                  value={formData.amazon_music_url} 
                  onChange={(v) => handleChange('amazon_music_url', v)} 
                  placeholder="https://www.amazon.com/music/player/artists/..." 
                  helpText="Paste the full Amazon Music artist or album page URL where fans can purchase your music"
                />
                <FormField label="Website (Optional)" value={formData.website} onChange={(v) => handleChange('website', v)} placeholder="https://yourband.com" />
              </div>

              <div className="border-t-2 border-[#2C2416] pt-4 mt-6">
                <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2">
                  {loading ? (<><Loader2 className="animate-spin" size={18} />Submitting...</>) : (<><Send size={18} />Submit Band Request</>)}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

// ============== DEALS SECTION ==============
const DealsSection = () => {
  const [deals, setDeals] = useState([]);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showPurchase, setShowPurchase] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [showSubmitDeal, setShowSubmitDeal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [purchaseResult, setPurchaseResult] = useState(null);
  const [paymentSessionId, setPaymentSessionId] = useState(null);
  const [selectedCommunity, setSelectedCommunity] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');

  useEffect(() => { fetchDeals(); }, []);

  // Poll for payment completion when session ID is set
  useEffect(() => {
    if (!paymentSessionId) return;
    
    const pollPayment = async () => {
      try {
        const response = await axios.get(`${API}/purchases/status/${paymentSessionId}`);
        
        if (response.data.voucher_code) {
          // Payment complete and voucher generated
          setPurchaseResult(response.data);
          setProcessingPayment(false);
          setPaymentSessionId(null);
        } else if (response.data.payment_status === 'paid') {
          // Payment confirmed but voucher still processing
          setTimeout(pollPayment, 2000);
        } else {
          // Still processing
          setTimeout(pollPayment, 2000);
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
        setTimeout(pollPayment, 3000); // Retry on error
      }
    };
    
    pollPayment();
  }, [paymentSessionId]);

  const fetchDeals = async () => {
    try { 
      const res = await axios.get(`${API}/deals?approved_only=true`);
      setDeals(res.data); 
    } catch (err) { 
      console.error(err); 
    }
  };

  // Use standardized locations for community filter
  const communities = ['All', ...LOCATIONS];
  const categories = ['All', ...CATEGORIES];

  // Filter deals by selected community AND category
  const filteredDeals = deals.filter(deal => {
    // Check location match
    const locationMatch = selectedCommunity === 'All' || 
      (Array.isArray(deal.location) 
        ? deal.location.includes(selectedCommunity)
        : deal.location === selectedCommunity);
    
    // Check category match
    const categoryMatch = selectedCategory === 'All' || 
      (Array.isArray(deal.category) 
        ? deal.category.includes(selectedCategory)
        : deal.category === selectedCategory);
    
    return locationMatch && categoryMatch;
  });

  const handlePurchase = async () => {
    if (!customerEmail || !customerName || !selectedDeal) return;
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/deals/${selectedDeal.id}/create-checkout`, {
        deal_id: selectedDeal.id,
        customer_email: customerEmail,
        customer_name: customerName
      });
      
      // Open Stripe checkout in new tab
      window.open(response.data.url, '_blank');
      
      // Close purchase modal and show processing state
      setShowPurchase(false);
      setProcessingPayment(true);
      setPaymentSessionId(response.data.session_id);
      
      // Reset form
      setCustomerName('');
      setCustomerEmail('');
    } catch (err) {
      alert('Failed to create checkout. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const discount = (deal) => Math.round((1 - deal.deal_price / deal.original_price) * 100);

  return (
    <section data-testid="deals-section">
      <SectionHeader icon={Tag} overline="Support Local" title="EXCLUSIVE DEALS FOR OUR LISTENERS" subtitle="Every special offer purchased supports local business, local news and local music" id="deals" />
      
      {/* Location Filter Buttons */}
      <div className="mb-4">
        <h3 className="font-utility text-xs uppercase tracking-wide text-[#5C503D] mb-2 px-4 sm:px-0">Filter by Location</h3>
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-2 min-w-min px-4 sm:px-0 pb-2">
            {communities.map(community => (
              <button
                key={community}
                onClick={() => setSelectedCommunity(community)}
                className={`px-4 sm:px-6 py-2 sm:py-3 font-utility text-xs sm:text-sm uppercase tracking-wide transition-all border-2 border-[#2C2416] whitespace-nowrap flex-shrink-0 ${
                  selectedCommunity === community
                    ? 'bg-[#2C2416] text-[#F5F0E1] font-bold'
                    : 'bg-[#F5F0E1] text-[#2C2416] hover:bg-[#E8E2D2]'
                }`}
              >
                {community}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Category Filter Buttons */}
      <div className="mb-6">
        <h3 className="font-utility text-xs uppercase tracking-wide text-[#5C503D] mb-2 px-4 sm:px-0">Filter by Category</h3>
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-2 min-w-min px-4 sm:px-0 pb-2">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 sm:px-6 py-2 sm:py-3 font-utility text-xs sm:text-sm uppercase tracking-wide transition-all border-2 border-[#2C2416] whitespace-nowrap flex-shrink-0 ${
                  selectedCategory === category
                    ? 'bg-[#8B3A3A] text-[#F5F0E1] font-bold'
                    : 'bg-[#F5F0E1] text-[#2C2416] hover:bg-[#E8E2D2]'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDeals.map(deal => (
          <div key={deal.id} className="card-newspaper" data-testid={`deal-card-${deal.id}`}>
            <div className="relative">
              {deal.image_url && <div className="aspect-video overflow-hidden"><img src={deal.image_url} alt={deal.title} className="w-full h-full object-cover img-newsprint" /></div>}
              <div className="absolute top-2 right-2 bg-[#8B3A3A] text-[#F5F0E1] px-3 py-1 font-utility font-bold">{discount(deal)}% OFF</div>
              {deal.featured && <div className="absolute top-2 left-2 bg-[#3A5A40] text-[#F5F0E1] px-2 py-1 font-mono text-xs uppercase">Featured</div>}
            </div>
            <div className="p-4">
              <div className="flex flex-wrap gap-1 mb-2">
                {(Array.isArray(deal.category) ? deal.category : [deal.category]).filter(Boolean).map((cat) => (
                  <span key={cat} className="badge-newspaper">{cat}</span>
                ))}
              </div>
              <h3 className="headline-sm mt-2">{deal.title}</h3>
              <p className="font-utility text-sm text-[#8B3A3A] font-semibold">{deal.business}</p>
              <p className="font-body text-sm text-[#5C503D] mt-2 line-clamp-2">{deal.description}</p>
              <div className="flex items-center gap-3 mt-4">
                <span className="font-heading text-2xl font-bold">${deal.deal_price.toFixed(2)}</span>
                <span className="font-body text-lg line-through text-[#5C503D]">${deal.original_price.toFixed(2)}</span>
              </div>
              <div className="flex items-center gap-2 mt-2 text-[#5C503D] flex-wrap">
                <MapPin size={14} />
                <span className="font-utility text-sm">
                  {Array.isArray(deal.location) ? deal.location.join(', ') : deal.location}
                </span>
              </div>
              {deal.quantity_available !== null && deal.quantity_available !== undefined && (
                <div className={`mt-2 text-xs font-utility uppercase ${deal.quantity_available <= 5 ? 'text-[#8B3A3A] font-bold' : 'text-[#5C503D]'}`}>
                  {deal.quantity_available === 0 ? '❌ Sold Out' : `⚡ Only ${deal.quantity_available} ${deal.quantity_available === 1 ? 'left' : 'remaining'}!`}
                </div>
              )}
              <button 
                onClick={() => { setSelectedDeal(deal); setShowPurchase(true); }} 
                className="btn-primary w-full mt-4 flex items-center justify-center gap-2" 
                data-testid={`buy-deal-${deal.id}`}
                disabled={deal.quantity_available === 0}
              >
                <DollarSign size={18} /> {deal.quantity_available === 0 ? 'Sold Out' : `Buy Now - $${deal.deal_price.toFixed(2)}`}
              </button>
            </div>
          </div>
        ))}
      </div>
      
      {/* No deals message */}
      {filteredDeals.length === 0 && (
        <div className="text-center py-12 bg-[#F5F0E1] border-2 border-[#2C2416] mt-6">
          <Tag size={48} className="mx-auto mb-4 text-[#5C503D]" />
          <h3 className="headline-sm mb-2">No Deals Available</h3>
          <p className="font-utility text-[#5C503D]">
            {selectedCommunity === 'All' 
              ? 'Check back soon for new deals!' 
              : `No deals available in ${selectedCommunity} at the moment.`}
          </p>
          {selectedCommunity !== 'All' && (
            <button
              onClick={() => setSelectedCommunity('All')}
              className="mt-4 btn-secondary"
            >
              View All Deals
            </button>
          )}
        </div>
      )}
      
      {/* Purchase Modal */}
      {showPurchase && selectedDeal && (
        <ModalPortal>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
            <div className="bg-[#F5F0E1] border-2 border-[#2C2416] max-w-md w-full animate-slide-up">
              <div className="p-6 border-b-2 border-[#2C2416] flex justify-between items-start">
                <div>
                  <h3 className="headline-lg">Purchase Deal</h3>
                  <p className="font-utility text-sm text-[#5C503D] mt-1">You'll be redirected to secure checkout</p>
                </div>
                <button onClick={() => setShowPurchase(false)} className="p-2 hover:bg-[#E8E2D2]"><X size={24} /></button>
              </div>
              
              <div className="p-6">
                <div className="bg-[#E8E2D2] p-4 border-l-4 border-[#8B3A3A] mb-6">
                  <h4 className="font-heading text-lg font-bold">{selectedDeal.title}</h4>
                  <p className="font-utility text-sm text-[#5C503D]">{selectedDeal.business}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="font-heading text-2xl font-bold text-[#8B3A3A]">${selectedDeal.deal_price.toFixed(2)}</span>
                    <span className="font-body line-through text-[#5C503D]">${selectedDeal.original_price.toFixed(2)}</span>
                    <span className="badge-newspaper">{discount(selectedDeal)}% OFF</span>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="font-utility text-sm uppercase tracking-wide mb-2 block">Your Name</label>
                    <input 
                      type="text" 
                      value={customerName} 
                      onChange={(e) => setCustomerName(e.target.value)} 
                      className="input-newspaper w-full" 
                      placeholder="John Doe" 
                      data-testid="customer-name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="font-utility text-sm uppercase tracking-wide mb-2 block">Your Email</label>
                    <input 
                      type="email" 
                      value={customerEmail} 
                      onChange={(e) => setCustomerEmail(e.target.value)} 
                      className="input-newspaper w-full" 
                      placeholder="you@email.com" 
                      data-testid="customer-email"
                      required
                    />
                    <p className="font-utility text-xs text-[#5C503D] mt-1">We'll email your voucher here</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 border-t-2 border-[#2C2416] flex gap-3">
                <button onClick={() => setShowPurchase(false)} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button 
                  onClick={handlePurchase} 
                  disabled={loading || !customerEmail || !customerName} 
                  className="btn-primary flex-1 flex items-center justify-center gap-2" 
                  data-testid="confirm-purchase"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <DollarSign size={18} />}
                  {loading ? 'Processing...' : 'Proceed to Checkout'}
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
      
      {/* Processing Payment Overlay */}
      {processingPayment && (
        <ModalPortal>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
            <div className="bg-[#F5F0E1] border-2 border-[#2C2416] max-w-md w-full p-8 text-center animate-slide-up">
              <Loader2 className="animate-spin w-16 h-16 text-[#8B3A3A] mx-auto mb-4" />
              <h3 className="headline-lg mb-2">Processing Your Payment</h3>
              <p className="font-body text-[#5C503D]">Please complete your payment in the new tab.</p>
              <p className="font-utility text-sm text-[#5C503D] mt-4">Your voucher will appear here automatically once payment is confirmed.</p>
              <button 
                onClick={() => {
                  setProcessingPayment(false);
                  setPaymentSessionId(null);
                }}
                className="btn-secondary mt-6"
              >
                Cancel
              </button>
            </div>
          </div>
        </ModalPortal>
      )}
      
      {/* Voucher Display Modal */}
      {purchaseResult && (
        <ModalPortal>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
            <div className="bg-[#F5F0E1] border-2 border-[#2C2416] max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slide-up">
              <div className="p-6 border-b-2 border-[#2C2416] flex justify-between items-start bg-[#3A5A40] text-[#F5F0E1]">
                <div>
                  <h3 className="headline-lg">Payment Successful!</h3>
                  <p className="font-utility text-sm mt-1">Your voucher is ready</p>
                </div>
                <button onClick={() => setPurchaseResult(null)} className="p-2 hover:bg-[#2C5A30]"><X size={24} /></button>
              </div>
              
              <div className="p-6">
                <div className="bg-white border-4 border-dashed border-[#8B3A3A] p-6 text-center mb-6">
                  <p className="font-utility text-xs uppercase tracking-wide text-[#5C503D] mb-2">Voucher Code</p>
                  <h1 className="font-heading text-4xl font-bold text-[#2C2416] tracking-widest mb-4">{purchaseResult.voucher_code}</h1>
                  {purchaseResult.qr_code_data && (
                    <img src={purchaseResult.qr_code_data} alt="QR Code" className="mx-auto w-48 h-48 mb-2" />
                  )}
                </div>
                
                <div className="bg-[#E8E2D2] p-4 mb-4">
                  <h4 className="font-heading text-lg font-bold mb-2">{purchaseResult.deal_title}</h4>
                  <p className="font-utility text-sm text-[#5C503D] mb-2">{purchaseResult.business_name}</p>
                  <div className="flex items-center gap-3">
                    <span className="font-heading text-2xl font-bold text-[#8B3A3A]">${purchaseResult.amount_paid.toFixed(2)}</span>
                    <span className="font-body line-through text-[#5C503D]">${purchaseResult.original_price.toFixed(2)}</span>
                    <span className="badge-newspaper">You saved ${(purchaseResult.original_price - purchaseResult.amount_paid).toFixed(2)}</span>
                  </div>
                </div>
                
                <div className="bg-white border-l-4 border-[#3A5A40] p-4 mb-4">
                  <h5 className="font-utility text-sm uppercase tracking-wide font-bold mb-2">Redemption Instructions</h5>
                  <p className="font-body text-sm text-[#5C503D]">{purchaseResult.redemption_instructions}</p>
                  <p className="font-utility text-xs text-[#5C503D] mt-2">
                    <strong>Expires:</strong> {new Date(purchaseResult.expiration_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
                
                <div className="bg-[#D4EDD9] border-l-4 border-[#3A5A40] p-4">
                  <p className="font-utility text-sm flex items-start gap-2">
                    <CheckCircle size={18} className="text-[#3A5A40] flex-shrink-0 mt-0.5" />
                    <span>A copy of this voucher has been emailed to <strong>{purchaseResult.customer_email}</strong></span>
                  </p>
                </div>
              </div>
              
              <div className="p-6 border-t-2 border-[#2C2416]">
                <button onClick={() => setPurchaseResult(null)} className="btn-primary w-full">
                  Close
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
      
      {/* Submit Deal Button */}
      <div className="mt-12 text-center">
        <button 
          onClick={() => setShowSubmitDeal(true)}
          className="btn-primary flex items-center gap-2 mx-auto"
          data-testid="submit-deal-btn"
        >
          <Plus size={18} /> Feature your company here
        </button>
      </div>

      {showSubmitDeal && <SubmitDealModal onClose={() => setShowSubmitDeal(false)} />}
    </section>
  );
};

// ============== EVENTS SECTION ==============
const EventsSection = () => {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState('list'); // 'calendar' or 'list'
  const [selectedCommunity, setSelectedCommunity] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showSubmitEvent, setShowSubmitEvent] = useState(false);
  // RSVP/reminder modal state. `null` = closed, otherwise the event being subscribed to.
  const [reminderEvent, setReminderEvent] = useState(null);
  const [reminderEmail, setReminderEmail] = useState('');
  const [reminderName, setReminderName] = useState('');
  const [reminderNewsletter, setReminderNewsletter] = useState(false);
  const [reminderStatus, setReminderStatus] = useState(null); // 'submitting' | 'success' | 'already' | 'error'
  const [reminderError, setReminderError] = useState(null);
  const [reminderNewsletterAdded, setReminderNewsletterAdded] = useState(false);

  const openReminderModal = (event) => {
    setReminderEvent(event);
    setReminderEmail('');
    setReminderName('');
    setReminderNewsletter(false);
    setReminderStatus(null);
    setReminderError(null);
    setReminderNewsletterAdded(false);
  };

  const submitReminder = async (e) => {
    e?.preventDefault();
    if (!reminderEvent) return;
    if (!reminderEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reminderEmail)) {
      setReminderError('Please enter a valid email address.');
      return;
    }
    setReminderStatus('submitting');
    setReminderError(null);
    try {
      const res = await axios.post(`${API}/events/${reminderEvent.id}/remind`, {
        email: reminderEmail.trim(),
        name: reminderName.trim(),
        newsletter_opt_in: reminderNewsletter,
      });
      setReminderNewsletterAdded(!!res.data.newsletter_added);
      setReminderStatus(res.data.status === 'already_subscribed' ? 'already' : 'success');
    } catch (err) {
      console.error('Reminder subscription failed:', err);
      setReminderError(err.response?.data?.detail || 'Could not save your reminder. Please try again.');
      setReminderStatus('error');
    }
  };

  // Parse a backend date string as a LOCAL date (not UTC).
  // `new Date("2026-04-18")` is parsed as UTC midnight per the ISO 8601 spec,
  // which shifts to the previous day for any negative-offset timezone (e.g. MT).
  // We construct the Date from its Y/M/D components so the calendar lines up
  // with what the admin entered, regardless of viewer timezone.
  const parseEventDate = (dateStr) => {
    if (!dateStr) return null;
    const ymd = String(dateStr).slice(0, 10).split('-');
    if (ymd.length !== 3) return new Date(dateStr);
    const [y, m, d] = ymd.map(Number);
    if (!y || !m || !d) return new Date(dateStr);
    return new Date(y, m - 1, d);
  };

  // ---- Calendar export helpers ---------------------------------------------
  // Parse a free-text time like "7:00 PM" / "07:00 PM" / "19:00" → {h, m}.
  // Returns null if parsing fails (caller should treat as all-day in that case).
  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    const s = String(timeStr).trim();
    const m12 = s.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])$/);
    if (m12) {
      let h = parseInt(m12[1], 10) % 12;
      if (m12[3].toUpperCase() === 'PM') h += 12;
      return { h, m: parseInt(m12[2], 10) };
    }
    const m24 = s.match(/^(\d{1,2}):(\d{2})$/);
    if (m24) {
      return { h: parseInt(m24[1], 10), m: parseInt(m24[2], 10) };
    }
    return null;
  };

  // Build start/end JS Date objects for the event in the viewer's local timezone.
  // If no time is given (all_day), end is the day AFTER (Google + iCal use
  // exclusive end for all-day events).
  const buildEventDates = (event) => {
    const start = parseEventDate(event.date);
    if (!start) return null;
    const endBase = parseEventDate(event.end_date) || start;
    const startTime = !event.all_day ? parseTime(event.time) : null;
    const endTime = !event.all_day ? parseTime(event.end_time) : null;

    if (startTime) {
      start.setHours(startTime.h, startTime.m, 0, 0);
    }
    let end = new Date(endBase);
    if (endTime) {
      end.setHours(endTime.h, endTime.m, 0, 0);
    } else if (startTime) {
      // Default duration: 1 hour after start
      end = new Date(start.getTime() + 60 * 60 * 1000);
    } else {
      // All-day: end is the next day (exclusive)
      end.setDate(end.getDate() + 1);
    }
    return { start, end, allDay: !!event.all_day || !startTime };
  };

  // Format Date as YYYYMMDDTHHMMSSZ (UTC) for Google Calendar / iCal
  const fmtUTC = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return (
      d.getUTCFullYear().toString() +
      pad(d.getUTCMonth() + 1) +
      pad(d.getUTCDate()) +
      'T' +
      pad(d.getUTCHours()) +
      pad(d.getUTCMinutes()) +
      pad(d.getUTCSeconds()) +
      'Z'
    );
  };

  // Format Date as YYYYMMDD (date-only, for all-day events)
  const fmtDate = (d) => {
    const pad = (n) => String(n).padStart(2, '0');
    return d.getFullYear().toString() + pad(d.getMonth() + 1) + pad(d.getDate());
  };

  const buildGoogleCalendarUrl = (event) => {
    const dates = buildEventDates(event);
    if (!dates) return null;
    const range = dates.allDay
      ? `${fmtDate(dates.start)}/${fmtDate(dates.end)}`
      : `${fmtUTC(dates.start)}/${fmtUTC(dates.end)}`;
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: event.title || 'Event',
      dates: range,
      details: event.description || '',
      location: event.location || '',
    });
    return `https://www.google.com/calendar/render?${params.toString()}`;
  };

  // Escape per RFC 5545: backslashes, commas, semicolons, newlines.
  const icsEscape = (s) =>
    String(s || '')
      .replace(/\\/g, '\\\\')
      .replace(/\n/g, '\\n')
      .replace(/,/g, '\\,')
      .replace(/;/g, '\\;');

  const downloadIcsFile = (event) => {
    const dates = buildEventDates(event);
    if (!dates) return;
    const uid = `vrm-${event.id || Math.random().toString(36).slice(2)}@vigilanteradiomt.com`;
    const now = fmtUTC(new Date());
    const dtStart = dates.allDay
      ? `DTSTART;VALUE=DATE:${fmtDate(dates.start)}`
      : `DTSTART:${fmtUTC(dates.start)}`;
    const dtEnd = dates.allDay
      ? `DTEND;VALUE=DATE:${fmtDate(dates.end)}`
      : `DTEND:${fmtUTC(dates.end)}`;

    const lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Vigilante Radio Montana//Events//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${now}`,
      dtStart,
      dtEnd,
      `SUMMARY:${icsEscape(event.title)}`,
      `DESCRIPTION:${icsEscape(event.description)}`,
      `LOCATION:${icsEscape(event.location)}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ];
    const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeTitle = (event.title || 'event').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'event';
    a.download = `${safeTitle}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  // --------------------------------------------------------------------------

  useEffect(() => { fetchEvents(); }, []);

  const fetchEvents = async () => {
    try { const res = await axios.get(`${API}/events`); setEvents(res.data); } catch (err) { console.error(err); }
  };

  // Use standardized locations and categories for filtering
  const communities = ['All', ...LOCATIONS];
  const categories = ['All', ...EVENT_CATEGORIES];

  // Filter events by selected community AND category
  const filteredEvents = events.filter(event => {
    // Check location match
    const locationMatch = selectedCommunity === 'All' || event.location === selectedCommunity;
    
    // Check category match
    const categoryMatch = selectedCategory === 'All' || event.category === selectedCategory;
    
    return locationMatch && categoryMatch;
  });

  // Get all dates in current month
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Add all days in month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    return filteredEvents.filter(event => {
      const eventDate = parseEventDate(event.date);
      if (!eventDate) return false;
      return eventDate.getDate() === date.getDate() &&
             eventDate.getMonth() === date.getMonth() &&
             eventDate.getFullYear() === date.getFullYear();
    });
  };

  const changeMonth = (delta) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + delta, 1));
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const days = getDaysInMonth();
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Get upcoming events (sorted by date)
  const upcomingEvents = [...filteredEvents]
    .filter(e => {
      const ed = parseEventDate(e.date);
      if (!ed) return false;
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      return ed >= todayStart;
    })
    .sort((a, b) => parseEventDate(a.date) - parseEventDate(b.date))
    .slice(0, 10);

  return (
    <section data-testid="events-section">
      <SectionHeader icon={Calendar} overline="Get out into your community" title="EVENTS CALENDAR" id="events" />
      
      {/* Location Filter Buttons */}
      <div className="mb-4">
        <h3 className="font-utility text-xs uppercase tracking-wide text-[#5C503D] mb-2 px-4 sm:px-0">Filter by Location</h3>
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-2 min-w-min px-4 sm:px-0 pb-2">
            {communities.map(community => (
              <button
                key={community}
                onClick={() => setSelectedCommunity(community)}
                className={`px-4 sm:px-6 py-2 sm:py-3 font-utility text-xs sm:text-sm uppercase tracking-wide transition-all border-2 border-[#2C2416] whitespace-nowrap flex-shrink-0 ${
                  selectedCommunity === community
                    ? 'bg-[#2C2416] text-[#F5F0E1] font-bold'
                    : 'bg-[#F5F0E1] text-[#2C2416] hover:bg-[#E8E2D2]'
                }`}
              >
                {community}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Category Filter Buttons */}
      <div className="mb-6">
        <h3 className="font-utility text-xs uppercase tracking-wide text-[#5C503D] mb-2 px-4 sm:px-0">Filter by Category</h3>
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-2 min-w-min px-4 sm:px-0 pb-2">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 sm:px-6 py-2 sm:py-3 font-utility text-xs sm:text-sm uppercase tracking-wide transition-all border-2 border-[#2C2416] whitespace-nowrap flex-shrink-0 ${
                  selectedCategory === category
                    ? 'bg-[#3A5A40] text-[#F5F0E1] font-bold'
                    : 'bg-[#F5F0E1] text-[#2C2416] hover:bg-[#E8E2D2]'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* View Toggle */}
      <div className="flex justify-center gap-2 mb-6">
        <button 
          onClick={() => setViewMode('calendar')}
          className={`px-3 sm:px-4 py-2 font-utility text-xs sm:text-sm uppercase tracking-wide transition-colors ${
            viewMode === 'calendar' ? 'bg-[#2C2416] text-[#F5F0E1]' : 'bg-[#E8E2D2] text-[#2C2416] hover:bg-[#D8D2C2]'
          }`}
        >
          <span className="hidden sm:inline">Calendar View</span>
          <span className="sm:hidden">Calendar</span>
        </button>
        <button 
          onClick={() => setViewMode('list')}
          className={`px-3 sm:px-4 py-2 font-utility text-xs sm:text-sm uppercase tracking-wide transition-colors ${
            viewMode === 'list' ? 'bg-[#2C2416] text-[#F5F0E1]' : 'bg-[#E8E2D2] text-[#2C2416] hover:bg-[#D8D2C2]'
          }`}
        >
          <span className="hidden sm:inline">List View</span>
          <span className="sm:hidden">List</span>
        </button>
      </div>

      {viewMode === 'calendar' ? (
        <div className="max-w-6xl mx-auto">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-4 sm:mb-6 border-b-2 border-[#2C2416] pb-3 sm:pb-4">
            <button onClick={() => changeMonth(-1)} className="p-1.5 sm:p-2 hover:bg-[#E8E2D2] transition-colors">
              <ChevronLeft size={20} className="sm:w-6 sm:h-6" />
            </button>
            <h3 className="text-lg sm:text-2xl font-heading font-bold">{monthName}</h3>
            <button onClick={() => changeMonth(1)} className="p-1.5 sm:p-2 hover:bg-[#E8E2D2] transition-colors">
              <ChevronRight size={20} className="sm:w-6 sm:h-6" />
            </button>
          </div>

          {/* Day Names */}
          <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-center font-utility text-xs sm:text-sm uppercase tracking-wide text-[#5C503D] py-2">
                <span className="hidden sm:inline">{day}</span>
                <span className="sm:hidden">{day.charAt(0)}</span>
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 sm:gap-2">
            {days.map((date, index) => {
              const dayEvents = date ? getEventsForDate(date) : [];
              const isTodayDate = isToday(date);
              const cellKey = date ? `day-${date.toISOString()}` : `empty-${index}`;
              
              return (
                <div
                  key={cellKey}
                  className={`min-h-[60px] sm:min-h-[100px] border-2 border-[#2C2416] p-1 sm:p-2 ${
                    date ? 'bg-[#F5F0E1] hover:bg-[#E8E2D2] cursor-pointer' : 'bg-[#D8D2C2]'
                  } ${isTodayDate ? 'ring-2 ring-[#8B3A3A]' : ''} transition-colors`}
                >
                  {date && (
                    <>
                      <div className={`font-heading text-sm sm:text-lg ${isTodayDate ? 'text-[#8B3A3A] font-bold' : 'text-[#2C2416]'}`}>
                        {date.getDate()}
                      </div>
                      <div className="mt-1 space-y-1 hidden sm:block">
                        {dayEvents.map(event => (
                          <div
                            key={event.id}
                            onClick={() => setSelectedEvent(event)}
                            className="text-xs font-utility bg-[#2C2416] text-[#F5F0E1] p-1 rounded truncate hover:bg-[#8B3A3A] transition-colors"
                          >
                            {event.time} {event.title}
                          </div>
                        ))}
                      </div>
                      {/* Mobile: Just show dot indicator */}
                      {dayEvents.length > 0 && (
                        <div className="sm:hidden flex justify-center mt-1">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#8B3A3A]"></div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* List View */
        <div className="max-w-4xl mx-auto space-y-4">
          <h3 className="headline-sm mb-4">Upcoming Events</h3>
          {upcomingEvents.length === 0 ? (
            <div className="text-center py-12 bg-[#F5F0E1] border-2 border-[#2C2416]">
              <Calendar size={48} className="mx-auto mb-4 text-[#5C503D]" />
              <h3 className="headline-sm mb-2">No Events Found</h3>
              <p className="font-utility text-[#5C503D]">
                {selectedCommunity === 'All' 
                  ? 'Check back soon for upcoming events!' 
                  : `No events available in ${selectedCommunity} at the moment.`}
              </p>
              {selectedCommunity !== 'All' && (
                <button
                  onClick={() => setSelectedCommunity('All')}
                  className="mt-4 btn-secondary"
                >
                  View All Events
                </button>
              )}
            </div>
          ) : (
            upcomingEvents.map(event => (
              <div
                key={event.id}
                onClick={() => setSelectedEvent(event)}
                className="border-2 border-[#2C2416] hover:bg-[#E8E2D2] cursor-pointer transition-colors overflow-hidden"
                data-testid={`event-card-${event.id}`}
              >
                <div className="flex flex-col sm:flex-row">
                  {/* Event Image */}
                  {event.image_url && (
                    <div className="w-full sm:w-48 h-48 sm:h-48 flex-shrink-0 bg-[#E8E2D2] overflow-hidden">
                      <img 
                        src={event.image_url} 
                        alt={event.title} 
                        className="w-full h-full object-cover img-newsprint"
                      />
                    </div>
                  )}
                  
                  {/* Event Details */}
                  <div className="flex-1 p-4 sm:p-4 flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="badge-newspaper text-xs">{event.category}</span>
                        {event.featured && <Star className="w-4 h-4 text-[#8B3A3A] fill-[#8B3A3A]" />}
                        {event.repeat_type && event.repeat_type !== 'none' && (
                          <span className="badge-newspaper bg-[#3A5A40] text-[#F5F0E1] text-xs">
                            <Repeat size={12} className="inline mr-1" />
                            Repeats {event.repeat_type}
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg sm:text-xl font-heading font-bold mb-3 leading-tight">{event.title}</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 text-[#5C503D] text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar size={16} className="flex-shrink-0" />
                          <span className="font-utility truncate">{parseEventDate(event.date)?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock size={16} className="flex-shrink-0" />
                          <span className="font-utility">{event.all_day ? 'All day' : event.time}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin size={16} className="flex-shrink-0" />
                          <span className="font-utility truncate">{event.location}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <User size={16} className="flex-shrink-0" />
                          <span className="font-utility truncate">{event.organizer}</span>
                        </div>
                      </div>
                    </div>
                    <ChevronRight className="text-[#5C503D] flex-shrink-0 ml-2 sm:ml-4 hidden sm:block" size={20} />
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Event Detail Modal */}
      {selectedEvent && (
        <ModalPortal>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" style={{ zIndex: 9999 }}>
            <div className="bg-[#F5F0E1] border-2 border-[#2C2416] max-w-xl w-full my-8 animate-slide-up max-h-[85vh] flex flex-col">
              <div className="p-4 border-b-2 border-[#2C2416] flex justify-between items-start flex-shrink-0">
                <div className="flex-1 pr-2">
                  <span className="badge-newspaper mb-2">{selectedEvent.category}</span>
                  {selectedEvent.repeat_type && selectedEvent.repeat_type !== 'none' && (
                    <span className="badge-newspaper bg-[#3A5A40] text-[#F5F0E1] ml-2">
                      <Repeat size={12} className="inline mr-1" />
                      Repeats {selectedEvent.repeat_type}
                    </span>
                  )}
                  <h2 className="headline-md mt-2">{selectedEvent.title}</h2>
                  <p className="font-utility text-sm text-[#5C503D] mt-1">Organized by {selectedEvent.organizer}</p>
                </div>
                <button onClick={() => setSelectedEvent(null)} className="p-2 hover:bg-[#E8E2D2] rounded flex-shrink-0 -mt-1 -mr-1"><X size={24} /></button>
              </div>
              
              <div className="overflow-y-auto flex-1">
                {selectedEvent.image_url && (
                  <div className="w-full max-h-72 overflow-hidden bg-[#E8E2D2]">
                    <img src={selectedEvent.image_url} alt={selectedEvent.title} className="w-full h-full object-cover img-newsprint" />
                  </div>
                )}
                
                <div className="p-4">
                  <h3 className="font-heading text-lg font-bold mb-3">Event Details</h3>
                  
                  <div className="bg-[#E8E2D2] p-3 border border-[#2C2416]/20 mb-4">
                    <div className="grid grid-cols-1 gap-3">
                      <div className="flex items-start gap-3">
                        <Calendar className="text-[#8B3A3A] flex-shrink-0 mt-1" size={18} />
                        <div>
                          <p className="font-utility text-xs uppercase text-[#5C503D]">Date</p>
                          <p className="font-body">{parseEventDate(selectedEvent.date)?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
                          {selectedEvent.end_date && selectedEvent.end_date !== selectedEvent.date && (
                            <p className="font-body text-sm text-[#5C503D] mt-1">
                              to {parseEventDate(selectedEvent.end_date)?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <Clock className="text-[#8B3A3A] flex-shrink-0 mt-1" size={18} />
                        <div>
                          <p className="font-utility text-xs uppercase text-[#5C503D]">Time</p>
                          <p className="font-body">
                            {selectedEvent.all_day ? 'All day' : selectedEvent.time}
                            {selectedEvent.end_time && !selectedEvent.all_day && ` - ${selectedEvent.end_time}`}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3">
                        <MapPin className="text-[#8B3A3A] flex-shrink-0 mt-1" size={18} />
                        <div>
                          <p className="font-utility text-xs uppercase text-[#5C503D]">Location</p>
                          <p className="font-body">{selectedEvent.location}</p>
                        </div>
                      </div>
                      
                      {selectedEvent.repeat_type && selectedEvent.repeat_type !== 'none' && (
                        <div className="flex items-start gap-3">
                          <Repeat className="text-[#8B3A3A] flex-shrink-0 mt-1" size={18} />
                          <div>
                            <p className="font-utility text-xs uppercase text-[#5C503D]">Repeats</p>
                            <p className="font-body capitalize">
                              {selectedEvent.repeat_type}
                              {selectedEvent.repeat_interval > 1 && ` (every ${selectedEvent.repeat_interval} ${selectedEvent.repeat_type}s)`}
                              {selectedEvent.repeat_days && ` on ${selectedEvent.repeat_days.split(',').join(', ')}`}
                            </p>
                            {selectedEvent.repeat_end_type !== 'never' && (
                              <p className="font-body text-sm text-[#5C503D] mt-1">
                                {selectedEvent.repeat_end_type === 'on_date' && `Until ${selectedEvent.repeat_end_date}`}
                                {selectedEvent.repeat_end_type === 'after_count' && `${selectedEvent.repeat_end_count} occurrences`}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h4 className="font-heading text-lg font-bold mb-2">About This Event</h4>
                    <p className="font-body leading-relaxed">{selectedEvent.description}</p>
                  </div>

                  {/* Add to Calendar */}
                  <div className="mb-4" data-testid="add-to-calendar">
                    <h4 className="font-heading text-lg font-bold mb-2">Add to Calendar</h4>
                    <div className="flex flex-wrap gap-2">
                      <a
                        href={buildGoogleCalendarUrl(selectedEvent) || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="add-to-google-calendar"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-[#3A5A40] hover:bg-[#2E4732] text-[#F5F0E1] font-utility text-sm uppercase tracking-wide rounded transition-colors"
                      >
                        <Calendar size={16} />
                        Google Calendar
                      </a>
                      <button
                        type="button"
                        onClick={() => downloadIcsFile(selectedEvent)}
                        data-testid="add-to-apple-calendar"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-[#2C2416] hover:bg-[#3D3220] text-[#F5F0E1] font-utility text-sm uppercase tracking-wide rounded transition-colors"
                      >
                        <Calendar size={16} />
                        Apple / Outlook (.ics)
                      </button>
                      <button
                        type="button"
                        onClick={() => openReminderModal(selectedEvent)}
                        data-testid="remind-me-btn"
                        className="inline-flex items-center gap-2 px-3 py-2 bg-[#8B3A3A] hover:bg-[#6B2A2A] text-[#F5F0E1] font-utility text-sm uppercase tracking-wide rounded transition-colors"
                      >
                        <Bell size={16} />
                        Remind Me
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
      
      {/* Submit Event Button */}
      <div className="mt-12 text-center">
        <button 
          onClick={() => setShowSubmitEvent(true)}
          className="btn-primary flex items-center gap-2 mx-auto"
          data-testid="submit-event-btn"
        >
          <Plus size={18} /> Submit your event here
        </button>
      </div>

      {/* Submit Event Modal */}
      {showSubmitEvent && <SubmitEventModal onClose={() => setShowSubmitEvent(false)} />}

      {/* Event Reminder Modal */}
      {reminderEvent && (
        <ModalPortal>
          <div
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.6)', zIndex: 10000 }}
            onClick={() => setReminderEvent(null)}
            data-testid="reminder-modal-backdrop"
          >
            <div
              className="bg-[#F5F0E1] border-4 border-[#2C2416] max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
              data-testid="reminder-modal"
            >
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <p className="font-utility text-xs uppercase tracking-wider text-[#5C503D]">Remind me about</p>
                  <h3 className="font-heading text-xl">{reminderEvent.title}</h3>
                </div>
                <button
                  onClick={() => setReminderEvent(null)}
                  className="p-1 hover:bg-[#E8E2D2] rounded -mt-1 -mr-1"
                  data-testid="reminder-modal-close"
                >
                  <X size={20} />
                </button>
              </div>

              {reminderStatus === 'success' || reminderStatus === 'already' ? (
                <div className="text-center py-4" data-testid="reminder-success">
                  <CheckCircle className="mx-auto mb-3 text-[#3A5A40]" size={48} />
                  <p className="font-body mb-2">
                    {reminderStatus === 'already'
                      ? "You're already on the reminder list for this event."
                      : "Got it! We'll email you 24 hours before the event."}
                  </p>
                  {reminderNewsletterAdded && (
                    <p className="font-body text-sm text-[#3A5A40]" data-testid="reminder-newsletter-confirm">
                      You're also subscribed to the Vigilante Radio newsletter. Welcome aboard!
                    </p>
                  )}
                  <button
                    onClick={() => setReminderEvent(null)}
                    className="btn-primary mt-4"
                    data-testid="reminder-close-btn"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={submitReminder} className="space-y-4" data-testid="reminder-form">
                  <p className="font-body text-sm text-[#5C503D]">
                    We'll send you a quick email reminder 24 hours before this event starts.
                  </p>
                  <div>
                    <label className="block font-utility text-xs uppercase tracking-wider mb-1">Your Name (optional)</label>
                    <input
                      type="text"
                      value={reminderName}
                      onChange={(e) => setReminderName(e.target.value)}
                      className="w-full border-2 border-[#2C2416] bg-transparent p-2 font-body"
                      placeholder="First name"
                      data-testid="reminder-name-input"
                    />
                  </div>
                  <div>
                    <label className="block font-utility text-xs uppercase tracking-wider mb-1">Email Address *</label>
                    <input
                      type="email"
                      value={reminderEmail}
                      onChange={(e) => setReminderEmail(e.target.value)}
                      required
                      className="w-full border-2 border-[#2C2416] bg-transparent p-2 font-body"
                      placeholder="you@example.com"
                      data-testid="reminder-email-input"
                    />
                  </div>
                  <label className="flex items-start gap-2 cursor-pointer" data-testid="reminder-newsletter-label">
                    <input
                      type="checkbox"
                      checked={reminderNewsletter}
                      onChange={(e) => setReminderNewsletter(e.target.checked)}
                      className="mt-1 w-4 h-4 accent-[#8B3A3A] flex-shrink-0"
                      data-testid="reminder-newsletter-checkbox"
                    />
                    <span className="font-body text-sm text-[#2C2416]">
                      Also send me the Vigilante Radio Montana newsletter — local music, news, deals, and special offers. Unsubscribe anytime.
                    </span>
                  </label>
                  {reminderError && (
                    <p className="font-mono text-sm text-[#8B3A3A]" data-testid="reminder-error">{reminderError}</p>
                  )}
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => setReminderEvent(null)}
                      className="px-4 py-2 border-2 border-[#2C2416] font-utility text-sm uppercase tracking-wide hover:bg-[#E8E2D2]"
                      data-testid="reminder-cancel-btn"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={reminderStatus === 'submitting'}
                      className="btn-primary disabled:opacity-60"
                      data-testid="reminder-submit-btn"
                    >
                      {reminderStatus === 'submitting' ? 'Saving…' : 'Remind Me'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </ModalPortal>
      )}
    </section>
  );
};

// ============== NONPROFITS SECTION ==============
const NonprofitsSection = () => {
  const [nonprofits, setNonprofits] = useState([]);
  const [selectedNonprofit, setSelectedNonprofit] = useState(null);
  const [showSubmitNonprofit, setShowSubmitNonprofit] = useState(false);
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [donationNonprofit, setDonationNonprofit] = useState(null);
  const [donationAmount, setDonationAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [recurring, setRecurring] = useState(false);
  const [loading, setLoading] = useState(false);
  const [processingDonation, setProcessingDonation] = useState(false);
  const [donationResult, setDonationResult] = useState(null);
  const [donationSessionId, setDonationSessionId] = useState(null);

  useEffect(() => { fetchNonprofits(); }, []);

  // Poll for donation completion
  useEffect(() => {
    if (!donationSessionId) return;
    
    const pollDonation = async () => {
      try {
        const response = await axios.get(`${API}/donations/status/${donationSessionId}`);
        
        if (response.data.receipt_number) {
          // Donation complete and receipt generated
          setDonationResult(response.data);
          setProcessingDonation(false);
          setDonationSessionId(null);
        } else {
          // Still processing
          setTimeout(pollDonation, 2000);
        }
      } catch (err) {
        console.error('Error checking donation status:', err);
        setTimeout(pollDonation, 3000); // Retry on error
      }
    };
    
    pollDonation();
  }, [donationSessionId]);

  const fetchNonprofits = async () => {
    try { 
      const res = await axios.get(`${API}/nonprofits?approved_only=true`); 
      setNonprofits(res.data); 
    } catch (err) { 
      console.error(err); 
    }
  };

  const handleDonate = (nonprofit) => {
    setDonationNonprofit(nonprofit);
    setShowDonateModal(true);
    setDonationAmount('25');
  };

  const handleDonateSubmit = async () => {
    const amount = donationAmount === 'custom' ? parseFloat(customAmount) : parseFloat(donationAmount);
    
    if (!donorEmail || !donorName || !amount || amount <= 0) {
      alert('Please fill in all fields with a valid amount');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/nonprofits/${donationNonprofit.id}/create-donation-checkout`, {
        nonprofit_id: donationNonprofit.id,
        donor_email: donorEmail,
        donor_name: donorName,
        amount: amount,
        recurring: recurring,
        anonymous: false
      });
      
      // Open Stripe checkout in new tab
      window.open(response.data.url, '_blank');
      
      // Close modal and show processing state
      setShowDonateModal(false);
      setProcessingDonation(true);
      setDonationSessionId(response.data.session_id);
      
      // Reset form
      setDonorName('');
      setDonorEmail('');
      setDonationAmount('25');
      setCustomAmount('');
    } catch (err) {
      alert('Failed to create donation checkout. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="nonprofits-section">
      <SectionHeader icon={Heart} overline="Every penny supports local non-profits and community journalism" title="INVEST IN OUR COMMUNITY" subtitle="Add your support to the people and groups working to make Montana a better place by supporting our curated offering of locally run non-profit organizations" id="nonprofits" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {nonprofits.map(np => (
          <div key={np.id} className="card-newspaper flex flex-col md:flex-row cursor-pointer" onClick={() => setSelectedNonprofit(np)} data-testid={`nonprofit-card-${np.id}`}>
            {np.image_url && <div className="md:w-1/3 aspect-video md:aspect-square overflow-hidden"><img src={np.image_url} alt={np.name} className="w-full h-full object-cover img-newsprint" /></div>}
            <div className="p-4 flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="badge-newspaper">{np.category}</span>
                {np.featured && <Star className="w-4 h-4 text-[#8B3A3A] fill-[#8B3A3A]" />}
              </div>
              <h3 className="headline-sm">{np.name}</h3>
              <p className="font-body text-[#8B3A3A] italic mt-1">{np.mission}</p>
              <p className="font-body text-sm text-[#5C503D] mt-2 line-clamp-2">{np.description}</p>
              <div className="flex items-center gap-2 mt-3 text-[#5C503D] flex-wrap">
                <MapPin size={14} />
                <span className="font-utility text-sm">
                  {Array.isArray(np.location) ? np.location.join(', ') : np.location}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {selectedNonprofit && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" style={{ zIndex: 9999 }}>
          <div className="bg-[#F5F0E1] border-2 border-[#2C2416] max-w-xl w-full my-8 animate-slide-up max-h-[85vh] flex flex-col">
            <div className="p-4 border-b-2 border-[#2C2416] flex justify-between items-start flex-shrink-0">
              <div className="flex-1 pr-2">
                <span className="badge-newspaper mb-2">{selectedNonprofit.category}</span>
                <h2 className="headline-md mt-2">{selectedNonprofit.name}</h2>
                <p className="font-body text-sm text-[#8B3A3A] italic mt-1">{selectedNonprofit.mission}</p>
                <div className="flex items-center gap-2 mt-2 text-[#5C503D] flex-wrap">
                  <MapPin size={14} />
                  <span className="font-utility text-sm">
                    {Array.isArray(selectedNonprofit.location) ? selectedNonprofit.location.join(', ') : selectedNonprofit.location}
                  </span>
                </div>
              </div>
              <button onClick={() => setSelectedNonprofit(null)} className="p-2 hover:bg-[#E8E2D2] rounded flex-shrink-0 -mt-1 -mr-1"><X size={24} /></button>
            </div>
            <div className="overflow-y-auto flex-1">
              {selectedNonprofit.image_url && (
                <div className="w-full max-h-72 overflow-hidden bg-[#E8E2D2]">
                  <img src={selectedNonprofit.image_url} alt={selectedNonprofit.name} className="w-full h-full object-cover img-newsprint" />
                </div>
              )}
              <div className="p-4">
                <h3 className="font-heading text-lg font-bold mb-2">About This Organization</h3>
                <p className="font-body leading-relaxed mb-4">{selectedNonprofit.description}</p>
                
                <div className="bg-[#E8E2D2] p-3 border border-[#2C2416]/20 mb-4">
                  <h4 className="font-utility uppercase text-xs tracking-wide mb-2 text-[#2C2416]">Contact Information</h4>
                  {selectedNonprofit.location && (
                    <div className="flex items-start gap-2 mb-2">
                      <MapPin size={14} className="mt-1 flex-shrink-0" />
                      <span className="font-body text-sm">{selectedNonprofit.location}</span>
                    </div>
                  )}
                  {selectedNonprofit.website && (
                    <div className="flex items-start gap-2">
                      <ExternalLink size={14} className="mt-1 flex-shrink-0" />
                      <a href={selectedNonprofit.website} target="_blank" rel="noopener noreferrer" className="font-body text-sm text-[#8B3A3A] hover:underline break-all">{selectedNonprofit.website}</a>
                    </div>
                  )}
                </div>
                
                <button 
                  onClick={() => { setSelectedNonprofit(null); handleDonate(selectedNonprofit); }} 
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  <Heart size={16} /> Make a Donation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Donation Modal */}
      {showDonateModal && donationNonprofit && (
        <ModalPortal>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
            <div className="bg-[#F5F0E1] border-2 border-[#2C2416] max-w-md w-full animate-slide-up">
              <div className="p-6 border-b-2 border-[#2C2416] flex justify-between items-start">
                <div>
                  <h3 className="headline-lg">Make a Donation</h3>
                  <p className="font-utility text-sm text-[#5C503D] mt-1">{donationNonprofit.name}</p>
                </div>
                <button onClick={() => setShowDonateModal(false)} className="p-2 hover:bg-[#E8E2D2]"><X size={24} /></button>
              </div>
              
              <div className="p-6">
                {/* Amount Selection */}
                <div className="mb-6">
                  <label className="font-utility text-sm uppercase tracking-wide mb-3 block">Select Amount</label>
                  <div className="grid grid-cols-4 gap-2 mb-3">
                    {['10', '25', '50', '100'].map(amount => (
                      <button
                        key={amount}
                        onClick={() => setDonationAmount(amount)}
                        className={`py-3 px-2 border-2 transition-colors ${
                          donationAmount === amount
                            ? 'bg-[#3A5A40] text-[#F5F0E1] border-[#3A5A40]'
                            : 'bg-[#F5F0E1] border-[#2C2416] hover:bg-[#E8E2D2]'
                        }`}
                      >
                        <span className="font-heading text-xl font-bold">${amount}</span>
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setDonationAmount('custom')}
                    className={`w-full py-3 border-2 transition-colors ${
                      donationAmount === 'custom'
                        ? 'bg-[#3A5A40] text-[#F5F0E1] border-[#3A5A40]'
                        : 'bg-[#F5F0E1] border-[#2C2416] hover:bg-[#E8E2D2]'
                    }`}
                  >
                    <span className="font-heading">Custom Amount</span>
                  </button>
                  
                  {donationAmount === 'custom' && (
                    <div className="mt-3">
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 font-heading text-xl">$</span>
                        <input
                          type="number"
                          step="0.01"
                          min="1"
                          value={customAmount}
                          onChange={(e) => setCustomAmount(e.target.value)}
                          className="input-newspaper w-full pl-8 text-xl"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Recurring Toggle */}
                <div className="mb-6 bg-[#E8E2D2] p-4 border border-[#2C2416]/20">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={recurring}
                      onChange={(e) => setRecurring(e.target.checked)}
                      className="w-5 h-5"
                    />
                    <div>
                      <span className="font-heading font-bold block">Make this monthly</span>
                      <span className="font-utility text-xs text-[#5C503D]">Support {donationNonprofit.name} every month</span>
                    </div>
                  </label>
                </div>

                {/* Donor Information */}
                <div className="space-y-4">
                  <div>
                    <label className="font-utility text-sm uppercase tracking-wide mb-2 block">Your Name</label>
                    <input 
                      type="text" 
                      value={donorName} 
                      onChange={(e) => setDonorName(e.target.value)} 
                      className="input-newspaper w-full" 
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="font-utility text-sm uppercase tracking-wide mb-2 block">Your Email</label>
                    <input 
                      type="email" 
                      value={donorEmail} 
                      onChange={(e) => setDonorEmail(e.target.value)} 
                      className="input-newspaper w-full" 
                      placeholder="you@email.com"
                      required
                    />
                    <p className="font-utility text-xs text-[#5C503D] mt-1">We'll email your tax receipt here</p>
                  </div>
                </div>
              </div>
              
              <div className="p-6 border-t-2 border-[#2C2416] flex gap-3">
                <button onClick={() => setShowDonateModal(false)} className="btn-secondary flex-1">
                  Cancel
                </button>
                <button 
                  onClick={handleDonateSubmit} 
                  disabled={loading || !donorEmail || !donorName || (!donationAmount && !customAmount)} 
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : <Heart size={18} />}
                  {loading ? 'Processing...' : `Donate $${donationAmount === 'custom' ? (customAmount || '0') : donationAmount}`}
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
      
      {/* Processing Donation Overlay */}
      {processingDonation && (
        <ModalPortal>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
            <div className="bg-[#F5F0E1] border-2 border-[#2C2416] max-w-md w-full p-8 text-center animate-slide-up">
              <Loader2 className="animate-spin w-16 h-16 text-[#3A5A40] mx-auto mb-4" />
              <h3 className="headline-lg mb-2">Processing Your Donation</h3>
              <p className="font-body text-[#5C503D]">Please complete your payment in the new tab.</p>
              <p className="font-utility text-sm text-[#5C503D] mt-4">Your receipt will appear here automatically once payment is confirmed.</p>
              <button 
                onClick={() => {
                  setProcessingDonation(false);
                  setDonationSessionId(null);
                }}
                className="btn-secondary mt-6"
              >
                Cancel
              </button>
            </div>
          </div>
        </ModalPortal>
      )}
      
      {/* Donation Receipt Modal */}
      {donationResult && (
        <ModalPortal>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
            <div className="bg-[#F5F0E1] border-2 border-[#2C2416] max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slide-up">
              <div className="p-6 border-b-2 border-[#2C2416] flex justify-between items-start bg-[#3A5A40] text-[#F5F0E1]">
                <div>
                  <h3 className="headline-lg">Thank You!</h3>
                  <p className="font-utility text-sm mt-1">Your donation has been received</p>
                </div>
                <button onClick={() => setDonationResult(null)} className="p-2 hover:bg-[#2C5A30]"><X size={24} /></button>
              </div>
              
              <div className="p-6">
                <div className="bg-white border-4 border-[#3A5A40] p-6 text-center mb-6">
                  <p className="font-utility text-xs uppercase tracking-wide text-[#5C503D] mb-2">Receipt Number</p>
                  <h1 className="font-heading text-3xl font-bold text-[#2C2416] tracking-wide mb-4">{donationResult.receipt_number}</h1>
                </div>
                
                <div className="bg-[#E8E2D2] p-4 mb-4">
                  <h4 className="font-heading text-lg font-bold mb-2">{donationResult.nonprofit_name}</h4>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="font-heading text-3xl font-bold text-[#3A5A40]">${donationResult.amount.toFixed(2)}</span>
                    <span className="badge-newspaper bg-[#3A5A40] text-[#F5F0E1]">Donation</span>
                  </div>
                  <p className="font-utility text-sm text-[#5C503D] mt-2">
                    Date: {new Date(donationResult.donation_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                  {donationResult.tax_deductible && (
                    <p className="font-utility text-sm text-[#3A5A40] mt-2">✓ Tax Deductible</p>
                  )}
                </div>
                
                <div className="bg-[#D4EDD9] border-l-4 border-[#3A5A40] p-4">
                  <p className="font-utility text-sm flex items-start gap-2">
                    <CheckCircle size={18} className="text-[#3A5A40] flex-shrink-0 mt-0.5" />
                    <span>A copy of this receipt has been emailed to <strong>{donationResult.donor_email}</strong></span>
                  </p>
                </div>
                
                <div className="bg-white border-l-4 border-[#8B3A3A] p-4 mt-4">
                  <p className="font-body text-sm text-[#5C503D]">
                    <strong>Your generosity makes a difference!</strong> Thank you for supporting {donationResult.nonprofit_name}.
                  </p>
                </div>
              </div>
              
              <div className="p-6 border-t-2 border-[#2C2416]">
                <button onClick={() => setDonationResult(null)} className="btn-primary w-full">
                  Close
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
      
      {/* Submit Nonprofit Button */}
      <div className="mt-12 text-center">
        <button 
          onClick={() => setShowSubmitNonprofit(true)}
          className="btn-primary flex items-center gap-2 mx-auto"
          data-testid="submit-nonprofit-btn"
        >
          <Plus size={18} /> List your nonprofit here
        </button>
      </div>

      {/* Submit Nonprofit Modal */}
      {showSubmitNonprofit && <SubmitNonprofitModal onClose={() => setShowSubmitNonprofit(false)} />}
    </section>
  );
};

// ============== PODCAST SECTION (custom RSS-driven player) ==============
const PodcastSection = () => {
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/podcast/feed`);
        if (!cancelled) setFeed(res.data);
      } catch (err) {
        if (!cancelled) setError('Could not load podcast episodes right now.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const strip = (html) => sanitizeHtml(html || '').replace(/<[^>]+>/g, '').trim();
  const fmtDate = (str) => {
    if (!str) return '';
    try { return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch { return ''; }
  };

  const episodes = feed?.episodes || [];
  const visibleEpisodes = expanded ? episodes : episodes.slice(0, 5);
  const showArtwork = feed?.artwork;

  return (
    <section data-testid="podcast-section">
      <SectionHeader
        icon={Mic}
        overline="On-Demand Archive"
        title="THE VIGILANTE PODCAST"
        subtitle="Catch up on past Vigilante Radio Montana broadcasts, interviews, and stories — anytime, anywhere."
        id="podcast"
      />
      <div className="max-w-4xl mx-auto">
        <div className="bg-[#F5F0E1] border-4 border-[#2C2416] p-4 md:p-6 shadow-lg">
          {/* Show header */}
          {feed && (
            <div className="flex items-start gap-4 pb-4 border-b-2 border-[#2C2416]/20" data-testid="podcast-header">
              {showArtwork && (
                <img
                  src={showArtwork}
                  alt={feed.title}
                  className="w-24 h-24 md:w-32 md:h-32 rounded-md object-cover flex-shrink-0 border-2 border-[#2C2416]"
                />
              )}
              <div className="min-w-0 flex-1">
                <h3 className="font-heading text-xl md:text-2xl leading-tight" data-testid="podcast-title">{feed.title}</h3>
                {feed.author && (
                  <p className="font-utility text-xs uppercase tracking-wider text-[#5C503D] mt-1">By {feed.author}</p>
                )}
                {feed.description && (
                  <p className="font-body text-sm text-[#2C2416] mt-2 line-clamp-3">{strip(feed.description)}</p>
                )}
                <p className="font-mono text-xs text-[#5C503D] mt-2">
                  {episodes.length} episode{episodes.length === 1 ? '' : 's'}
                </p>
              </div>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-16" data-testid="podcast-loading">
              <Loader2 className="animate-spin w-8 h-8 text-[#8B3A3A]" />
              <p className="font-utility text-sm text-[#5C503D] ml-3">Loading episodes…</p>
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="text-center py-8" data-testid="podcast-error">
              <p className="font-body text-[#8B3A3A]">{error}</p>
            </div>
          )}

          {/* Episodes list */}
          {!loading && !error && episodes.length > 0 && (
            <ul className="divide-y divide-[#2C2416]/15 mt-2" data-testid="podcast-episode-list">
              {visibleEpisodes.map((ep) => (
                <li key={ep.id} className="py-4" data-testid={`podcast-episode-${ep.id}`}>
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    {(ep.artwork || showArtwork) && (
                      <img
                        src={ep.artwork || showArtwork}
                        alt={ep.title}
                        className="w-20 h-20 rounded object-cover flex-shrink-0 border border-[#2C2416]/30"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <h4 className="font-heading text-base leading-tight" data-testid="podcast-episode-title">{ep.title}</h4>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 font-mono text-xs text-[#5C503D]">
                        {ep.episode_number && <span>Ep {ep.episode_number}</span>}
                        {ep.duration && <span>{ep.duration}</span>}
                        {ep.published && <span>{fmtDate(ep.published)}</span>}
                      </div>
                      {ep.description && (
                        <p className="font-body text-sm text-[#2C2416] mt-2 line-clamp-2">{strip(ep.description)}</p>
                      )}
                      {ep.audio_url ? (
                        playingId === ep.id ? (
                          <audio
                            controls
                            autoPlay
                            preload="none"
                            src={ep.audio_url}
                            className="w-full mt-3"
                            data-testid={`podcast-audio-${ep.id}`}
                          >
                            Your browser does not support the audio element.
                          </audio>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setPlayingId(ep.id)}
                            className="inline-flex items-center gap-2 mt-3 px-3 py-1.5 bg-[#8B3A3A] hover:bg-[#6B2A2A] text-[#F5F0E1] font-utility text-xs uppercase tracking-wide rounded transition-colors"
                            data-testid={`podcast-play-${ep.id}`}
                          >
                            <Play size={14} fill="currentColor" /> Play Episode
                          </button>
                        )
                      ) : (
                        ep.link && (
                          <a
                            href={ep.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block mt-3 font-utility text-xs uppercase tracking-wide text-[#8B3A3A] hover:underline"
                          >
                            Listen on Spotify
                          </a>
                        )
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Show more / Show less */}
          {episodes.length > 5 && (
            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => setExpanded((x) => !x)}
                className="font-utility text-xs uppercase tracking-wider text-[#8B3A3A] hover:underline"
                data-testid="podcast-toggle-expand"
              >
                {expanded ? 'Show fewer episodes' : `Show all ${episodes.length} episodes`}
              </button>
            </div>
          )}

          {/* Footer subscribe hint */}
          {feed?.link && (
            <p className="font-utility text-xs uppercase tracking-wider text-[#5C503D] mt-4 text-center border-t border-[#2C2416]/20 pt-3">
              Subscribe on{' '}
              <a
                href={feed.link}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-[#2C2416]"
                data-testid="podcast-subscribe-link"
              >Spotify / Apple Podcasts</a>
              {' '}or your favorite podcast app.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

// ============== BANDS SECTION ==============
const BandsSection = () => {
  const [bands, setBands] = useState([]);
  const [selectedBand, setSelectedBand] = useState(null);
  const [showSubmitBand, setShowSubmitBand] = useState(false);

  useEffect(() => { fetchBands(); }, []);

  const fetchBands = async () => {
    try { 
      const res = await axios.get(`${API}/bands?approved_only=true`); 
      setBands(res.data); 
    } catch (err) { 
      console.error(err); 
    }
  };

  return (
    <section data-testid="bands-section">
      <SectionHeader icon={Music} overline="Montana Music" title="SUPPORT MONTANA MUSIC" subtitle="Support local Montana artists by purchasing their music on Amazon" id="bands" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {bands.map(band => (
          <div key={band.id} className="card-newspaper cursor-pointer" onClick={() => setSelectedBand(band)} data-testid={`band-card-${band.id}`}>
            {band.image_url && <div className="aspect-square overflow-hidden"><img src={band.image_url} alt={band.name} className="w-full h-full object-cover img-newsprint" /></div>}
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="badge-newspaper">{band.genre}</span>
                {band.featured && <Star className="w-4 h-4 text-[#8B3A3A] fill-[#8B3A3A]" />}
              </div>
              <h3 className="headline-sm">{band.name}</h3>
              <p className="font-body text-sm text-[#5C503D] mt-2 line-clamp-2">{band.bio}</p>
              <div className="flex items-center gap-2 mt-3 text-[#5C503D]"><MapPin size={14} /><span className="font-utility text-sm">{band.location}</span></div>
            </div>
          </div>
        ))}
      </div>
      {selectedBand && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" style={{ zIndex: 9999 }}>
          <div className="bg-[#F5F0E1] border-2 border-[#2C2416] max-w-xl w-full my-8 animate-slide-up max-h-[85vh] flex flex-col">
            <div className="p-4 border-b-2 border-[#2C2416] flex justify-between items-start flex-shrink-0">
              <div className="flex-1 pr-2">
                <span className="badge-newspaper mb-2">{selectedBand.genre}</span>
                <h2 className="headline-md mt-2">{selectedBand.name}</h2>
                <div className="flex items-center gap-2 mt-2 text-[#5C503D]">
                  <MapPin size={14} />
                  <span className="font-utility text-sm">{selectedBand.location}</span>
                </div>
              </div>
              <button onClick={() => setSelectedBand(null)} className="p-2 hover:bg-[#E8E2D2] rounded flex-shrink-0 -mt-1 -mr-1"><X size={24} /></button>
            </div>
            <div className="overflow-y-auto flex-1">
              {selectedBand.image_url && (
                <div className="w-full max-h-72 overflow-hidden bg-[#E8E2D2]">
                  <img src={selectedBand.image_url} alt={selectedBand.name} className="w-full h-full object-cover img-newsprint" />
                </div>
              )}
              <div className="p-4">
                <h3 className="font-heading text-lg font-bold mb-2">About the Band</h3>
                <p className="font-body leading-relaxed mb-4">{selectedBand.bio}</p>
                
                {selectedBand.amazon_music_url && (
                  <div className="mb-4">
                    <h4 className="font-utility uppercase text-xs tracking-wide mb-2">Buy Music on Amazon</h4>
                    <iframe src={selectedBand.amazon_music_url}
                      width="100%" height="450" frameBorder="0" 
                      scrolling="yes"
                      loading="lazy" 
                      title={`${selectedBand.name} on Amazon Music`}
                      sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                       />
                  </div>
                )}
                
                <div className="bg-[#E8E2D2] p-3 border border-[#2C2416]/20 mb-4">
                  <h4 className="font-utility uppercase text-xs tracking-wide mb-2 text-[#2C2416]">Band Details</h4>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <Music size={14} className="mt-1 flex-shrink-0" />
                      <span className="font-body text-sm"><strong>Genre:</strong> {selectedBand.genre}</span>
                    </div>
                    {selectedBand.location && (
                      <div className="flex items-start gap-2">
                        <MapPin size={14} className="mt-1 flex-shrink-0" />
                        <span className="font-body text-sm"><strong>Based in:</strong> {selectedBand.location}</span>
                      </div>
                    )}
                    {selectedBand.website && (
                      <div className="flex items-start gap-2">
                        <ExternalLink size={14} className="mt-1 flex-shrink-0" />
                        <a href={selectedBand.website} target="_blank" rel="noopener noreferrer" className="font-body text-sm text-[#8B3A3A] hover:underline break-all">{selectedBand.website}</a>
                      </div>
                    )}
                  </div>
                </div>
                
                {selectedBand.amazon_music_url && (
                  <a href={selectedBand.amazon_music_url} target="_blank" rel="noopener noreferrer" className="btn-forest w-full flex items-center justify-center gap-2">
                    <ShoppingCart size={16} /> Buy on Amazon
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Submit Band Button */}
      <div className="mt-12 text-center">
        <button 
          onClick={() => setShowSubmitBand(true)}
          className="btn-primary flex items-center gap-2 mx-auto"
          data-testid="submit-band-btn"
        >
          <Plus size={18} /> Submit your band here
        </button>
      </div>

      {/* Submit Band Modal */}
      {showSubmitBand && <SubmitBandModal onClose={() => setShowSubmitBand(false)} />}
    </section>
  );
};

// ============== MERCH SECTION ==============
const MerchSection = () => {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetails, setProductDetails] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [shippingInfo, setShippingInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: 'US'
  });

  useEffect(() => { fetchProducts(); }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/printful/products?limit=50`);
      setProducts(res.data.data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductDetails = async (productId) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/printful/products/${productId}`);
      const details = res.data.data;
      setProductDetails(details);
      if (details.variants && details.variants.length > 0) {
        setSelectedVariant(details.variants[0]);
      }
    } catch (err) {
      console.error('Error fetching product details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    fetchProductDetails(product.id);
  };

  const addToCart = () => {
    if (!selectedVariant || !productDetails) return;
    
    const cartItem = {
      id: Date.now(),
      sync_variant_id: selectedVariant.id,
      product_name: productDetails.product.name,
      variant_name: selectedVariant.name,
      price: parseFloat(selectedVariant.retail_price || 0),
      quantity: 1,
      image: selectedVariant.files?.find(f => f.type === 'preview')?.preview_url || productDetails.product.thumbnail_url
    };
    
    setCart([...cart, cartItem]);
    setSelectedProduct(null);
    setProductDetails(null);
  };

  const removeFromCart = (id) => setCart(cart.filter(item => item.id !== id));
  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const handleCheckout = async () => {
    if (!shippingInfo.firstName || !shippingInfo.email || !shippingInfo.address1) {
      alert('Please fill in all required shipping fields');
      return;
    }

    setLoading(true);
    try {
      // Create Stripe checkout session with Printful order metadata
      const response = await axios.post(`${API}/printful/create-checkout`, {
        cart_items: cart,
        shipping_info: shippingInfo,
        total_amount: cartTotal
      });

      if (!response.data?.url) {
        throw new Error('No checkout URL returned');
      }

      // Same-tab redirect to Stripe Checkout. Stripe handles the entire payment UI
      // (incl. Apple Pay / Google Pay on mobile) and redirects the user back to
      // `success_url` once payment is complete or `cancel_url` if abandoned.
      window.location.href = response.data.url;
      // Note: no code after this point will run reliably — the browser is navigating away.
      
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Failed to create checkout session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="merch-section">
      <SectionHeader icon={ShoppingBag} overline="Show off your Vigilante ways" title="OUR MERCH TABLE" subtitle="Show the world that you support local music, local journalism and local business" id="merch" />
      {cart.length > 0 && (
        <div className="text-center mb-8">
          <button onClick={() => setShowCart(true)} className="btn-primary flex items-center gap-2 mx-auto"><ShoppingBag size={18} /> View Cart ({cart.length})</button>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading && <p className="col-span-full text-center font-body text-[#5C503D]">Loading products...</p>}
        {!loading && products.length === 0 && (
          <p className="col-span-full text-center font-body text-[#5C503D]">No products available yet. Check back soon!</p>
        )}
        {products.map(product => {
          const syncProduct = product.sync_product || product;
          const thumbnail = syncProduct.thumbnail_url || '';
          const productName = syncProduct.name || product.name || 'Unknown Product';
          
          return (
            <div 
              key={product.id} 
              className="card-newspaper cursor-pointer" 
              onClick={() => handleProductClick(product)} 
              data-testid={`product-card-${product.id}`}
            >
              {thumbnail && (
                <div className="aspect-square overflow-hidden bg-[#E8E2D2]">
                  <img src={thumbnail} alt={productName} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-4">
                <span className="badge-newspaper mb-2">Merch</span>
                <h3 className="headline-sm mt-2">{productName}</h3>
                <p className="font-heading text-lg font-bold mt-3 text-[#8B3A3A]">Click to view options</p>
              </div>
            </div>
          );
        })}
      </div>
      {selectedProduct && productDetails && (
        <ModalPortal>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
            <div className="bg-[#F5F0E1] border-2 border-[#2C2416] max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-slide-up">
              <div className="p-6 border-b-2 border-[#2C2416] flex justify-between items-start">
                <div>
                  <span className="badge-newspaper mb-2">Official Merch</span>
                  <h2 className="headline-lg mt-2">{productDetails.product.name}</h2>
                </div>
                <button onClick={() => { setSelectedProduct(null); setProductDetails(null); }} className="p-2 hover:bg-[#E8E2D2]">
                  <X size={24} />
                </button>
              </div>
              
              {productDetails.product.thumbnail_url && (
                <div className="bg-[#E8E2D2] p-8">
                  <img src={productDetails.product.thumbnail_url} alt={productDetails.product.name} className="w-full max-h-96 object-contain mx-auto" />
                </div>
              )}
              
              <div className="p-6">
                {loading ? (
                  <p className="text-center font-body">Loading variants...</p>
                ) : (
                  <>
                    {productDetails.variants && productDetails.variants.length > 0 ? (
                      <div className="mb-6">
                        <h4 className="font-utility uppercase text-sm tracking-wide mb-3 flex items-center justify-between">
                          <span>Select Size</span>
                          {selectedVariant && (
                            <span className="font-heading text-xl text-[#8B3A3A]">${selectedVariant.retail_price}</span>
                          )}
                        </h4>
                        <div className="grid grid-cols-4 gap-2">
                          {productDetails.variants.map((variant) => (
                            <button
                              key={variant.id}
                              onClick={() => setSelectedVariant(variant)}
                              className={`py-3 px-4 border-2 font-heading font-bold transition-all ${
                                selectedVariant?.id === variant.id
                                  ? 'border-[#2C2416] bg-[#2C2416] text-[#F5F0E1]'
                                  : 'border-[#2C2416]/30 hover:border-[#2C2416] bg-white'
                              }`}
                            >
                              {variant.name.split('/').pop().trim()}
                            </button>
                          ))}
                        </div>
                        {selectedVariant && (
                          <p className="font-utility text-sm text-[#5C503D] mt-3">
                            Selected: {selectedVariant.name}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="font-body text-[#5C503D] mb-6">No variants available for this product yet.</p>
                    )}
                    
                    <button
                      onClick={addToCart}
                      disabled={!selectedVariant}
                      className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {selectedVariant ? 'Add to Cart' : 'Select a size first'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
      {showCart && (
        <ModalPortal>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
            <div className="bg-[#F5F0E1] border-2 border-[#2C2416] max-w-md w-full max-h-[90vh] flex flex-col animate-slide-up">
              <div className="p-6 border-b-2 border-[#2C2416] flex justify-between items-center flex-shrink-0">
                <h3 className="headline-md">Shopping Cart ({cart.length})</h3>
                <button onClick={() => setShowCart(false)} className="p-2 hover:bg-[#E8E2D2]"><X size={24} /></button>
              </div>
              
              <div className="p-6 flex-1 overflow-y-auto">
                {cart.length === 0 ? (
                  <p className="font-body text-center text-[#5C503D]">Your cart is empty</p>
                ) : (
                  <div className="space-y-4">
                    {cart.map(item => (
                      <div key={item.id} className="flex items-center gap-4 border-b border-[#2C2416]/20 pb-4">
                        {item.image && <img src={item.image} alt={item.product_name} className="w-16 h-16 object-cover border border-[#2C2416]/20" />}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-heading font-bold text-sm">{item.product_name}</h4>
                          <p className="font-utility text-xs text-[#5C503D] truncate">{item.variant_name}</p>
                          <p className="font-heading font-bold text-[#8B3A3A] mt-1">${item.price.toFixed(2)}</p>
                        </div>
                        <button onClick={() => removeFromCart(item.id)} className="p-2 hover:bg-[#E8E2D2] text-[#8B3A3A] flex-shrink-0">
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {cart.length > 0 && (
                <div className="p-6 border-t-2 border-[#2C2416] flex-shrink-0">
                  <div className="flex justify-between items-center mb-4">
                    <span className="font-utility uppercase tracking-wide">Total</span>
                    <span className="font-heading text-2xl font-bold text-[#8B3A3A]">${cartTotal.toFixed(2)}</span>
                  </div>
                  <button
                    onClick={() => { setShowCart(false); setShowCheckout(true); }}
                    className="btn-primary w-full"
                  >
                    Proceed to Checkout
                  </button>
                </div>
              )}
            </div>
          </div>
        </ModalPortal>
      )}
      
      {/* Checkout Modal with Shipping */}
      {showCheckout && (
        <ModalPortal>
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
            <div className="bg-[#F5F0E1] border-2 border-[#2C2416] max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slide-up">
              <div className="p-6 border-b-2 border-[#2C2416] flex justify-between items-start bg-[#3A5A40] text-[#F5F0E1]">
                <div>
                  <h3 className="headline-lg">Checkout</h3>
                  <p className="font-utility text-sm mt-1">Complete your order</p>
                </div>
                <button onClick={() => setShowCheckout(false)} className="p-2 hover:bg-[#2C5A30]"><X size={24} /></button>
              </div>
              
              <div className="p-6">
                <h4 className="font-heading font-bold text-lg mb-4">Shipping Information</h4>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="font-utility text-sm uppercase tracking-wide block mb-2">First Name *</label>
                      <input
                        type="text"
                        value={shippingInfo.firstName}
                        onChange={(e) => setShippingInfo({...shippingInfo, firstName: e.target.value})}
                        className="w-full border-2 border-[#2C2416] p-3 font-body"
                        required
                      />
                    </div>
                    <div>
                      <label className="font-utility text-sm uppercase tracking-wide block mb-2">Last Name *</label>
                      <input
                        type="text"
                        value={shippingInfo.lastName}
                        onChange={(e) => setShippingInfo({...shippingInfo, lastName: e.target.value})}
                        className="w-full border-2 border-[#2C2416] p-3 font-body"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="font-utility text-sm uppercase tracking-wide block mb-2">Email *</label>
                    <input
                      type="email"
                      value={shippingInfo.email}
                      onChange={(e) => setShippingInfo({...shippingInfo, email: e.target.value})}
                      className="w-full border-2 border-[#2C2416] p-3 font-body"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="font-utility text-sm uppercase tracking-wide block mb-2">Phone *</label>
                    <input
                      type="tel"
                      value={shippingInfo.phone}
                      onChange={(e) => setShippingInfo({...shippingInfo, phone: e.target.value})}
                      className="w-full border-2 border-[#2C2416] p-3 font-body"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="font-utility text-sm uppercase tracking-wide block mb-2">Address Line 1 *</label>
                    <input
                      type="text"
                      value={shippingInfo.address1}
                      onChange={(e) => setShippingInfo({...shippingInfo, address1: e.target.value})}
                      className="w-full border-2 border-[#2C2416] p-3 font-body"
                      placeholder="Street address"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="font-utility text-sm uppercase tracking-wide block mb-2">Address Line 2</label>
                    <input
                      type="text"
                      value={shippingInfo.address2}
                      onChange={(e) => setShippingInfo({...shippingInfo, address2: e.target.value})}
                      className="w-full border-2 border-[#2C2416] p-3 font-body"
                      placeholder="Apt, suite, etc. (optional)"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="font-utility text-sm uppercase tracking-wide block mb-2">City *</label>
                      <input
                        type="text"
                        value={shippingInfo.city}
                        onChange={(e) => setShippingInfo({...shippingInfo, city: e.target.value})}
                        className="w-full border-2 border-[#2C2416] p-3 font-body"
                        required
                      />
                    </div>
                    <div>
                      <label className="font-utility text-sm uppercase tracking-wide block mb-2">State *</label>
                      <input
                        type="text"
                        value={shippingInfo.state}
                        onChange={(e) => setShippingInfo({...shippingInfo, state: e.target.value})}
                        className="w-full border-2 border-[#2C2416] p-3 font-body"
                        placeholder="MT"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="font-utility text-sm uppercase tracking-wide block mb-2">ZIP Code *</label>
                      <input
                        type="text"
                        value={shippingInfo.zip}
                        onChange={(e) => setShippingInfo({...shippingInfo, zip: e.target.value})}
                        className="w-full border-2 border-[#2C2416] p-3 font-body"
                        required
                      />
                    </div>
                    <div>
                      <label className="font-utility text-sm uppercase tracking-wide block mb-2">Country *</label>
                      <input
                        type="text"
                        value={shippingInfo.country}
                        onChange={(e) => setShippingInfo({...shippingInfo, country: e.target.value})}
                        className="w-full border-2 border-[#2C2416] p-3 font-body"
                        placeholder="US"
                        required
                      />
                    </div>
                  </div>
                </div>
                
                <div className="bg-[#E8E2D2] p-4 mt-6">
                  <h4 className="font-heading font-bold mb-3">Order Summary</h4>
                  <div className="space-y-2">
                    {cart.map(item => (
                      <div key={item.id} className="flex justify-between text-sm">
                        <span className="font-body">{item.product_name} ({item.variant_name.split('/').pop()})</span>
                        <span className="font-heading font-bold">${item.price.toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="border-t-2 border-[#2C2416] pt-2 mt-2 flex justify-between">
                      <span className="font-heading font-bold">Total</span>
                      <span className="font-heading text-xl font-bold text-[#8B3A3A]">${cartTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-6 border-t-2 border-[#2C2416] flex gap-3">
                <button onClick={() => setShowCheckout(false)} className="btn-secondary flex-1">
                  Back to Cart
                </button>
                <button
                  onClick={handleCheckout}
                  disabled={loading || !shippingInfo.firstName || !shippingInfo.email || !shippingInfo.address1 || !shippingInfo.city || !shippingInfo.state || !shippingInfo.zip}
                  className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Processing...' : 'Pay with Stripe'}
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
    </section>
  );
};

// ============== BACK TO TOP ==============
const BackToTop = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 500);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  if (!visible) return null;
  return (
    <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="fixed bottom-8 right-8 w-12 h-12 bg-[#8B3A3A] text-[#F5F0E1] border-2 border-[#2C2416] flex items-center justify-center hover:bg-[#2C2416] transition-colors z-40"
      data-testid="back-to-top">
      <ArrowUp size={24} />
    </button>
  );
};

// ============== FOOTER ==============
const Footer = ({ setShowAuth, setShowAdmin }) => {
  const { user, logout } = useAuth();
  
  return (
    <footer className="bg-[#2C2416] text-[#F5F0E1] py-16 mt-16">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          <div>
            <h3 className="font-heading text-2xl font-bold mb-4">VIGILANTE RADIO MONTANA</h3>
            <p className="font-body text-[#F5F0E1]/70 mb-4">Your voice for resisting change. Broadcasting from the capital of the Montana Territory, Virginia City, MT</p>
          </div>
          <div>
            <h4 className="font-utility uppercase tracking-wide mb-4">Contact</h4>
            <div className="space-y-2 text-[#F5F0E1]/70">
              <div className="flex items-center gap-2"><MapPin size={16} /><span>Virginia City, MT</span></div>
              <div className="flex items-center gap-2"><Mail size={16} /><span>VigilanteRadioMT@Gmail.com</span></div>
            </div>
            <div className="mt-4">
              <p className="font-body text-[#F5F0E1]/70 text-sm italic">From Missoula to Billings - Whitefish to West Yellowstone and everywhere else that the Vigilante ways can be found</p>
            </div>
          </div>
          <div>
            <h4 className="font-utility uppercase tracking-wide mb-4">Admin</h4>
            {user ? (
              <div className="space-y-3">
                <p className="font-body text-[#F5F0E1]/70 text-sm">Logged in as {user.name}</p>
                {user.role === 'admin' && (
                  <button onClick={() => setShowAdmin(true)} 
                    className="w-full bg-[#F5F0E1] text-[#2C2416] border-2 border-[#F5F0E1] font-utility text-sm uppercase tracking-wide py-2 px-4 flex items-center justify-center gap-2 hover:bg-transparent hover:text-[#F5F0E1] transition-colors"
                    data-testid="admin-panel-btn">
                    <Settings size={16} /> Admin Panel
                  </button>
                )}
                <button onClick={logout} 
                  className="w-full border-2 border-[#F5F0E1]/50 text-[#F5F0E1]/70 font-utility text-sm uppercase tracking-wide py-2 px-4 flex items-center justify-center gap-2 hover:border-[#F5F0E1] hover:text-[#F5F0E1] transition-colors"
                  data-testid="logout-btn">
                  <LogOut size={16} /> Logout
                </button>
              </div>
            ) : (
              <button onClick={() => setShowAuth(true)} 
                className="w-full border-2 border-[#F5F0E1]/50 text-[#F5F0E1]/70 font-utility text-sm uppercase tracking-wide py-2 px-4 flex items-center justify-center gap-2 hover:border-[#F5F0E1] hover:text-[#F5F0E1] transition-colors"
                data-testid="login-btn">
                <User size={16} /> Admin Login
              </button>
            )}
          </div>
          <div>
            <h4 className="font-utility uppercase tracking-wide mb-4">Business</h4>
            <a href="/business"
              className="w-full border-2 border-[#8B3A3A] text-[#F5F0E1] bg-[#8B3A3A] font-utility text-sm uppercase tracking-wide py-2 px-4 flex items-center justify-center gap-2 hover:bg-[#6B2A2A] hover:border-[#6B2A2A] transition-colors"
              data-testid="footer-business-portal">
              <User size={16} /> Business Portal
            </a>
          </div>
        </div>
        <div className="border-t border-[#F5F0E1]/20 pt-8 text-center">
          <p className="font-mono text-sm text-[#F5F0E1]/50">© {new Date().getFullYear()} Vigilante Radio Montana. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};


// ============== PURCHASE SUCCESS PAGE ==============
const PurchaseSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [purchase, setPurchase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setError('No session ID found');
      setLoading(false);
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await axios.get(`${API}/purchases/status/${sessionId}`);
        
        if (response.data.voucher_code) {
          // Payment confirmed and voucher created
          setPurchase(response.data);
          setLoading(false);
        } else if (response.data.payment_status === 'paid') {
          // Payment confirmed but voucher still processing
          setTimeout(pollStatus, 2000);
        } else {
          // Still processing
          setTimeout(pollStatus, 2000);
        }
      } catch (err) {
        setError('Failed to retrieve purchase details');
        setLoading(false);
      }
    };

    pollStatus();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen newsprint-bg flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin w-16 h-16 text-[#8B3A3A] mx-auto mb-4" />
          <p className="font-heading text-2xl">Processing your purchase...</p>
          <p className="font-body text-[#5C503D] mt-2">Please wait while we generate your voucher</p>
        </div>
      </div>
    );
  }

  if (error || !purchase) {
    return (
      <div className="min-h-screen newsprint-bg flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-[#F5F0E1] border-4 border-[#8B3A3A] p-8 text-center">
            <XCircle size={64} className="text-[#8B3A3A] mx-auto mb-4" />
            <h1 className="headline-lg mb-4">Purchase Error</h1>
            <p className="font-body mb-6">{error || 'Unable to retrieve purchase details'}</p>
            <a href="/#deals" className="btn-primary">Back to Deals</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen newsprint-bg py-16">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-[#F5F0E1] border-4 border-[#3A5A40] animate-slide-up">
          {/* Success Header */}
          <div className="bg-[#3A5A40] text-[#F5F0E1] p-8 text-center">
            <CheckCircle size={64} className="mx-auto mb-4" />
            <h1 className="font-heading text-3xl font-bold mb-2">Purchase Successful!</h1>
            <p className="font-body text-lg">Your voucher is ready</p>
          </div>

          {/* Voucher Code */}
          <div className="p-8 border-b-4 border-[#2C2416]">
            <p className="font-utility text-sm uppercase tracking-wide text-center mb-3 text-[#5C503D]">Your Voucher Code</p>
            <div className="bg-white border-4 border-dashed border-[#2C2416] p-6 text-center">
              <p className="font-mono text-4xl font-bold text-[#2C2416] tracking-wider">{purchase.voucher_code}</p>
            </div>
          </div>

          {/* QR Code */}
          <div className="p-8 border-b-4 border-[#2C2416] text-center">
            <p className="font-utility text-sm uppercase tracking-wide mb-4 text-[#5C503D]">Scan to Redeem</p>
            <div className="inline-block bg-white p-4 border-2 border-[#2C2416]">
              <img src={purchase.qr_code_data} alt="QR Code" className="w-48 h-48" />
            </div>
          </div>

          {/* Deal Details */}
          <div className="p-8 border-b-4 border-[#2C2416]">
            <h3 className="font-heading text-xl font-bold mb-4">Deal Details</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="font-utility text-[#5C503D]">Business:</span>
                <span className="font-body font-bold">{purchase.business_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-utility text-[#5C503D]">Deal:</span>
                <span className="font-body font-bold">{purchase.deal_title}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-utility text-[#5C503D]">Amount Paid:</span>
                <span className="font-heading text-xl font-bold text-[#3A5A40]">${purchase.amount_paid.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-utility text-[#5C503D]">Original Price:</span>
                <span className="font-body line-through">${purchase.original_price.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-utility text-[#5C503D]">You Saved:</span>
                <span className="font-heading text-xl font-bold text-[#8B3A3A]">${(purchase.original_price - purchase.amount_paid).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-utility text-[#5C503D]">Expires:</span>
                <span className="font-body">{new Date(purchase.expiration_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
            </div>
          </div>

          {/* Redemption Instructions */}
          <div className="p-8 bg-[#E8E2D2]">
            <h3 className="font-heading text-lg font-bold mb-3">How to Redeem</h3>
            <p className="font-body leading-relaxed">{purchase.redemption_instructions}</p>
          </div>

          {/* Actions */}
          <div className="p-8 flex gap-4">
            <a href="/#deals" className="btn-secondary flex-1 text-center">
              Browse More Deals
            </a>
            <button 
              onClick={() => window.print()} 
              className="btn-primary flex-1"
            >
              Print Voucher
            </button>
          </div>

          {/* Email Notice */}
          <div className="p-6 bg-[#3A5A40] text-[#F5F0E1] text-center">
            <p className="font-utility text-sm">
              ✉️ A copy of this voucher has been emailed to <strong>{purchase.customer_email}</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== DONATION SUCCESS PAGE ==============
const DonationSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [donation, setDonation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setError('No session ID found');
      setLoading(false);
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await axios.get(`${API}/donations/status/${sessionId}`);
        
        if (response.data.receipt_number) {
          // Payment confirmed and receipt created
          setDonation(response.data);
          setLoading(false);
        } else {
          // Still processing
          setTimeout(pollStatus, 2000);
        }
      } catch (err) {
        setError('Failed to retrieve donation details');
        setLoading(false);
      }
    };

    pollStatus();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen newsprint-bg flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin w-16 h-16 text-[#3A5A40] mx-auto mb-4" />
          <p className="font-heading text-2xl">Processing your donation...</p>
          <p className="font-body text-[#5C503D] mt-2">Please wait while we generate your receipt</p>
        </div>
      </div>
    );
  }

  if (error || !donation) {
    return (
      <div className="min-h-screen newsprint-bg flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-[#F5F0E1] border-4 border-[#8B3A3A] p-8 text-center">
            <XCircle size={64} className="text-[#8B3A3A] mx-auto mb-4" />
            <h1 className="headline-lg mb-4">Donation Error</h1>
            <p className="font-body mb-6">{error || 'Unable to retrieve donation details'}</p>
            <a href="/#nonprofits" className="btn-primary">Back to Nonprofits</a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen newsprint-bg py-16">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-[#F5F0E1] border-4 border-[#3A5A40] animate-slide-up">
          {/* Success Header */}
          <div className="bg-[#3A5A40] text-[#F5F0E1] p-8 text-center">
            <Heart size={64} className="mx-auto mb-4 fill-current" />
            <h1 className="font-heading text-3xl font-bold mb-2">Thank You!</h1>
            <p className="font-body text-lg">Your generosity makes a difference</p>
          </div>

          {/* Donation Details */}
          <div className="p-8 border-b-4 border-[#2C2416]">
            <h3 className="font-heading text-xl font-bold mb-4">Donation Receipt</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="font-utility text-[#5C503D]">Receipt Number:</span>
                <span className="font-mono font-bold">{donation.receipt_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-utility text-[#5C503D]">Organization:</span>
                <span className="font-body font-bold">{donation.nonprofit_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-utility text-[#5C503D]">Amount:</span>
                <span className="font-heading text-2xl font-bold text-[#3A5A40]">${donation.amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-utility text-[#5C503D]">Date:</span>
                <span className="font-body">{new Date(donation.donation_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
              </div>
              {donation.tax_deductible && (
                <div className="bg-[#E8E2D2] p-3 mt-4">
                  <p className="font-utility text-sm">✓ This donation is tax-deductible</p>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="p-8 flex gap-4">
            <a href="/#nonprofits" className="btn-secondary flex-1 text-center">
              Browse Nonprofits
            </a>
            <button 
              onClick={() => window.print()} 
              className="btn-primary flex-1"
            >
              Print Receipt
            </button>
          </div>

          {/* Email Notice */}
          <div className="p-6 bg-[#3A5A40] text-[#F5F0E1] text-center">
            <p className="font-utility text-sm">
              ✉️ A receipt has been emailed to <strong>{donation.donor_email}</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};


// ============== MAIN APP ==============
const MainApp = () => {
  const { user, loading } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'info') => setToast({ message, type });

  // Note: Manual seed endpoint available at /api/seed if needed for initial setup
  // Automatic seeding removed to prevent recreation of deleted content

  // Register service worker for PWA
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/service-worker.js')
          .then(registration => {
            console.log('Service Worker registered:', registration);
          })
          .catch(error => {
            console.log('Service Worker registration failed:', error);
          });
      });
    }
  }, []);

  if (loading) {
    return <div className="min-h-screen bg-[#F5F0E1] flex items-center justify-center"><Loader2 className="animate-spin w-12 h-12 text-[#8B3A3A]" /></div>;
  }

  return (
    <div className="min-h-screen newsprint-bg">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 pb-16">
        <NotificationBanner />
        <AdBanner />
        <RadioSection />
        <NewsSection />
        <EventsSection />
        <PodcastSection />
        <BandsSection />
        <VRMBlogSection />
      </main>

      <Footer setShowAuth={setShowAuth} setShowAdmin={setShowAdmin} />
      <BackToTop />

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      {showAdmin && <AdminPanel onClose={() => setShowAdmin(false)} showToast={showToast} />}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <PWAInstallPrompt />
    </div>
  );
};

// ============== MERCH ORDER SUCCESS PAGE ==============
const MerchOrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [state, setState] = useState('loading'); // loading | complete | error
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setError('No session ID found');
      setState('error');
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 30; // ~60 seconds at 2s interval

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/printful/order-by-session/${sessionId}`);
        if (res.data.state === 'complete') {
          setOrder(res.data.order);
          setState('complete');
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          // We stop polling but show a friendly "in progress" success page.
          setState('complete');
          return;
        }
        setTimeout(poll, 2000);
      } catch (err) {
        console.error('Order lookup failed:', err);
        setError('Could not retrieve order details.');
        setState('error');
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  if (state === 'loading') {
    return (
      <div className="min-h-screen newsprint-bg flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin w-16 h-16 text-[#8B3A3A] mx-auto mb-4" />
          <p className="font-heading text-2xl">Confirming your order…</p>
          <p className="font-body text-[#5C503D] mt-2">Sending it to Printful for fulfillment.</p>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="min-h-screen newsprint-bg flex items-center justify-center" data-testid="merch-success-error">
        <div className="max-w-md w-full mx-4">
          <div className="bg-[#F5F0E1] border-4 border-[#8B3A3A] p-8 text-center">
            <XCircle size={64} className="text-[#8B3A3A] mx-auto mb-4" />
            <h1 className="headline-lg mb-4">We hit a snag</h1>
            <p className="font-body mb-6">{error || 'Unable to retrieve order details.'}</p>
            <p className="font-body text-sm mb-6 text-[#5C503D]">
              If your card was charged, your order is still being processed and you'll receive a confirmation email shortly.
            </p>
            <a href="/#merch" className="btn-primary" data-testid="merch-success-back-btn">Back to Merch</a>
          </div>
        </div>
      </div>
    );
  }

  // Complete (or stopped polling – still treat as success since Stripe redirected here)
  return (
    <div className="min-h-screen newsprint-bg py-16" data-testid="merch-success-page">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-[#F5F0E1] border-4 border-[#3A5A40] animate-slide-up">
          <div className="bg-[#3A5A40] text-[#F5F0E1] p-8 text-center">
            <CheckCircle size={64} className="mx-auto mb-4" />
            <h1 className="headline-lg">Order Confirmed!</h1>
            <p className="font-utility mt-2 opacity-90">Thanks for supporting Vigilante Radio Montana.</p>
          </div>
          <div className="p-8 space-y-6">
            {order ? (
              <>
                <div>
                  <p className="font-mono text-xs uppercase tracking-wider text-[#5C503D]">Printful Order ID</p>
                  <p className="font-heading text-xl" data-testid="merch-order-id">
                    {order.printful_order_id || '—'}
                  </p>
                </div>
                {order.items?.length > 0 && (
                  <div>
                    <p className="font-mono text-xs uppercase tracking-wider text-[#5C503D] mb-2">Items</p>
                    <ul className="space-y-2">
                      {order.items.map((it, i) => (
                        <li key={`${it.sync_variant_id}-${i}`} className="flex justify-between font-body">
                          <span>{it.product_name} — {it.variant_name}</span>
                          <span>×{it.quantity || 1}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="flex justify-between border-t border-[#2C2416]/20 pt-4">
                  <span className="font-heading text-lg">Total</span>
                  <span className="font-heading text-lg" data-testid="merch-order-total">
                    ${(order.total_amount || 0).toFixed(2)}
                  </span>
                </div>
                <p className="font-body text-sm text-[#5C503D]">
                  You'll receive a confirmation email at <strong>{order.customer_email}</strong> as Printful prints and ships your order.
                </p>
              </>
            ) : (
              <p className="font-body text-[#5C503D]">
                Payment received! Your order is being processed and you'll receive a confirmation email shortly.
              </p>
            )}
            <a href="/#merch" className="btn-primary w-full text-center" data-testid="merch-success-continue-btn">
              Continue Shopping
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== UNSUBSCRIBE PAGE ==============
const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const token = searchParams.get('t') || '';
  const [state, setState] = useState('loading'); // loading | success | already | error
  const [errorMsg, setErrorMsg] = useState('');
  // Guard against React 18 StrictMode double-invocation in dev. Without this the
  // second mount sees the user already unsubscribed and the screen flickers to
  // "Already Unsubscribed" even on the very first click.
  const requestedRef = useRef(false);

  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;

    if (!email || !token) {
      setErrorMsg('This unsubscribe link is missing required parameters.');
      setState('error');
      return;
    }
    (async () => {
      try {
        const res = await axios.post(`${API}/newsletter/unsubscribe`, { email, token });
        setState(res.data.status === 'already_unsubscribed' ? 'already' : 'success');
      } catch (err) {
        setErrorMsg(err.response?.data?.detail || 'Unable to unsubscribe — link may be invalid or expired.');
        setState('error');
      }
    })();
  }, [email, token]);

  return (
    <div className="min-h-screen newsprint-bg flex items-center justify-center px-4" data-testid="unsubscribe-page">
      <div className="max-w-md w-full bg-[#F5F0E1] border-4 border-[#2C2416] p-8 text-center">
        {state === 'loading' && (
          <>
            <Loader2 className="animate-spin w-12 h-12 text-[#8B3A3A] mx-auto mb-4" />
            <p className="font-body">Processing your request…</p>
          </>
        )}
        {state === 'success' && (
          <>
            <CheckCircle size={56} className="text-[#3A5A40] mx-auto mb-4" data-testid="unsubscribe-success" />
            <h1 className="headline-lg mb-3">You're Unsubscribed</h1>
            <p className="font-body mb-2 text-[#2C2416]">
              <strong>{email}</strong> has been removed from the Vigilante Radio newsletter.
            </p>
            <p className="font-body text-sm text-[#5C503D] mb-6">
              You won't receive any more newsletter emails from us. Event reminders you specifically signed up for will still go out.
            </p>
            <a href="/" className="btn-primary inline-block" data-testid="unsubscribe-home-link">Back to Vigilante Radio</a>
          </>
        )}
        {state === 'already' && (
          <>
            <CheckCircle size={56} className="text-[#5C503D] mx-auto mb-4" data-testid="unsubscribe-already" />
            <h1 className="headline-lg mb-3">Already Unsubscribed</h1>
            <p className="font-body mb-6">No further action needed — <strong>{email}</strong> is not on our list.</p>
            <a href="/" className="btn-primary inline-block">Back to Vigilante Radio</a>
          </>
        )}
        {state === 'error' && (
          <>
            <XCircle size={56} className="text-[#8B3A3A] mx-auto mb-4" data-testid="unsubscribe-error" />
            <h1 className="headline-lg mb-3">Something Went Wrong</h1>
            <p className="font-body mb-6 text-[#5C503D]">{errorMsg}</p>
            <p className="font-body text-sm text-[#5C503D] mb-4">
              Email <a href="mailto:vigilanteradiomt@gmail.com" className="underline">vigilanteradiomt@gmail.com</a> and we'll remove you manually.
            </p>
            <a href="/" className="btn-primary inline-block">Back to Vigilante Radio</a>
          </>
        )}
      </div>
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/business/*" element={<BusinessPortal />} />
          <Route path="/purchase-success" element={<PurchaseSuccess />} />
          <Route path="/donation-success" element={<DonationSuccess />} />
          <Route path="/merch-order-success" element={<MerchOrderSuccess />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/*" element={<MainApp />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
